
// src/utils/generateWithGroq.js
import { emitGroqToast } from "./groqToast";
import { createTemporaryUnavailableError } from "./aiProcessingGuards";
import { NEXT_STEP_ACTIONS } from "@/lib/overview/utils";
import { consumeAiUsage } from "./aiUsageClient";

const apiKey = import.meta.env.VITE_GROQ_API_KEY;
void 0;

const DEBUG_AI_TAGS =
  import.meta?.env?.DEV === true &&
  typeof globalThis !== "undefined" &&
  globalThis.__DEBUG_AI_TAGS__ === true;
const DEBUG_DISABLE_POST_PHASE_EXPANSION = false;

// Determinism controls. Set to true to get repeatable outputs for identical prompts.
const GROQ_DETERMINISTIC = true;
const GROQ_DEFAULT_TEMPERATURE = 0;

/* ------------------------------------------------------------------ */
/* Tuning knobs */
/* ------------------------------------------------------------------ */

// Keep tags stable even if Groq misses vocab alignment.
// We build vocabulary as: Groq vocab + insight tags + span tags (then dedupe).
const EXPAND_VOCAB_FROM_INSIGHTS_AND_SPANS = true;

// When research is rich, ask for more spans
const MIN_SPANS_FLOOR = 40;

/* ------------------------------------------------------------------ */
/* Small helpers */
/* ------------------------------------------------------------------ */

function safeText(v) {
  return v == null ? "" : String(v);
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function normalizeDocText(s) {
  // CRITICAL: use the exact same normalization everywhere (offsets + UI render)
  return safeText(s).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

function toSingleSentencePreview(value, maxWords = 24) {
  const raw = safeText(value).
  replace(/\s+/g, " ").
  replace(/[•\-–—]\s+/g, "").
  trim();
  if (!raw) return "";

  const firstSentence = raw.split(/(?<=[.!?])\s+/)[0] || raw;
  let cleaned = firstSentence.replace(/^["'“”]+|["'“”]+$/g, "").trim();

  cleaned = cleaned.replace(/^we\s+will\s+/i, "");
  cleaned = cleaned.replace(
    /^create\s+(?:a|an)\s+(?:service|platform|tool|feature)\s+that\s+/i,
    "Focus on "
  );

  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length > maxWords) {
    cleaned = words.slice(0, maxWords).join(" ").replace(/[,:;]+$/, "").trim();
  }
  if (cleaned && !/[.!?]$/.test(cleaned)) cleaned = `${cleaned}.`;
  return cleaned;
}

function stripCodeFences(raw) {
  const s = safeText(raw).trim();
  const fenceMatch = s.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  if (fenceMatch && fenceMatch[1]) return fenceMatch[1].trim();
  return s.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "").trim();
}

function normalizeModelText(raw) {
  return stripCodeFences(raw).trim();
}

function extractFirstJsonObject(raw) {
  const s = safeText(raw);
  if (!s) return "";
  const trimmed = s.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return trimmed;

  const starts = ["{", "["].
  map((ch) => trimmed.indexOf(ch)).
  filter((i) => i >= 0);
  if (!starts.length) return "";
  const startIdx = Math.min(...starts);

  const str = trimmed.slice(startIdx);
  let depth = 0;
  let inString = false;
  let escape = false;

  const openChar = str[0];
  const closeChar = openChar === "{" ? "}" : "]";

  for (let i = 0; i < str.length; i++) {
    const ch = str[i];

    if (inString) {
      if (escape) escape = false;else
      if (ch === "\\") escape = true;else
      if (ch === '"') inString = false;
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === openChar) depth++;
    if (ch === closeChar) depth--;

    if (depth === 0) {
      return str.slice(0, i + 1).trim();
    }
  }

  return "";
}

function tryRepairJsonCommonMistakes(s) {
  let out = safeText(s).trim();
  if (!out) return "";

  // Remove trailing commas
  out = out.replace(/,\s*([}\]])/g, "$1");

  // Normalize NBSP, zero-width
  out = out.replace(/\u00a0/g, " ").replace(/\u200b/g, "");

  return out.trim();
}

function safeJsonParse(raw) {
  const cleaned = stripCodeFences(raw);
  const extracted = extractFirstJsonObject(cleaned) || cleaned.trim();
  const repaired = tryRepairJsonCommonMistakes(extracted);
  try {
    return JSON.parse(repaired);
  } catch (err) {
    const rawText = safeText(raw);
    const hadFence = /^\s*```/m.test(rawText);
    const head = rawText.replace(/\s+/g, " ").slice(0, 200);
    const tail = rawText.replace(/\s+/g, " ").slice(-200);
    const msg = [
    "AI JSON parse failed.",
    `error=${err?.message || "unknown"}`,
    `rawLen=${rawText.length}`,
    `cleanedLen=${cleaned.length}`,
    `extractedLen=${extracted.length}`,
    `repairedLen=${repaired.length}`,
    `codeFence=${hadFence}`,
    `head="${head}"`,
    `tail="${tail}"`].
    join(" ");
    throw new Error(msg);
  }
}

function extractGroqErrorFields(parsedErr, rawText) {
  const parsedError = parsedErr?.error && typeof parsedErr.error === "object" ? parsedErr.error : parsedErr;
  const parsedCode = safeText(parsedError?.code || parsedErr?.code || "").trim();
  const parsedMessage =
    safeText(parsedError?.message || parsedErr?.message || rawText || "").trim();
  return { parsedCode, parsedMessage };
}

function isGroqRequestBudgetError(status, parsedErr, rawText) {
  const numericStatus = Number(status || 0);
  if (numericStatus === 413) return true;
  if (numericStatus !== 400) return false;

  const { parsedCode, parsedMessage } = extractGroqErrorFields(parsedErr, rawText);
  const haystack = `${parsedCode} ${parsedMessage} ${safeText(rawText)}`.toLowerCase();
  return (
    haystack.includes("limit") ||
    haystack.includes("requested") ||
    haystack.includes("context") ||
    haystack.includes("token") ||
    haystack.includes("too large")
  );
}

function createGroqRequestFailureError({
  status,
  parsedErr,
  rawText,
  requestMeta,
  title,
  body,
  code,
  retryable = false
}) {
  const { parsedCode, parsedMessage } = extractGroqErrorFields(parsedErr, rawText);
  const details = [
    `HTTP status: ${status}`,
    parsedCode ? `Parsed code: ${parsedCode}` : null,
    parsedMessage ? `Parsed message: ${parsedMessage}` : null,
    rawText ? `Raw response: ${rawText}` : null,
    Number.isFinite(requestMeta?.requestJsonChars) ? `Request JSON chars: ${requestMeta.requestJsonChars}` : null,
    Number.isFinite(requestMeta?.combinedInputChars) ? `Combined input chars: ${requestMeta.combinedInputChars}` : null,
    Number.isFinite(requestMeta?.maxCompletionTokens) ? `Max completion tokens: ${requestMeta.maxCompletionTokens}` : null,
    requestMeta?.logLabel ? `Log label: ${requestMeta.logLabel}` : null,
  ].filter(Boolean);

  const message = body || parsedMessage || "Groq request failed.";
  const err = new Error(message);
  err.code = code || "groq_request_failed";
  err.status = status;
  err.title = title || "Groq request failed";
  err.body = body || message;
  err.details = details;
  err.message = message;
  err.retryable = retryable;
  err.cause = parsedErr || rawText;
  return err;
}

function validateGroqFoundationResponse(raw) {
  if (!raw || typeof raw !== "object") {
    throw new Error("Groq response is not an object.");
  }

  const allowedKeys = new Set([
  "tagVocabulary",
  "tagThemes",
  "overview",
  "insightGroups",
  "researchTagSpans",
  "emergingSignals",
  "problemDefinition",
  "weWill"]
  );

  for (const key of Object.keys(raw)) {
    if (!allowedKeys.has(key)) {
      throw new Error(`Groq response has unexpected top-level key: ${key}`);
    }
  }

  const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

  if (raw.tagVocabulary != null && !isObj(raw.tagVocabulary)) {
    throw new Error("Groq tagVocabulary must be an object.");
  }
  if (raw.tagThemes != null && !isObj(raw.tagThemes)) {
    throw new Error("Groq tagThemes must be an object.");
  }
  if (raw.overview != null && !isObj(raw.overview)) {
    throw new Error("Groq overview must be an object.");
  }
  if (raw.insightGroups != null && !isObj(raw.insightGroups)) {
    throw new Error("Groq insightGroups must be an object.");
  }
  if (raw.researchTagSpans != null && !isObj(raw.researchTagSpans)) {
    throw new Error("Groq researchTagSpans must be an object.");
  }
  if (raw.emergingSignals != null && !isObj(raw.emergingSignals)) {
    throw new Error("Groq emergingSignals must be an object.");
  }

  if (raw.tagVocabulary?.items != null && !Array.isArray(raw.tagVocabulary.items)) {
    throw new Error("Groq tagVocabulary.items must be an array.");
  }
  if (raw.tagThemes?.themes != null && !Array.isArray(raw.tagThemes.themes)) {
    throw new Error("Groq tagThemes.themes must be an array.");
  }
  if (raw.overview?.researchInsights != null && !Array.isArray(raw.overview.researchInsights)) {
    throw new Error("Groq overview.researchInsights must be an array.");
  }
  if (raw.insightGroups?.groups != null && !Array.isArray(raw.insightGroups.groups)) {
    throw new Error("Groq insightGroups.groups must be an array.");
  }
  if (raw.researchTagSpans?.items != null && !Array.isArray(raw.researchTagSpans.items)) {
    throw new Error("Groq researchTagSpans.items must be an array.");
  }
  if (raw.problemDefinition != null && typeof raw.problemDefinition !== "string") {
    throw new Error("Groq problemDefinition must be a string.");
  }
  if (raw.weWill != null && typeof raw.weWill !== "string") {
    throw new Error("Groq weWill must be a string.");
  }
}

function hash32(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function now() {
  return Date.now();
}

function warnInvariant(condition, message, meta = null) {
  if (condition) return;
  void 0;
}

/* ------------------------------------------------------------------ */
/* ✅ Parse bundled wizard text blocks (optional fallback) */
/* ------------------------------------------------------------------ */

function parseBundledResearchBlocks(text) {
  const raw = normalizeDocText(text);
  if (!raw.includes("--- RESEARCH_ITEM_START ---")) return [];

  const chunks = raw.split("--- RESEARCH_ITEM_START ---").slice(1);
  const docs = [];

  for (const chunk of chunks) {
    const endIdx = chunk.indexOf("--- RESEARCH_ITEM_END ---");
    const body = (endIdx >= 0 ? chunk.slice(0, endIdx) : chunk).trim();
    if (!body) continue;

    const ridMatch = body.match(/^\s*RID:\s*(.+)\s*$/m);
    const labelMatch = body.match(/^\s*LABEL:\s*(.+)\s*$/m);
    const contentMarker = body.indexOf("\nCONTENT:");
    const content =
    contentMarker >= 0 ?
    body.slice(contentMarker + "\nCONTENT:".length).trim() :
    "";

    const id = safeText(ridMatch?.[1]).trim();
    const label = safeText(labelMatch?.[1]).trim() || "Research";
    const text = normalizeDocText(content).trim();

    if (!id || !text) continue;
    docs.push({ id, label, text });
  }

  return docs;
}

function normalizePersonasForApp(personas) {
  if (!Array.isArray(personas)) return [];

  return personas.map((p, index) => {
    const persona = { ...(p || {}) };

    if (!persona.id) persona.id = `p${index + 1}`;
    if (typeof persona.id !== "string" || !persona.id.trim()) persona.id = `p${index + 1}`;

    if (typeof persona.age === "string") {
      const n = Number(persona.age.replace(/[^\d.]/g, ""));
      persona.age = Number.isFinite(n) ? Math.round(n) : persona.age;
    }

    const d = persona.demographics;

    if (Array.isArray(d)) persona.demographics = d;else
    if (d && typeof d === "object") {
      persona.demographics = Object.entries(d).
      filter(([, value]) => value != null && String(value).trim() !== "").
      map(([key, value]) => {
        const prettyKey = key.
        replace(/([A-Z])/g, " $1").
        replace(/^./, (c) => c.toUpperCase());
        return `${prettyKey}: ${value}`;
      });
    } else persona.demographics = [];

    ["goals", "painPoints", "behaviors", "tools", "successMetrics"].forEach((field) => {
      if (!Array.isArray(persona[field])) {
        if (persona[field] == null || persona[field] === "") persona[field] = [];else
        persona[field] = [String(persona[field])];
      }
    });

    if (typeof persona.role !== "string") persona.role = safeText(persona.role);
    if (typeof persona.summary !== "string") persona.summary = safeText(persona.summary);
    if (typeof persona.quote !== "string") persona.quote = safeText(persona.quote);

    return persona;
  });
}

/* ------------------------------------------------------------------ */
/* Direction normalization (kept) */
/* ------------------------------------------------------------------ */

function normalizeDirectionDraft(directionDraft, { fallbackSummary = "" } = {}) {
  const d = directionDraft && typeof directionDraft === "object" ? directionDraft : null;

  const label =
  typeof d?.label === "string" && d.label.trim() ?
  d.label.trim() :
  "Draft focus (needs review)";

  const anchor = typeof d?.anchor === "string" && d.anchor.trim() ? d.anchor.trim() : "";

  const constraint =
  typeof d?.constraint === "string" && d.constraint.trim() ?
  d.constraint.trim() :
  fallbackSummary ?
  `Clarify insights to produce a specific focus: ${fallbackSummary}` :
  "Clarify insights to produce a specific focus.";

  const implications = Array.isArray(d?.implications) ?
  d.implications.map((x) => safeText(x).trim()).filter(Boolean).slice(0, 3) :
  [];

  const assumptionsToValidate = Array.isArray(d?.assumptionsToValidate) ?
  d.assumptionsToValidate.map((x) => safeText(x).trim()).filter(Boolean).slice(0, 2) :
  [];

  const successSignals = Array.isArray(d?.successSignals) ?
  d.successSignals.map((x) => safeText(x).trim()).filter(Boolean).slice(0, 3) :
  [];

  const safeImplications =
  implications.length === 3 ?
  implications :
  [
  ...implications,
  ...[
  "Identify the single decision step most impacted by the insights and reduce friction there.",
  "Make the key uncertainty explicit in the UI and validate it with quick user feedback.",
  "Instrument the critical path so you can measure drop-off and completion."]].

  slice(0, 3);

  const safeAssumptions =
  assumptionsToValidate.length === 2 ?
  assumptionsToValidate :
  [
  ...assumptionsToValidate,
  ...[
  "The insights reflect the primary user group for this project.",
  "Fixing the primary constraint will improve task success on the main flow."]].

  slice(0, 2);

  const safeSignals =
  successSignals.length >= 2 ?
  successSignals :
  [...successSignals, ...["Higher completion rate on the main flow", "Lower drop-off at the key step"]].slice(
    0,
    3
  );

  return {
    mode: "draft",
    label,
    anchor,
    constraint,
    implications: safeImplications,
    assumptionsToValidate: safeAssumptions,
    successSignals: safeSignals
  };
}

/* ------------------------------------------------------------------ */
/* Canonical insights helpers */
/* ------------------------------------------------------------------ */

const CATEGORY_OPTIONS = [
"Trust & Safety",
"Pricing & Value",
"Discovery",
"Quality & Reliability",
"Convenience",
"Capability & Fit",
"Other"];


function normalizeCategory(c, fallback = "Other") {
  const s = String(c || "").trim();
  if (!s) return fallback;
  const found = CATEGORY_OPTIONS.find((x) => x.toLowerCase() === s.toLowerCase());
  return found || fallback;
}

function normalizeSourceType(s) {
  const v = String(s || "").trim().toLowerCase();
  if (v === "research") return "research";
  if (v === "prompt") return "prompt";
  if (v === "ai_suggested") return "ai_suggested";
  if (v === "ai") return "ai_suggested";
  return "ai_suggested";
}

function normalizeConfidence(s, text = "") {
  const v = String(s || "").trim().toLowerCase();
  if (v === "high") return "high";
  if (v === "medium") return "medium";
  if (v === "low") return "low";
  if (v === "hypothesis") return "hypothesis";
  if (/^hypothesis:\s*/i.test(String(text || ""))) return "hypothesis";
  return "medium";
}

function deriveConfidence({ sourceType, sourcesCount, text }) {
  if (normalizeConfidence("", text) === "hypothesis") return "hypothesis";
  if (sourceType === "ai_suggested") return "low";
  if (sourceType === "prompt") return "medium";
  if (sourceType === "research") return sourcesCount >= 2 ? "high" : "medium";
  return "medium";
}

function deriveInsightTitleFromText(text, { maxWords = 8, maxChars = 80 } = {}) {
  const raw = safeText(text).replace(/[“”"]/g, "").trim();
  if (!raw) return "Untitled insight";
  const sentence = raw.split(/(?<=[.!?])\s+/)[0] || raw;
  const words = sentence.split(/\s+/).filter(Boolean).slice(0, maxWords);
  let title = words.join(" ");
  if (title.length > maxChars) title = title.slice(0, maxChars).trim();
  return title || "Untitled insight";
}

/* ------------------------------------------------------------------ */
/* Insight groups */
/* ------------------------------------------------------------------ */

function slugToGroupId(title, fallbackIndex = 0) {
  const base = safeText(title).
  toLowerCase().
  trim().
  replace(/['"]/g, "").
  replace(/[^a-z0-9]+/g, "_").
  replace(/^_+|_+$/g, "");

  const safeBase = base || `group_${fallbackIndex + 1}`;
  return `grp_${safeBase}`;
}

function isGenericGroupId(id) {
  const raw = safeText(id).trim().toLowerCase();
  if (!raw) return true;
  const stripped = raw.replace(/^grp_/, "");
  return !stripped || /^grp?\d+$/.test(stripped) || /^group_?\d+$/.test(stripped);
}

function normalizeGroupIdFromGroq(rawId, title, idx) {
  if (typeof rawId === "string" && rawId.trim()) {
    const trimmed = rawId.trim();
    if (!isGenericGroupId(trimmed)) {
      return trimmed.startsWith("grp_") ? trimmed : slugToGroupId(trimmed, idx);
    }
  }
  return slugToGroupId(title, idx);
}

function decodeContiguousRangeString(raw, insightCount) {
  const s = safeText(raw).trim();
  if (!s || !/^\d+$/.test(s) || insightCount <= 0) return null;
  for (let start = 0; start < insightCount; start += 1) {
    let built = "";
    for (let end = start; end < insightCount; end += 1) {
      built += String(end);
      if (built === s) {
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
      }
      if (built.length >= s.length) break;
    }
  }
  return null;
}

function decodeConcatenatedIndexListString(raw, insightCount) {
  const s = safeText(raw).trim();
  if (!s || !/^\d+$/.test(s) || insightCount <= 0) return null;

  const maxIndex = Math.max(0, insightCount - 1);
  const maxDigits = String(maxIndex).length;
  const memo = new Map();

  function solve(pos, prev) {
    const key = `${pos}|${prev}`;
    if (memo.has(key)) return memo.get(key);
    if (pos >= s.length) return [];

    let best = null;
    for (let len = 1; len <= maxDigits && pos + len <= s.length; len += 1) {
      const slice = s.slice(pos, pos + len);
      if (slice.length > 1 && slice.startsWith("0")) continue;

      const value = Number(slice);
      if (!Number.isInteger(value) || value < 0 || value >= insightCount) continue;
      if (prev >= 0 && value <= prev) continue;

      const tail = solve(pos + len, value);
      if (!tail) continue;

      const candidate = [value, ...tail];
      if (!best || candidate.length > best.length) {
        best = candidate;
      }
    }

    memo.set(key, best);
    return best;
  }

  return solve(0, -1);
}

function parseInsightIndexToken(token, insightCount) {
  if (typeof token === "number") {
    if (!Number.isInteger(token)) return [];
    if (token >= 0 && token < insightCount) return [token];
    const asRaw = String(Math.abs(token));
    if (/^\d+$/.test(asRaw)) {
      const decodedList = decodeConcatenatedIndexListString(asRaw, insightCount);
      if (decodedList && decodedList.length) return decodedList;
      const decodedRange = decodeContiguousRangeString(asRaw, insightCount);
      if (decodedRange && decodedRange.length) return decodedRange;
    }
    return [token];
  }
  const raw = safeText(token).trim();
  if (!raw) return [];

  if (/^[\d,\s]+$/.test(raw) && /[,\s]/.test(raw)) {
    return raw.
    split(/[,\s]+/).
    map((x) => Number(x)).
    filter((n) => Number.isInteger(n));
  }

  if (/^\d+$/.test(raw)) {
    const asNum = Number(raw);
    if (Number.isSafeInteger(asNum) && String(asNum) === raw && asNum >= 0 && asNum < insightCount) {
      return [asNum];
    }
    const decodedList = decodeConcatenatedIndexListString(raw, insightCount);
    if (decodedList && decodedList.length) return decodedList;
    const decoded = decodeContiguousRangeString(raw, insightCount);
    if (decoded && decoded.length) return decoded;
    if (Number.isSafeInteger(asNum) && String(asNum) === raw) return [asNum];
  }

  return [];
}

function normalizeInsightGroupsFromGroq(rawGroups, insightCount) {
  const groupsArr =
  rawGroups && typeof rawGroups === "object" && Array.isArray(rawGroups.groups) ?
  rawGroups.groups :
  [];

  const cleaned = groupsArr.
  map((g, idx) => {
    const o = g && typeof g === "object" ? g : null;
    if (!o) return null;

    const title = safeText(o.title).trim();
    const order = Number.isFinite(Number(o.order)) ? Number(o.order) : idx;

    const invalidIndexes = [];
    const insightIndexes = Array.isArray(o.insightIndexes) ?
    o.insightIndexes.
    flatMap((n) => parseInsightIndexToken(n, insightCount)).
    filter((n) => {
      const isInt = Number.isInteger(n);
      const inRange = n >= 0 && n < insightCount;
      if (!isInt || !inRange) invalidIndexes.push(n);
      return isInt && inRange;
    }) :
    [];

    if (invalidIndexes.length && DEBUG_AI_TAGS) {
      void 0;





    }

    if (!title) return null;

    const id = normalizeGroupIdFromGroq(o.id, title, idx);

    return { id, title, order, insightIndexes: Array.from(new Set(insightIndexes)) };
  }).
  filter(Boolean);

  const byId = new Map();
  for (const g of cleaned) if (!byId.has(g.id)) byId.set(g.id, g);

  const groups = Array.from(byId.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (!groups.length) return null;

  return { version: 1, groups, createdAt: now(), updatedAt: now() };
}

function applyGroupsToCanonicalInsights(canonical, normalizedGroups) {
  const items = Array.isArray(canonical?.items) ? canonical.items.slice() : [];
  if (!items.length) return canonical;

  const groups = Array.isArray(normalizedGroups?.groups) ? normalizedGroups.groups.slice() : [];
  if (!groups.length) return { ...(canonical || {}), items };

  const orderToGroup = new Map();
  const titleToGroup = new Map();
  const normalizedTitle = (value) =>
  safeText(value).
  trim().
  toLowerCase().
  replace(/[^a-z0-9]+/g, " ").
  trim();

  for (const g of groups) {
    const titleKey = normalizedTitle(g?.title);
    if (titleKey && !titleToGroup.has(titleKey)) titleToGroup.set(titleKey, g.id);
    for (const idx of g.insightIndexes || []) {
      orderToGroup.set(Number(idx), g.id);
    }
  }

  const ensureGroupForTitle = (title) => {
    const titleKey = normalizedTitle(title);
    if (!titleKey) return null;
    const existingId = titleToGroup.get(titleKey);
    if (existingId) return existingId;
    const id = normalizeGroupIdFromGroq("", safeText(title).trim(), groups.length);
    groups.push({ id, title: safeText(title).trim(), order: groups.length, insightIndexes: [] });
    titleToGroup.set(titleKey, id);
    return id;
  };

  const promptGroup = groups.find((g) => normalizedTitle(g?.title) === "prompt insights") || null;
  const researchGroup = groups.find((g) => normalizedTitle(g?.title) === "research insights") || null;

  const withGroupIds = items.map((it, idx) => {
    const order = Number.isFinite(Number(it.order)) ? Number(it.order) : null;
    const sourceType = safeText(it?.sourceType).trim().toLowerCase();
    const source = safeText(it?.source).trim().toLowerCase();
    const explicitGroupTitle = safeText(it?.groupTitle).trim();
    const fallbackGroupId =
    ensureGroupForTitle(explicitGroupTitle) || (
    sourceType === "prompt" || source === "hypothesis" ?
    promptGroup?.id || ensureGroupForTitle("Prompt insights") :
    sourceType === "research" ?
    researchGroup?.id || ensureGroupForTitle("Research insights") :
    null);
    const gid =
    (order != null ? orderToGroup.get(order) : null) ??
    orderToGroup.get(idx) ??
    fallbackGroupId;
    return gid ? { ...it, groupId: gid } : it;
  });

  // Use a non-generic fallback group to avoid "General/Other" violations.
  const generalId = "grp_ungrouped";
  const hasGeneral = groups.some((g) => g.id === generalId);

  const generalGroup = hasGeneral ?
  null :
  { id: generalId, title: "Ungrouped insights", order: groups.length, insightIndexes: [] };

  const finalItems = withGroupIds.map((it) => it.groupId ? it : { ...it, groupId: generalId });
  const finalGroups = hasGeneral ? groups : [...groups, generalGroup];

  return {
    ...(canonical || {}),
    items: finalItems,
    insightGroups: {
      version: 1,
      groups: finalGroups,
      createdAt: normalizedGroups.createdAt,
      updatedAt: normalizedGroups.updatedAt
    }
  };
}

/* ------------------------------------------------------------------ */
/* Evidence helpers */
/* ------------------------------------------------------------------ */

function inferEvidenceTypeFromText(text) {
  const t = safeText(text).trim();
  if (!t) return "observation";
  if (/[\"“”]/.test(t)) return "quote";

  if (
  /\b(i|we|my|our|me|us)\b/i.test(t) &&
  /\b(need|want|like|love|hate|feel|think|prefer|struggle|cant|can't|dont|don't|won't|would)\b/i.test(t))

  return "quote";

  if (
  /\b(%|percent|rate|ratio|average|avg|median|mean|conversion|retention|churn|nps|csat|dau|mau|arr|mrr)\b/i.test(t))

  return "fact";

  return "quote";
}

function evidenceIndicatesPreference(text) {
  const t = safeText(text).toLowerCase();
  if (!t) return false;
  return (
    /\b(i|we|my|our|they)\s+(want|need|prefer|expect|require|avoid|like|love|hate|won't|can't|cannot)\b/.test(t) ||
    /\b(must|only|required|require|as long as|unless|before|after|without|avoid|not allowed)\b/.test(t));

}

function normalizeEvidenceItems(evidence) {
  const arr = Array.isArray(evidence) ? evidence : [];

  return arr.
  map((e, idx) => {
    const o = e && typeof e === "object" ? e : null;
    if (!o) return null;

    const rawText = safeText(o.text).trim();
    const factText = safeText(o.factText || rawText).trim();
    if (!factText) return null;

    const typeRaw = String(o.type || "").trim().toLowerCase();
    const type =
    typeRaw === "quote" || typeRaw === "observation" || typeRaw === "fact" ?
    typeRaw :
    inferEvidenceTypeFromText(factText);

    const researchItemId =
    typeof o.researchItemId === "string" && o.researchItemId.trim() ? o.researchItemId.trim() : null;

    const id =
    typeof o.id === "string" && o.id.trim() ? o.id.trim() : `ev_${hash32(factText + "::" + idx)}`;

    const spanId = typeof o.spanId === "string" && o.spanId.trim() ? o.spanId.trim() : null;
    const spanText =
    typeof o.spanText === "string" && o.spanText.trim() ?
    o.spanText.trim() :
    typeof o.quote === "string" && o.quote.trim() ?
    o.quote.trim() :
    null;
    const quote = typeof o.quote === "string" && o.quote.trim() ? o.quote.trim() : null;
    return { id, type, text: factText, factText, spanText, researchItemId, spanId, quote };
  }).
  filter(Boolean);
}

function findResearchItemIdForEvidence(text, researchItemsById) {
  if (!researchItemsById || !text) return null;

  const needle = normalizeDocText(text).toLowerCase();
  if (!needle) return null;

  for (const [id, docText] of researchItemsById.entries()) {
    const hay = normalizeDocText(docText).toLowerCase();
    if (hay.includes(needle)) return id;
  }

  const stripped = needle.replace(/[“”"']/g, "").trim();
  if (!stripped) return null;

  for (const [id, docText] of researchItemsById.entries()) {
    const hay = normalizeDocText(docText).toLowerCase();
    if (hay.includes(stripped)) return id;
  }

  return null;
}

function normalizeForMatch(s) {
  return safeText(s).
  toLowerCase().
  replace(/[“”]/g, '"').
  replace(/[‘’]/g, "'").
  replace(/[–—]/g, "-").
  replace(/\u00a0/g, " ").
  replace(/\s+/g, " ").
  trim();
}

function quoteExistsInText(text, quote) {
  const T = normalizeForMatch(text);
  const q = normalizeForMatch(quote);
  if (!T || !q) return false;
  return T.includes(q);
}

function clampQuote(s, max = 180) {
  const t = safeText(s).trim();
  if (!t) return "";
  return t.length <= max ? t : t.slice(0, max - 1).trimEnd() + "…";
}

function splitIntoSnippets(text) {
  const t = normalizeDocText(text);
  if (!t.trim()) return [];

  return t.
  split(/\n+|(?<=[.!?])\s+/g).
  map((x) => x.trim()).
  filter((x) => x.length >= 20);
}

function tokenize(s) {
  return new Set(
    safeText(s).
    toLowerCase().
    replace(/[^a-z0-9\s]/g, " ").
    split(/\s+/).
    filter((w) => w.length >= 3)
  );
}

function scoreSnippet(snippet, tokens) {
  const st = tokenize(snippet);
  let score = 0;
  for (const tok of tokens) if (st.has(tok)) score += 1;
  return score;
}

function fallbackExtractPhrases({ insightText, corpusText, maxPhrases = 3 }) {
  if (!corpusText) return [];
  const snippets = splitIntoSnippets(corpusText);
  if (!snippets.length) return [];

  const tokens = tokenize(insightText);
  if (!tokens.size) return [];

  return snippets.
  map((snip) => ({ snip, score: scoreSnippet(snip, tokens) })).
  filter((x) => x.score > 0).
  sort((a, b) => b.score - a.score).
  slice(0, maxPhrases).
  map((x) => clampQuote(x.snip, 180)).
  filter(Boolean);
}

/* ------------------------------------------------------------------ */
/* ✅ GENERIC TAG QUALITY (light filtering, no "vanishing") */
/* ------------------------------------------------------------------ */

const GENERIC_TAGS = new Set([
"the",
"and",
"with",
"from",
"that",
"this",
"there",
"their",
"have",
"has",
"had",
"were",
"was",
"really",
"just",
"like",
"kind",
"maybe",
"think",
"know",
"want",
"need",
"good",
"bad",
"what",
"why",
"how",
"who",
"when",
"where",
"would",
"should",
"could",
"your",
"you",
"are",
"is",
"do",
"user",
"users",
"people",
"person",
"feedback",
"experience",
"issue",
"problem",
"general",
"improvement",
"ux",
"ui",
"app",
"platform",
"feature",
"interface",
"product",
"solution"]
);

function clampTag(tag) {
  return safeText(tag).
  trim().
  toLowerCase().
  replace(/[“”]/g, '"').
  replace(/[‘’]/g, "'").
  replace(/[\u200b-\u200d\uFEFF]/g, "").
  replace(/\s+/g, " ").
  replace(/^\W+|\W+$/g, "").
  trim();
}

function normalizeTag(tag) {
  return clampTag(tag);
}

function isGenericTag(tag) {
  const t = normalizeTag(tag);
  if (!t) return true;
  if (t.length <= 2) return true;
  if (GENERIC_TAGS.has(t)) return true;

  const words = t.split(" ").filter(Boolean);
  if (!words.length) return true;

  const tooGenericWords = words.filter((w) => GENERIC_TAGS.has(w) || w.length <= 2);
  if (tooGenericWords.length === words.length) return true;

  return false;
}

function buildBannedTagsList(maxLines = 60) {
  return Array.from(GENERIC_TAGS).
  filter((t) => typeof t === "string" && t.trim()).
  slice(0, maxLines).
  map((t) => `- ${t}`).
  join("\n");
}

function normalizeTagVocabularyFromGroq(raw) {
  const tagsRaw =
  raw && typeof raw === "object" && Array.isArray(raw.items) ?
  raw.items :
  raw && typeof raw === "object" && Array.isArray(raw.tags) ?
  raw.tags :
  [];

  const out = [];
  const seen = new Set();

  for (const x of tagsRaw) {
    const rawTag =
    typeof x === "string" ? x : x && typeof x === "object" ? x.tag ?? x.name ?? x.label ?? "" : "";

    const tag = normalizeTag(rawTag);
    if (!tag) continue;
    if (seen.has(tag)) continue;

    seen.add(tag);

    const description = x && typeof x === "object" ? safeText(x?.description ?? x?.desc).trim() : "";

    out.push({
      tag,
      description: description ? description.slice(0, 160) : ""
    });
  }

  return out;
}

function extractAnchorPhrasesFromDescription(desc) {
  const raw = safeText(desc).trim();
  if (!raw) return [];
  const cleaned = raw.replace(/^anchors:\s*/i, "").trim();
  if (!cleaned) return [];
  const anchors = cleaned.
  split(/\s*\|\s*|,\s*|\n+/).
  map((s) => s.trim()).
  filter((s) => s.length >= 4).
  slice(0, 8);
  return anchors;
}

function extractRawTagsForDebug(raw) {
  const tagsRaw =
  raw && typeof raw === "object" && Array.isArray(raw.items) ?
  raw.items :
  raw && typeof raw === "object" && Array.isArray(raw.tags) ?
  raw.tags :
  [];

  const rawTags = [];
  const normalized = [];
  const invalid = [];

  for (const x of tagsRaw) {
    const rawTag =
    typeof x === "string" ? x : x && typeof x === "object" ? x.tag ?? x.name ?? x.label ?? "" : "";
    rawTags.push(rawTag);

    const tag = normalizeTag(rawTag);
    if (!tag) invalid.push(rawTag);else
    normalized.push(tag);
  }

  return {
    rawTags,
    normalized: Array.from(new Set(normalized)),
    invalid
  };
}

function normalizeInsightTags(tags) {
  const arr = Array.isArray(tags) ? tags : [];
  const cleaned = [];

  for (const t of arr) {
    const rawTag =
    typeof t === "string" ? t : t && typeof t === "object" ? t.tag ?? t.name ?? t.label ?? "" : "";

    const tag = normalizeTag(rawTag);
    if (!tag) continue;
    cleaned.push(tag);
  }

  return Array.from(new Set(cleaned));
}

function slugToThemeId(title, fallbackIndex = 0) {
  const base = safeText(title).
  toLowerCase().
  trim().
  replace(/['"]/g, "").
  replace(/[^a-z0-9]+/g, "_").
  replace(/^_+|_+$/g, "");

  const safeBase = base || `theme_${fallbackIndex + 1}`;
  return `theme_${safeBase}`;
}

function collectThemeTags(rawTags) {
  const out = [];

  const pushTag = (val) => {
    if (val == null) return;

    if (typeof val === "string") {
      val.
      split(",").
      map((x) => x.trim()).
      filter(Boolean).
      forEach((x) => out.push(x));
      return;
    }

    if (typeof val === "object") {
      const rawTag = val.tag ?? val.name ?? val.label ?? val.value ?? "";
      pushTag(rawTag);
    }
  };

  if (Array.isArray(rawTags)) rawTags.forEach(pushTag);else
  pushTag(rawTags);

  return out;
}

function buildAnchorSnippetsForTag(tag, researchItemsById, max = 3) {
  const out = [];
  const tagText = safeText(tag).trim();
  if (!tagText || !researchItemsById) return out;
  const tokens = extractAnchorTokensFromTag(tagText);
  const re = buildLooseTagRegex(tagText);
  for (const [id, text] of researchItemsById.entries()) {
    if (!text) continue;
    let snippet = "";
    if (re) snippet = findMatchWindowByRegex(text, re, 60);
    if (!snippet && tokens.length) {
      snippet = findMatchWindowByKeywords(text, tokens, tokens.length >= 3 ? 2 : 1, 60);
    }
    if (snippet && !out.includes(snippet)) out.push(snippet);
    if (out.length >= max) break;
  }
  return out;
}

function anchorExistsInCorpus(anchor, researchItemsById) {
  const a = safeText(anchor).trim();
  if (!a || !researchItemsById) return false;
  for (const txt of researchItemsById.values()) {
    if (safeText(txt).includes(a)) return true;
  }
  return false;
}

function setDescAnchorsStrict(vocabDescMap, tag, researchItemsById, maxAnchors = 3) {
  if (!vocabDescMap || vocabDescMap.get(tag)) return;
  const anchors = buildAnchorSnippetsForTag(tag, researchItemsById, maxAnchors).
  map((a) => safeText(a).trim()).
  filter((a) => a && anchorExistsInCorpus(a, researchItemsById)).
  slice(0, 8);
  vocabDescMap.set(tag, anchors.length ? anchors.join(" | ") : "");
}

function enforceAnchorCoverage({ vocabDescMap, spanItems, researchItemsById, maxSpansPerAnchor = 3 }) {
  const spans = Array.isArray(spanItems) ? [...spanItems] : [];
  const indexByTag = new Map();
  spans.forEach((sp) => {
    const tag = normalizeTag(sp?.tag);
    if (!tag) return;
    if (!indexByTag.has(tag)) indexByTag.set(tag, []);
    indexByTag.get(tag).push(sp);
  });

  let added = 0;

  for (const [tag, desc] of vocabDescMap.entries()) {
    const anchors = extractAnchorPhrasesFromDescription(desc);
    if (!anchors.length) continue;
    const tagSpans = indexByTag.get(tag) || [];
    for (const anchor of anchors) {
      const hasSpan = tagSpans.some((sp) => safeText(sp?.text).includes(anchor));
      if (hasSpan) continue;
      for (const [rid, docText] of researchItemsById.entries()) {
        const anchored = anchorExcerptInDoc(anchor, docText);
        if (!anchored) continue;
        const excerpt = makeWindowExcerpt(docText, anchored.start, anchored.end, 80);
        const newSpan = {
          id: `tag_${hash32(`${tag}::${rid}::${anchored.start}::${anchored.end}`)}`,
          tag,
          researchItemId: rid,
          start: anchored.start,
          end: anchored.end,
          text: excerpt,
          origin: "anchor"
        };
        spans.push(newSpan);
        tagSpans.push(newSpan);
        added += 1;
        if (tagSpans.length >= maxSpansPerAnchor) break;
        break;
      }
    }
  }

  return { items: spans, added };
}

function buildAnchorSpansFromDescriptions(vocabDescMap, researchItemsById, maxPerTagPerDoc = 4) {
  const t = now();
  const items = [];
  if (!vocabDescMap || !researchItemsById) return items;

  for (const [tag, desc] of vocabDescMap.entries()) {
    const tagPhrase = normalizeTag(tag);
    if (!tagPhrase) continue;
    const anchors = extractAnchorPhrasesFromDescription(desc);
    if (!anchors.length) continue;
    for (const [id, text] of researchItemsById.entries()) {
      const doc = safeText(text);
      if (!doc) continue;
      let taken = 0;
      for (const anchor of anchors) {
        const occs = findAllOccurrencesInsensitive(doc, anchor);
        for (const occ of occs) {
          if (isQuestionLineForSpan(doc, occ.start)) continue;
          items.push({
            id: `tag_${hash32(`${tagPhrase}::${id}::${occ.start}::${occ.end}`)}`,
            tag: tagPhrase,
            researchItemId: id,
            start: occ.start,
            end: occ.end,
            text: doc.slice(occ.start, occ.end),
            origin: "anchor_desc",
            createdAt: t,
            updatedAt: t
          });
          taken += 1;
          if (taken >= maxPerTagPerDoc) break;
        }
        if (taken >= maxPerTagPerDoc) break;
      }
    }
  }
  return items;
}

function normalizeTagThemesFromGroq(raw, vocabSet) {
  const t = now();

  const themesRaw =
  raw && typeof raw === "object" ?
  Array.isArray(raw.themes) ?
  raw.themes :
  Array.isArray(raw) ?
  raw :
  [] :
  [];

  const seenIds = new Set();
  const assignedTags = new Set();
  const themes = [];

  themesRaw.forEach((theme, idx) => {
    if (!theme || typeof theme !== "object") return;

    const rawTitle = safeText(theme.title ?? theme.name).trim() || `Theme ${idx + 1}`;
    const baseTitle = ["general", "other"].includes(rawTitle.toLowerCase()) ? "Unassigned" : rawTitle;

    let id = safeText(theme.id).trim();
    if (!id) id = slugToThemeId(baseTitle, idx);

    if (seenIds.has(id)) {
      let n = 2;
      let next = `${id}_${n}`;
      while (seenIds.has(next)) {
        n += 1;
        next = `${id}_${n}`;
      }
      id = next;
    }

    seenIds.add(id);

    const order = Number.isFinite(Number(theme.order)) ? Number(theme.order) : idx;

    const rawTags = collectThemeTags(theme.tags ?? theme.items ?? theme.tagList);
    const tags = [];
    const localSeen = new Set();

    for (const rawTag of rawTags) {
      const tag = normalizeTag(rawTag);
      if (!tag) continue;
      if (localSeen.has(tag)) continue;
      localSeen.add(tag);
      if (assignedTags.has(tag)) continue;
      assignedTags.add(tag);

      tags.push(tag);
      if (vocabSet && typeof vocabSet.add === "function") vocabSet.add(tag);
    }

    themes.push({
      id,
      title: baseTitle,
      order,
      tags,
      origin: "ai",
      createdAt: Number.isFinite(Number(theme.createdAt)) ? Number(theme.createdAt) : t,
      updatedAt: Number.isFinite(Number(theme.updatedAt)) ? Number(theme.updatedAt) : t
    });
  });

  if (!themes.length) return null;

  return {
    version: 1,
    themes: themes.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    createdAt: t,
    updatedAt: t
  };
}

function normalizeForTagKey(tag) {
  const stop = new Set(["a", "an", "the", "of", "for", "to", "in", "on", "with", "and"]);
  const tokens = safeText(tag).
  toLowerCase().
  replace(/[^a-z0-9\s]/g, " ").
  split(/\s+/).
  filter(Boolean).
  map((w) => {
    if (w.length > 3 && w.endsWith("s")) return w.slice(0, -1);
    return w;
  }).
  filter((w) => !stop.has(w));

  let out = tokens;
  if (tokens.length <= 3) {
    out = tokens.slice().sort();
  }
  return out.join(" ").trim();
}

function tokenOverlapScore(aTokens, bTokens) {
  if (!aTokens.length || !bTokens.length) return 0;
  const aSet = new Set(aTokens);
  const bSet = new Set(bTokens);
  let overlap = 0;
  for (const t of aSet) if (bSet.has(t)) overlap += 1;
  const minLen = Math.min(aSet.size, bSet.size);
  return minLen ? overlap / minLen : 0;
}

function canonicalizeTagsBundle({
  vocabItemsFromGroq,
  vocabDescMap,
  finalVocabSet,
  finalSpanItems,
  finalInsights,
  finalTagThemes,
  researchItemsById
}) {
  const tagsAll = new Set();
  (Array.isArray(finalSpanItems) ? finalSpanItems : []).forEach((s) => tagsAll.add(normalizeTag(s?.tag)));
  (Array.isArray(finalInsights?.items) ? finalInsights.items : []).forEach((it) =>
  safeArray(it.tags).forEach((t) => tagsAll.add(normalizeTag(t)))
  );
  (Array.isArray(finalTagThemes?.themes) ? finalTagThemes.themes : []).forEach((th) =>
  safeArray(th.tags).forEach((t) => tagsAll.add(normalizeTag(t)))
  );
  Array.from(finalVocabSet || []).forEach((t) => tagsAll.add(normalizeTag(t)));
  const tags = Array.from(tagsAll).filter(Boolean);

  const tagKey = new Map();
  const tagTokens = new Map();
  tags.forEach((t) => {
    const key = normalizeForTagKey(t);
    tagKey.set(t, key);
    tagTokens.set(t, key ? key.split(" ").filter(Boolean) : []);
  });

  const spanSigByTag = new Map();
  for (const sp of Array.isArray(finalSpanItems) ? finalSpanItems : []) {
    const tag = normalizeTag(sp?.tag);
    if (!tag) continue;
    const sig = `${sp.researchItemId}::${sp.start}::${sp.end}`;
    if (!spanSigByTag.has(tag)) spanSigByTag.set(tag, new Set());
    spanSigByTag.get(tag).add(sig);
  }

  const tagList = tags;
  const parent = new Map(tagList.map((t) => [t, t]));
  const find = (x) => {
    let p = parent.get(x);
    if (p !== x) {
      p = find(p);
      parent.set(x, p);
    }
    return p;
  };
  const union = (a, b) => {
    const pa = find(a);
    const pb = find(b);
    if (pa !== pb) parent.set(pb, pa);
  };

  const clustersLog = [];
  const pairSeen = new Set();

  const compareAndUnion = (a, b) => {
    if (!a || !b || a === b) return;
    const key = a < b ? `${a}::${b}` : `${b}::${a}`;
    if (pairSeen.has(key)) return;
    pairSeen.add(key);

    if (isGenericTag(a) && isGenericTag(b)) return;

    const keyA = tagKey.get(a);
    const keyB = tagKey.get(b);
    const sameKey = keyA && keyB && keyA === keyB;

    const tokensA = tagTokens.get(a) || [];
    const tokensB = tagTokens.get(b) || [];
    const overlap = tokenOverlapScore(tokensA, tokensB);
    const tokenOverlap = tokensA.length >= 2 && tokensB.length >= 2 && overlap >= 0.75;

    const spansA = spanSigByTag.get(a) || new Set();
    const spansB = spanSigByTag.get(b) || new Set();
    let spanOverlap = 0;
    if (spansA.size && spansB.size) {
      let inter = 0;
      for (const s of spansA) if (spansB.has(s)) inter += 1;
      spanOverlap = inter / Math.min(spansA.size, spansB.size);
    }
    const spanSignal = spanOverlap >= 0.5;

    const signals = [sameKey, tokenOverlap, spanSignal].filter(Boolean).length;
    if (signals >= 2) {
      union(a, b);
      if (DEBUG_AI_TAGS) {
        clustersLog.push({ a, b, sameKey, tokenOverlap, spanOverlap: Number(spanOverlap.toFixed(2)) });
      }
    }
  };

  const keyBuckets = new Map();
  const tokenBuckets = new Map();

  for (const t of tagList) {
    const key = tagKey.get(t);
    if (key) {
      if (!keyBuckets.has(key)) keyBuckets.set(key, []);
      keyBuckets.get(key).push(t);
    }
    const tokens = tagTokens.get(t) || [];
    for (const tok of tokens) {
      if (!tokenBuckets.has(tok)) tokenBuckets.set(tok, []);
      tokenBuckets.get(tok).push(t);
    }
  }

  const addPairsFromBucket = (bucket) => {
    if (!bucket || bucket.length < 2) return;
    for (let i = 0; i < bucket.length; i++) {
      for (let j = i + 1; j < bucket.length; j++) {
        compareAndUnion(bucket[i], bucket[j]);
      }
    }
  };

  keyBuckets.forEach(addPairsFromBucket);
  tokenBuckets.forEach(addPairsFromBucket);

  const clusters = new Map();
  for (const t of tagList) {
    const p = find(t);
    if (!clusters.has(p)) clusters.set(p, []);
    clusters.get(p).push(t);
  }

  const inGroqVocab = new Set((vocabItemsFromGroq || []).map((x) => normalizeTag(x?.tag)).filter(Boolean));

  const chooseCanonical = (arr) => {
    const scored = arr.
    map((t) => {
      const inVocab = inGroqVocab.has(t);
      const hasDesc = !!vocabDescMap.get(t);
      const tokens = t.split(" ").filter(Boolean);
      const tokenCount = tokens.length;
      const shortPenalty = tokenCount === 1 && isGenericTag(t) ? 2 : 0;
      return {
        tag: t,
        score: (inVocab ? 3 : 0) + (hasDesc ? 2 : 0) - shortPenalty,
        tokenCount
      };
    }).
    sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (a.tokenCount !== b.tokenCount) return a.tokenCount - b.tokenCount;
      return a.tag.localeCompare(b.tag);
    });
    return scored[0]?.tag || arr[0];
  };

  const aliasToCanonical = new Map();
  for (const group of clusters.values()) {
    if (group.length < 2) continue;
    const canonical = chooseCanonical(group);
    for (const t of group) {
      if (t !== canonical) aliasToCanonical.set(t, canonical);
    }
  }

  const rewriteTag = (t) => aliasToCanonical.get(t) || t;

  const updatedSpanItems = (Array.isArray(finalSpanItems) ? finalSpanItems : []).map((sp) => ({
    ...sp,
    tag: rewriteTag(normalizeTag(sp.tag))
  }));

  let dedupedSpans = dedupeAndClampTagSpans(updatedSpanItems, researchItemsById);
  dedupedSpans = normalizeAndMergeFinalTagSpans(dedupedSpans, researchItemsById);

  const updatedInsights = {
    ...(finalInsights || {}),
    items: (finalInsights?.items || []).map((it) => ({
      ...it,
      tags: Array.from(new Set(safeArray(it.tags).map(normalizeTag).map(rewriteTag))).filter(Boolean)
    }))
  };

  const originalThemeCounts = new Map();
  (Array.isArray(finalTagThemes?.themes) ? finalTagThemes.themes : []).forEach((th) => {
    safeArray(th.tags).forEach((t) => {
      const key = `${rewriteTag(normalizeTag(t))}::${th.id}`;
      originalThemeCounts.set(key, (originalThemeCounts.get(key) || 0) + 1);
    });
  });

  let updatedThemes = (finalTagThemes?.themes || []).map((th) => ({
    ...th,
    tags: Array.from(new Set(safeArray(th.tags).map(normalizeTag).map(rewriteTag))).filter(Boolean)
  }));

  const tagToThemes = new Map();
  updatedThemes.forEach((th) => {
    th.tags.forEach((t) => {
      if (!tagToThemes.has(t)) tagToThemes.set(t, []);
      tagToThemes.get(t).push(th.id);
    });
  });

  for (const [tag, themeIds] of tagToThemes.entries()) {
    if (themeIds.length <= 1) continue;
    let best = themeIds[0];
    let bestScore = -1;
    for (const id of themeIds) {
      const score = originalThemeCounts.get(`${tag}::${id}`) || 0;
      if (score > bestScore) {
        bestScore = score;
        best = id;
      }
    }
    updatedThemes = updatedThemes.map((th) =>
    th.id === best ? th : { ...th, tags: th.tags.filter((t) => t !== tag) }
    );
  }

  updatedThemes = updatedThemes.filter((th) => th.tags.length > 0);
  if (updatedThemes.length > 1) {
    updatedThemes = updatedThemes.map((th) => ({ ...th, tags: Array.isArray(th.tags) ? th.tags : [] }));
    const singles = updatedThemes.filter((th) => th.tags.length === 1);
    for (const single of singles) {
      const target = updatedThemes.
      filter((t) => t.id !== single.id).
      sort((a, b) => a.tags.length - b.tags.length)[0];
      if (target) {
        target.tags = Array.from(new Set([...target.tags, ...single.tags]));
        single.tags = [];
      }
    }
    updatedThemes = updatedThemes.filter((th) => th.tags.length > 0);
  }

  const canonicalSet = new Set();
  updatedThemes.forEach((th) => th.tags.forEach((t) => canonicalSet.add(t)));
  (updatedInsights?.items || []).forEach((it) => safeArray(it.tags).forEach((t) => canonicalSet.add(t)));
  dedupedSpans.forEach((sp) => canonicalSet.add(normalizeTag(sp.tag)));

  const updatedVocabDescMap = new Map(vocabDescMap);
  for (const [alias, canonical] of aliasToCanonical.entries()) {
    const aliasDesc = updatedVocabDescMap.get(alias);
    const canonicalDesc = updatedVocabDescMap.get(canonical);
    if (!canonicalDesc && aliasDesc) {
      updatedVocabDescMap.set(canonical, aliasDesc.slice(0, 160));
    }
    updatedVocabDescMap.delete(alias);
  }

  if (DEBUG_AI_TAGS && aliasToCanonical.size) {
    void 0;
    void 0;
  }

  return {
    aliasToCanonical,
    canonicalSet,
    updatedSpanItems: dedupedSpans,
    updatedInsights,
    updatedThemes: updatedThemes.length ? { ...(finalTagThemes || {}), themes: updatedThemes } : finalTagThemes,
    updatedVocabDescMap
  };
}

/* ------------------------------------------------------------------ */
/* Span helpers */
/* ------------------------------------------------------------------ */

function escapeRegExp(s) {
  return safeText(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeForAnchorMatch(s) {
  return safeText(s).
  replace(/\r\n/g, "\n").
  replace(/\r/g, "\n").
  replace(/[“”]/g, '"').
  replace(/[‘’]/g, "'").
  replace(/[–—]/g, "-").
  replace(/\u00a0/g, " ").
  replace(/\s+/g, " ").
  trim().
  toLowerCase();
}

function buildNormalizedIndexMap(text) {
  const raw = normalizeDocText(text);
  let normalized = "";
  const indexMap = [];
  let lastWasSpace = false;

  for (let i = 0; i < raw.length; i++) {
    let ch = raw[i];

    if (ch === "\u00a0") ch = " ";
    if (ch === "“" || ch === "”") ch = '"';
    if (ch === "‘" || ch === "’") ch = "'";
    if (ch === "–" || ch === "—") ch = "-";

    if (/\s/.test(ch)) {
      if (lastWasSpace) continue;
      normalized += " ";
      indexMap.push(i);
      lastWasSpace = true;
      continue;
    }

    normalized += ch.toLowerCase();
    indexMap.push(i);
    lastWasSpace = false;
  }

  return { normalized, indexMap };
}

function buildExcerptTokenRegex(excerpt) {
  const norm = normalizeForAnchorMatch(excerpt);
  if (!norm) return null;

  const tokens = norm.
  split(/[^a-z0-9]+/i).
  filter((t) => t.length >= 4).
  map(escapeRegExp);

  if (!tokens.length) return null;

  const pattern = `\\b${tokens.join("\\W+")}\\b`;
  return new RegExp(pattern, "i");
}

function anchorExcerptInDoc(excerpt, docText) {
  const rawDoc = normalizeDocText(docText);
  const rawExcerpt = safeText(excerpt).trim();
  if (!rawExcerpt || rawExcerpt.length < 4) return null;

  const preferAnswerClause = (anchored) => {
    if (!anchored || !Number.isFinite(anchored.start)) return anchored;
    const { line, start, end } = lineAtIndexWithOffset(rawDoc, anchored.start);
    const upper = safeText(line).toUpperCase();
    const qIdx = upper.indexOf("Q:");
    const aIdx = upper.indexOf("A:");
    if (qIdx === -1 || aIdx === -1) return anchored;

    const answerStartAbs = start + aIdx + 2;
    if (anchored.start >= answerStartAbs) return anchored;

    let s = answerStartAbs;
    while (s < end && /\s/.test(rawDoc[s])) s += 1;
    if (s >= end) return anchored;

    const e = Math.min(end, s + 80);
    if (e - s < 10) return anchored;

    return { start: s, end: e, text: rawDoc.slice(s, e) };
  };

  // 1) exact match
  let at = rawDoc.indexOf(rawExcerpt);
  if (at !== -1) {
    return preferAnswerClause({
      start: at,
      end: at + rawExcerpt.length,
      text: rawDoc.slice(at, at + rawExcerpt.length)
    });
  }

  // 2) normalized match
  const normalizedExcerpt = normalizeForAnchorMatch(rawExcerpt);
  const { normalized, indexMap } = buildNormalizedIndexMap(rawDoc);
  if (!normalizedExcerpt || !normalized) return null;

  let nAt = normalized.indexOf(normalizedExcerpt);
  if (nAt !== -1) {
    const start = indexMap[nAt];
    const end = indexMap[nAt + normalizedExcerpt.length - 1] + 1;
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      return preferAnswerClause({ start, end, text: rawDoc.slice(start, end) });
    }
  }

  // 3) token regex match
  const tokenRe = buildExcerptTokenRegex(rawExcerpt);
  if (tokenRe) {
    const match = normalized.match(tokenRe);
    if (match && typeof match.index === "number") {
      const start = indexMap[match.index];
      const end = indexMap[match.index + match[0].length - 1] + 1;
      if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
        return preferAnswerClause({ start, end, text: rawDoc.slice(start, end) });
      }
    }
  }

  return null;
}

function buildLooseTagRegex(tagPhrase) {
  const raw = safeText(tagPhrase).trim();
  if (!raw) return null;

  const tokens = raw.
  split(/[\s\-_]+/).
  filter(Boolean).
  map(escapeRegExp);

  if (!tokens.length) return null;

  const joined = tokens.join("[\\s\\-_]+");
  const concatenated = tokens.join("");
  const pattern = `\\b(?:${joined}|${concatenated})\\b`;
  return new RegExp(pattern, "gi");
}

function clampExcerptLength(s, min = 10, max = 80) {
  const t = safeText(s);
  if (!t.trim()) return "";
  if (t.length >= min && t.length <= max) return t;
  if (t.length > max) return t.slice(0, max).trimEnd();
  return t;
}

function expandExcerptFromCorpus(anchor, corpusText, minLen = 25, maxLen = 120) {
  const doc = safeText(corpusText);
  const anch = safeText(anchor);
  if (!doc || !anch) return { start: 0, end: 0, text: anch };

  const anchored = anchorExcerptInDoc(anch, doc);
  if (!anchored) return { start: 0, end: 0, text: anch };

  let start = anchored.start;
  let end = anchored.end;

  const isBoundary = (ch) => /[.!?]/.test(ch);
  const isClause = (ch) => /[,–—]/.test(ch);

  while (start > 0 && !isBoundary(doc[start - 1]) && !isClause(doc[start - 1])) {
    start -= 1;
    if (end - start >= maxLen) break;
  }

  while (end < doc.length && !isBoundary(doc[end]) && !isClause(doc[end])) {
    end += 1;
    if (end - start >= maxLen) break;
  }

  let text = doc.slice(start, end);
  // Trim and adjust indices to match trimmed text
  const leading = text.match(/^\s+/);
  const trailing = text.match(/\s+$/);
  const leadLen = leading ? leading[0].length : 0;
  const trailLen = trailing ? trailing[0].length : 0;
  start += leadLen;
  end -= trailLen;
  text = text.trim();

  if (text.length > maxLen) {
    text = text.slice(0, maxLen).trimEnd();
    end = start + text.length;
  }

  if (text.length < minLen) {
    const window = makeWindowExcerpt(doc, anchored.start, anchored.end, maxLen);
    if (window.length >= text.length) {
      const winAt = doc.indexOf(window);
      if (winAt >= 0) {
        start = winAt;
        end = winAt + window.length;
        text = window;
      }
    }
  }

  return { start, end, text };
}

function expandExcerptAroundIndex(fullText, start, end, minLen = 25, maxLen = 120) {
  const doc = safeText(fullText);
  let s = Math.max(0, Math.min(start, doc.length));
  let e = Math.max(0, Math.min(end, doc.length));
  if (e <= s) return { start: s, end: e, text: "" };

  const isBoundary = (ch) => /[.!?]/.test(ch);
  const isClause = (ch) => /[,–—]/.test(ch);

  while (s > 0 && !isBoundary(doc[s - 1]) && !isClause(doc[s - 1])) {
    s -= 1;
    if (e - s >= maxLen) break;
  }

  while (e < doc.length && !isBoundary(doc[e]) && !isClause(doc[e])) {
    e += 1;
    if (e - s >= maxLen) break;
  }

  let text = doc.slice(s, e);
  const leading = text.match(/^\s+/);
  const trailing = text.match(/\s+$/);
  const leadLen = leading ? leading[0].length : 0;
  const trailLen = trailing ? trailing[0].length : 0;
  s += leadLen;
  e -= trailLen;
  text = text.trim();

  if (text.length > maxLen) {
    text = text.slice(0, maxLen).trimEnd();
    e = s + text.length;
  }

  if (text.length < minLen) {
    const win = makeWindowExcerpt(doc, start, end, maxLen);
    if (win.length >= text.length) {
      const at = doc.indexOf(win);
      if (at >= 0) {
        s = at;
        e = at + win.length;
        text = win;
      }
    }
  }

  return { start: s, end: e, text };
}

function validateEvidenceIdsUniqueAndConsistent(parsed) {
  const evidenceKeyToId = new Map();
  const seenIdToKey = new Map();

  const fixEvidenceList = (list) =>
  safeArray(list).map((ev) => {
    const text = safeText(ev?.text).trim();
    const researchItemId = ev?.researchItemId == null ? null : safeText(ev?.researchItemId).trim();
    const key = `${text}::${researchItemId ?? ""}`;
    if (!text) return { ...ev, id: ev?.id ? safeText(ev.id) : "" };

    let id = evidenceKeyToId.get(key);
    if (!id) {
      id = `ev_${hash32(key)}`;
      evidenceKeyToId.set(key, id);
    }

    const prevKey = seenIdToKey.get(id);
    if (prevKey && prevKey !== key) {
      id = `ev_${hash32(key)}`;
      evidenceKeyToId.set(key, id);
    }
    seenIdToKey.set(id, key);

    return { ...ev, id };
  });

  const nextInsights =
  parsed?.overview?.researchInsights != null ?
  {
    ...parsed.overview,
    researchInsights: safeArray(parsed.overview.researchInsights).map((ins) => ({
      ...ins,
      evidence: fixEvidenceList(ins?.evidence)
    }))
  } :
  parsed.overview;

  return {
    ...parsed,
    overview: nextInsights
  };
}

function makeWindowExcerpt(fullText, start, end, maxLen = 80) {
  const text = safeText(fullText);
  const s = Math.max(0, Math.min(start, text.length));
  const e = Math.max(0, Math.min(end, text.length));
  if (e <= s) return "";

  const pad = Math.max(10, Math.floor((maxLen - (e - s)) / 2));
  const ws = Math.max(0, s - pad);
  const we = Math.min(text.length, e + pad);

  let excerpt = text.slice(ws, we);

  if (excerpt.length > maxLen) {
    const desiredStart = Math.max(0, s - Math.floor(maxLen / 2));
    const desiredEnd = Math.min(text.length, desiredStart + maxLen);
    excerpt = text.slice(desiredStart, desiredEnd);
  }

  excerpt = excerpt.replace(/\s+/g, " ").trim();
  return clampExcerptLength(excerpt, 5, maxLen);
}

function findAllOccurrences(haystack, needle) {
  const text = safeText(haystack);
  const n = safeText(needle);
  if (!text || !n) return [];

  const out = [];
  let idx = 0;

  while (true) {
    const at = text.indexOf(n, idx);
    if (at === -1) break;
    out.push({ start: at, end: at + n.length });
    idx = at + Math.max(1, n.length);
  }

  return out;
}

function findAllOccurrencesInsensitive(haystack, needle) {
  const text = safeText(haystack);
  const n = safeText(needle);
  if (!text || !n) return [];
  try {
    const re = new RegExp(escapeRegExp(n), "gi");
    const out = [];
    let m;
    while ((m = re.exec(text)) !== null) {
      out.push({ start: m.index, end: m.index + m[0].length });
      if (re.lastIndex === m.index) re.lastIndex++;
    }
    return out;
  } catch {
    return findAllOccurrences(text, n);
  }
}

function lineAtIndexWithOffset(text, index) {
  const t = safeText(text);
  if (!t) return { line: "", start: 0, end: 0 };
  const i = Math.max(0, Math.min(index, t.length));
  const start = Math.max(0, t.lastIndexOf("\n", i - 1) + 1);
  const endIdx = t.indexOf("\n", i);
  const end = endIdx === -1 ? t.length : endIdx;
  return { line: t.slice(start, end), start, end };
}

function isQuestionLineForSpan(docText, spanStart) {
  // TEMP: Disable question-line filtering.
  return false;
}

/* ------------------------------------------------------------------ */
/* Normalize spans returned by AI */
/* ------------------------------------------------------------------ */

function buildTagSpansFromInsightEvidence(insights, researchItemsById) {
  const t = now();
  const items = [];
  const seen = new Set();
  const insightItems = Array.isArray(insights?.items) ? insights.items : [];

  const evidenceSupportsTag = (tag, evidenceText) => {
    const tagNorm = normalizeForMatch(tag);
    const evNorm = normalizeForMatch(evidenceText);
    if (!tagNorm || !evNorm) return false;
    if (evNorm.includes(tagNorm)) return true;
    const tagTokens = tagNorm.
    split(" ").
    filter((w) => w.length >= 3 && !GENERIC_TAGS.has(w));
    if (!tagTokens.length) return false;
    if (tagTokens.every((tok) => evNorm.includes(tok))) return true;
    const anyTokenMatch = tagTokens.some((tok) => evNorm.includes(tok));
    if (anyTokenMatch && evidenceIndicatesPreference(evNorm)) return true;
    const hasNumeric = /\b\d{1,4}(?:\s?(?:–|-|to)\s?\d{1,4})?\b/.test(evNorm);
    const hasTime = /\b(after school|before school|morning|afternoon|evening|weekday|weekend|daily|weekly)\b/.test(
      evNorm
    );
    const tagIsRange = /\b(age|duration|time|schedule|frequency|price|cost|payment|size|length|weight|distance)\b/.test(
      tagNorm
    );
    if (tagIsRange && (hasNumeric || hasTime)) return true;
    return false;
  };

  for (const ins of insightItems) {
    const tags = normalizeInsightTags(ins?.tags);
    if (!tags.length) continue;
    const evidence = Array.isArray(ins?.evidence) ? ins.evidence : [];
    for (const ev of evidence) {
      const text = safeText(ev?.text).trim();
      const researchItemId = safeText(ev?.researchItemId).trim();
      if (!text || !researchItemId) continue;
      const doc = researchItemsById.get(researchItemId);
      if (!doc) continue;
      const anchored = anchorExcerptInDoc(text, doc);
      if (!anchored) continue;
      if (isQuestionLineForSpan(doc, anchored.start)) continue;
      const occurrences = findAllOccurrences(doc, anchored.text);
      const occs = occurrences.length ? occurrences : [{ start: anchored.start, end: anchored.end }];
      for (const tag of tags) {
        if (!evidenceSupportsTag(tag, text)) continue;
        for (const occ of occs) {
          if (isQuestionLineForSpan(doc, occ.start)) continue;
          const key = `${tag}::${researchItemId}::${occ.start}::${occ.end}`;
          if (seen.has(key)) continue;
          seen.add(key);
          items.push({
            id: `tag_${hash32(key)}`,
            tag,
            researchItemId,
            start: occ.start,
            end: occ.end,
            text: doc.slice(occ.start, occ.end),
            origin: "evidence",
            createdAt: t,
            updatedAt: t
          });
        }
      }
    }
  }

  return items;
}

function normalizeResearchTagSpansFromGroq(rawSpans, researchItemsById, vocabDescMap = null) {
  const t = now();
  const dropStats = {
    total: 0,
    kept: 0,
    tooShort: 0,
    anchorMissing: 0,
    questionLine: 0,
    deduped: 0
  };
  const dropSamples = {
    tooShort: [],
    anchorMissing: [],
    questionLine: [],
    deduped: []
  };

  const itemsRaw =
  rawSpans && typeof rawSpans === "object" && Array.isArray(rawSpans.items) ? rawSpans.items : [];

  // researchItemsById is already stored with normalizeDocText applied (see buildVirtualDocs*).
  const docs = Array.from(researchItemsById.entries()).map(([id, text]) => ({
    id,
    text
  }));

  function findAnchorInDocs(excerpt) {
    for (const d of docs) {
      const anchored = anchorExcerptInDoc(excerpt, d.text);
      if (anchored) return { researchItemId: d.id, ...anchored };
    }
    return null;
  }

  function bestEffortAnchor({ tag, excerpt, researchItemId }) {
    const tagText = safeText(tag).trim();
    const ex = safeText(excerpt).trim();
    const anchorsFromDesc = vocabDescMap?.get?.(tagText) ?
    extractAnchorPhrasesFromDescription(vocabDescMap.get(tagText)) :
    [];
    const tokens = Array.from(
      new Set(
        [].
        concat(extractAnchorTokensFromTag(tagText)).
        concat(
          safeText(ex).
          toLowerCase().
          replace(/[^a-z0-9\s]/g, " ").
          split(/\s+/).
          filter((w) => w.length >= 3)
        )
      )
    );

    const tryDoc = (docText) => {
      if (!docText) return null;
      if (ex) {
        const anchored = anchorExcerptInDoc(ex, docText);
        if (anchored) return anchored;
      }
      if (anchorsFromDesc.length) {
        for (const anchor of anchorsFromDesc) {
          const anchored = anchorExcerptInDoc(anchor, docText);
          if (anchored) return anchored;
        }
      }
      if (tagText) {
        const re = buildLooseTagRegex(tagText);
        const window = re ? findMatchWindowByRegex(docText, re, 60) : "";
        if (window) {
          const anchored = anchorExcerptInDoc(window, docText);
          if (anchored) return anchored;
        }
      }
      if (tokens.length) {
        const window = findMatchWindowByKeywords(docText, tokens, tokens.length >= 3 ? 2 : 1, 60);
        if (window) {
          const anchored = anchorExcerptInDoc(window, docText);
          if (anchored) return anchored;
        }
      }
      return null;
    };

    if (researchItemId && researchItemsById.has(researchItemId)) {
      const anchored = tryDoc(researchItemsById.get(researchItemId));
      if (anchored) return { researchItemId, ...anchored };
    }
    for (const d of docs) {
      const anchored = tryDoc(d.text);
      if (anchored) return { researchItemId: d.id, ...anchored };
    }
    return null;
  }

  const seedSpans = [];

  for (let i = 0; i < itemsRaw.length; i += 1) {
    const o = itemsRaw[i];
    if (!o || typeof o !== "object") continue;

    const tag = normalizeTag(o.tag);
    if (!tag) continue;

    const sourceSpanId = `span_${i}`;
    const rawExcerpt = safeText(o.excerpt).trim();
    const rawFact = safeText(o.fact).trim();
    const excerpt = normalizeDocText(rawExcerpt).trim();
    dropStats.total += 1;
    if (!excerpt || excerpt.length < 5) {
      dropStats.tooShort += 1;
      if (dropSamples.tooShort.length < 6) {
        dropSamples.tooShort.push({
          tag,
          researchItemId: researchItemId || null,
          excerpt: rawExcerpt || ""
        });
      }
      continue;
    }

    let researchItemId = safeText(o.researchItemId).trim();

    const anchored = bestEffortAnchor({ tag, excerpt, researchItemId });
    if (anchored) {
      seedSpans.push({
        tag,
        sourceSpanId,
        researchItemId: anchored.researchItemId,
        excerpt: anchored.text,
        excerptRaw: rawExcerpt,
        fact: rawFact,
        start: anchored.start,
        end: anchored.end
      });
      continue;
    }
    dropStats.anchorMissing += 1;
    if (dropSamples.anchorMissing.length < 6) {
      dropSamples.anchorMissing.push({
        tag,
        researchItemId: researchItemId || null,
        excerpt: rawExcerpt || ""
      });
    }
  }

  const expanded = [];
  const MAX_PER_TAG_PER_DOC = 14;

  const grouped = new Map();
  for (const s of seedSpans) {
    const k = `${s.tag}::${s.researchItemId}`;
    if (!grouped.has(k)) grouped.set(k, []);
    grouped.get(k).push(s);
  }

  for (const [key, seeds] of grouped.entries()) {
    const [tag, researchItemId] = key.split("::");
    const fullText = researchItemsById.get(researchItemId);
    if (!fullText) continue;

    let spans = [];

    // A) excerpt matches (best)
    for (const seed of seeds) {
      const seedText = safeText(seed.excerpt).trim();
      if (!seedText) continue;

      const occurrences = findAllOccurrences(fullText, seedText);

      for (const occ of occurrences) {
        if (isQuestionLineForSpan(fullText, occ.start)) {
          dropStats.questionLine += 1;
          if (dropSamples.questionLine.length < 6) {
            dropSamples.questionLine.push({
              tag,
              researchItemId,
              excerpt: seed.excerptRaw || seed.excerpt || ""
            });
          }
          continue;
        }
        const raw = fullText.slice(occ.start, occ.end);
        if (raw.length < 25) {
          const expanded = expandExcerptAroundIndex(fullText, occ.start, occ.end, 25, 120);
          spans.push({
            start: expanded.start,
            end: expanded.end,
            text: expanded.text,
            excerpt: seed.excerptRaw || seed.excerpt,
            fact: seed.fact,
            sourceSpanId: seed.sourceSpanId
          });
        } else {
          spans.push({
            start: occ.start,
            end: occ.end,
            text: raw,
            excerpt: seed.excerptRaw || seed.excerpt,
            fact: seed.fact,
            sourceSpanId: seed.sourceSpanId
          });
        }
      }

      if (!occurrences.length) {
        const anchored = anchorExcerptInDoc(seedText, fullText);
        if (anchored) {
          if (isQuestionLineForSpan(fullText, anchored.start)) {
            dropStats.questionLine += 1;
            if (dropSamples.questionLine.length < 6) {
              dropSamples.questionLine.push({
                tag,
                researchItemId,
                excerpt: seed.excerptRaw || seed.excerpt || ""
              });
            }
            continue;
          }
          if (anchored.text.length < 25) {
            const expanded = expandExcerptAroundIndex(fullText, anchored.start, anchored.end, 25, 120);
            spans.push({
              start: expanded.start,
              end: expanded.end,
              text: expanded.text,
              excerpt: seed.excerptRaw || seed.excerpt,
              fact: seed.fact,
              sourceSpanId: seed.sourceSpanId
            });
          } else {
            spans.push({
              start: anchored.start,
              end: anchored.end,
              text: anchored.text,
              excerpt: seed.excerptRaw || seed.excerpt,
              fact: seed.fact,
              sourceSpanId: seed.sourceSpanId
            });
          }
        } else if (Number.isFinite(seed.start) && Number.isFinite(seed.end)) {
          if (isQuestionLineForSpan(fullText, seed.start)) {
            dropStats.questionLine += 1;
            if (dropSamples.questionLine.length < 6) {
              dropSamples.questionLine.push({
                tag,
                researchItemId,
                excerpt: seed.excerptRaw || seed.excerpt || ""
              });
            }
            continue;
          }
          const text = makeWindowExcerpt(fullText, seed.start, seed.end, 120);
          if (text.length < 25) {
            const expanded = expandExcerptAroundIndex(fullText, seed.start, seed.end, 25, 120);
            spans.push({
              start: expanded.start,
              end: expanded.end,
              text: expanded.text,
              excerpt: seed.excerptRaw || seed.excerpt,
              fact: seed.fact,
              sourceSpanId: seed.sourceSpanId
            });
          } else {
            spans.push({
              start: seed.start,
              end: seed.end,
              text,
              excerpt: seed.excerptRaw || seed.excerpt,
              fact: seed.fact,
              sourceSpanId: seed.sourceSpanId
            });
          }
        }
      }
    }

    if (spans.length < 1) continue;

    spans.
    sort((a, b) => a.start - b.start).
    slice(0, MAX_PER_TAG_PER_DOC).
    forEach((s) => {
      expanded.push({
        id: `tag_${hash32(`${tag}::${researchItemId}::${s.start}::${s.end}`)}`,
        tag,
        researchItemId,
        start: s.start,
        end: s.end,
        text: s.text,
        excerpt: s.excerpt || null,
        fact: s.fact || null,
        sourceSpanId: s.sourceSpanId || null,
        origin: "ai",
        createdAt: t,
        updatedAt: t
      });
    });
  }

  // Dedupe
  const byKey = new Map();
  for (const it of expanded) {
    const k = `${it.tag}::${it.researchItemId}::${it.start}::${it.end}`;
    if (!byKey.has(k)) byKey.set(k, it);else
    {
      dropStats.deduped += 1;
      if (dropSamples.deduped.length < 6) {
        dropSamples.deduped.push({
          tag: it.tag,
          researchItemId: it.researchItemId,
          start: it.start,
          end: it.end,
          excerpt: it.excerpt || ""
        });
      }
    }
  }

  const finalItems = Array.from(byKey.values());
  dropStats.kept = finalItems.length;
  if (DEBUG_AI_TAGS) {
    void 0;







    void 0;





  }

  return { version: 1, items: finalItems, createdAt: t, updatedAt: t };
}

/* ------------------------------------------------------------------ */
/* Fallback spans: search corpus for EXISTING tags */
/* ------------------------------------------------------------------ */

function computeSpanTargets(charCount) {
  const min = charCount > 12000 ? 90 : charCount > 7000 ? 70 : charCount > 2500 ? 45 : 25;
  const max = charCount > 12000 ? 140 : charCount > 7000 ? 110 : charCount > 2500 ? 80 : 50;
  return { min, max };
}

function buildFallbackTagSpansFromVocabulary(
researchItemsById,
vocabSet,
minItems = 40,
skipTags = null,
vocabDescMap = null)
{
  const t = now();
  const docs = Array.from(researchItemsById.entries());
  const items = [];

  if (!vocabSet || !vocabSet.size) return items;

  const tags = Array.from(vocabSet).sort((a, b) => b.split(" ").length - a.split(" ").length);
  const MAX_PER_TAG_PER_DOC = 6;

  const skip = skipTags instanceof Set ? skipTags : new Set();
  for (const tag of tags) {
    if (skip.has(tag)) continue;
    const tagPhrase = safeText(tag).trim();
    if (!tagPhrase) continue;
    const tagTokens = tagPhrase.
    toLowerCase().
    split(/\s+/).
    filter((w) => w.length >= 4 && !GENERIC_TAGS.has(w));

    for (const [id, text] of docs) {
      const fullText = safeText(text);
      if (!fullText) continue;

      let taken = 0;
      const re = new RegExp(`\\b${escapeRegExp(tagPhrase)}\\b`, "gi");

      try {
        if (!re) throw new Error("invalid tag regex");

        let m;
        while ((m = re.exec(fullText)) !== null && taken < MAX_PER_TAG_PER_DOC) {
          const start = m.index;
          const end = m.index + m[0].length;
          const excerpt = makeWindowExcerpt(fullText, start, end, 80);

          items.push({
            id: `tag_${hash32(`${tagPhrase}::${id}::${start}::${end}`)}`,
            tag: tagPhrase,
            researchItemId: id,
            start,
            end,
            text: excerpt,
            origin: "ai",
            createdAt: t,
            updatedAt: t
          });

          taken++;
          if (re.lastIndex === m.index) re.lastIndex++;
        }
      } catch {}

      // If tag has anchor phrases in description, try anchoring them.
      if (taken === 0 && vocabDescMap && vocabDescMap.get(tagPhrase)) {
        const anchors = extractAnchorPhrasesFromDescription(vocabDescMap.get(tagPhrase));
        if (DEBUG_AI_TAGS && anchors.length) {
          void 0;
        }
        for (const anchor of anchors) {
          const anchored = anchorExcerptInDoc(anchor, fullText);
          if (!anchored) continue;
          if (isQuestionLineForSpan(fullText, anchored.start)) continue;
          const excerpt = makeWindowExcerpt(fullText, anchored.start, anchored.end, 80);
          items.push({
            id: `tag_${hash32(`${tagPhrase}::${id}::${anchored.start}::${anchored.end}`)}`,
            tag: tagPhrase,
            researchItemId: id,
            start: anchored.start,
            end: anchored.end,
            text: excerpt,
            origin: "ai",
            createdAt: t,
            updatedAt: t
          });
          taken++;
          if (taken >= MAX_PER_TAG_PER_DOC) break;
        }
      }

      // Token fallback: allow a single strong token match when the line signals a preference/constraint.
      if (taken === 0 && tagTokens.length) {
        for (const tok of tagTokens) {
          const tokRe = new RegExp(`\\b${escapeRegExp(tok)}\\b`, "gi");
          let m;
          while ((m = tokRe.exec(fullText)) !== null && taken < MAX_PER_TAG_PER_DOC) {
            const start = m.index;
            const end = m.index + m[0].length;
            if (isQuestionLineForSpan(fullText, start)) {
              if (tokRe.lastIndex === m.index) tokRe.lastIndex++;
              continue;
            }
            const excerpt = makeWindowExcerpt(fullText, start, end, 80);
            items.push({
              id: `tag_${hash32(`${tagPhrase}::${id}::${start}::${end}`)}`,
              tag: tagPhrase,
              researchItemId: id,
              start,
              end,
              text: excerpt,
              origin: "ai",
              createdAt: t,
              updatedAt: t
            });
            taken++;
            if (tokRe.lastIndex === m.index) tokRe.lastIndex++;
          }
          if (taken >= MAX_PER_TAG_PER_DOC) break;
        }
      }

      if (items.length >= minItems) return items;
    }

    if (items.length >= minItems) return items;
  }

  return items;
}

function ensureAllTagsInThemes(tagThemes, allTagsSet) {
  const t = now();
  const tagsSet = allTagsSet instanceof Set ? allTagsSet : new Set(allTagsSet || []);
  if (!tagsSet.size) return tagThemes;

  const base =
  tagThemes && typeof tagThemes === "object" ?
  tagThemes :
  { version: 1, themes: [], createdAt: t, updatedAt: t };

  const themes = Array.isArray(base.themes) ? base.themes.slice() : [];

  const assigned = new Set();
  themes.forEach((theme) => {
    (Array.isArray(theme?.tags) ? theme.tags : []).forEach((tag) => {
      if (tag) assigned.add(tag);
    });
  });

  const missing = Array.from(tagsSet).filter((tag) => tag && !assigned.has(tag));
  if (!missing.length) return base;

  const unassignedId = "theme_unassigned";
  const idx = themes.findIndex((t) => t?.id === unassignedId);

  if (idx >= 0) {
    const existing = themes[idx];
    const existingTags = Array.isArray(existing?.tags) ? existing.tags : [];
    const merged = Array.from(new Set([...existingTags, ...missing]));
    themes[idx] = { ...existing, tags: merged, updatedAt: t };
  } else {
    themes.push({
      id: unassignedId,
      title: "Unassigned",
      order: themes.length,
      tags: missing,
      origin: "system",
      createdAt: t,
      updatedAt: t
    });
  }

  return { ...base, themes, updatedAt: t };
}

function extractKeywordsFromText(text) {
  const tokens = safeText(text).
  toLowerCase().
  replace(/[^a-z0-9\s]/g, " ").
  split(/\s+/).
  filter((w) => w.length >= 3 && !GENERIC_TAGS.has(w));

  return Array.from(new Set(tokens));
}

function extractAnchorTokensFromTag(tag) {
  const tokens = safeText(tag).
  toLowerCase().
  replace(/[^a-z0-9\s]/g, " ").
  split(/\s+/).
  filter((w) => w.length >= 2 && !GENERIC_TAGS.has(w));

  return Array.from(new Set(tokens));
}

function extractSentenceAt(text, index) {
  const t = normalizeDocText(text);
  if (!t) return "";

  const clampIndex = Math.max(0, Math.min(index, t.length - 1));
  const left = t.slice(0, clampIndex);
  const right = t.slice(clampIndex);

  const leftIdx = Math.max(left.lastIndexOf("."), left.lastIndexOf("!"), left.lastIndexOf("?"), left.lastIndexOf("\n"));

  const rightIdxRel = Math.min(
    ...[".", "!", "?", "\n"].map((ch) => {
      const idx = right.indexOf(ch);
      return idx === -1 ? Infinity : idx;
    })
  );

  const start = leftIdx === -1 ? 0 : leftIdx + 1;
  const end = rightIdxRel === Infinity ? t.length : clampIndex + rightIdxRel + 1;

  return t.slice(start, end).trim();
}

function findSentenceByRegex(text, re) {
  if (!re) return "";
  const match = text.match(re);
  if (match && typeof match.index === "number") {
    return extractSentenceAt(text, match.index);
  }
  return "";
}

function findSentenceByKeywords(text, keywords, minHits = 1) {
  if (!keywords.length) return "";
  const sentences = splitIntoSnippets(text);
  for (const sentence of sentences) {
    const lower = sentence.toLowerCase();
    let hits = 0;
    for (const kw of keywords) if (lower.includes(kw)) hits += 1;
    if (hits >= minHits) return sentence;
  }
  return "";
}

function findMatchWindowByRegex(text, re, maxLen = 50) {
  if (!re) return "";
  re.lastIndex = 0;
  const match = re.exec(text);
  if (!match || typeof match.index !== "number") return "";
  const start = match.index;
  const end = match.index + match[0].length;
  return makeWindowExcerpt(text, start, end, maxLen);
}

function findMatchWindowByKeywords(text, keywords, minHits = 1, maxLen = 50) {
  if (!keywords.length) return "";
  const sentences = splitIntoSnippets(text);
  for (const sentence of sentences) {
    const lower = sentence.toLowerCase();
    let hits = 0;
    for (const kw of keywords) if (lower.includes(kw)) hits += 1;
    if (hits < minHits) continue;

    const sentenceStart = text.indexOf(sentence);
    if (sentenceStart === -1) continue;

    let matchIdx = -1;
    let matchLen = 0;
    for (const kw of keywords) {
      const pos = lower.indexOf(kw);
      if (pos !== -1 && (matchIdx === -1 || pos < matchIdx)) {
        matchIdx = pos;
        matchLen = kw.length;
      }
    }

    const start = sentenceStart + Math.max(0, matchIdx);
    const end = start + Math.max(4, matchLen || 0);
    return makeWindowExcerpt(text, start, end, maxLen);
  }
  return "";
}

function repairMissingTagSpans({
  spanItems,
  finalVocabSet,
  insights,
  vocabDescMap,
  researchItemsById
}) {
  const t = now();
  const items = Array.isArray(spanItems) ? spanItems.slice() : [];
  const tagsWithSpans = new Set(items.map((s) => s.tag));
  const missingTags = Array.from(finalVocabSet).filter((tag) => !tagsWithSpans.has(tag));

  const docs = Array.from(researchItemsById.entries()).map(([id, text]) => ({
    id,
    text: normalizeDocText(text)
  }));

  const insightEvidenceByTag = new Map();
  (Array.isArray(insights?.items) ? insights.items : []).forEach((ins) => {
    const tags = Array.isArray(ins?.tags) ? ins.tags : [];
    const evidence = Array.isArray(ins?.evidence) ? ins.evidence : [];
    tags.forEach((tag) => {
      const key = normalizeTag(tag);
      if (!key) return;
      if (!insightEvidenceByTag.has(key)) insightEvidenceByTag.set(key, []);
      evidence.forEach((ev) => {
        const text = safeText(ev?.text).trim();
        if (text) insightEvidenceByTag.get(key).push(text);
      });
    });
  });

  const repairLog = [];

  for (const tag of missingTags) {
    let anchored = null;
    let method = "";

    const tagTokens = extractAnchorTokensFromTag(tag);
    const desc = vocabDescMap.get(tag) || "";
    const descTokens = extractKeywordsFromText(desc);
    const tagRegex = buildLooseTagRegex(tag);

    // 1) Insight evidence
    const evidenceList = insightEvidenceByTag.get(tag) || [];
    for (const ex of evidenceList) {
      for (const d of docs) {
        const found = anchorExcerptInDoc(ex, d.text);
        if (found) {
          anchored = { researchItemId: d.id, ...found };
          method = "insight_evidence";
          break;
        }
      }
      if (anchored) break;
    }

    // 2) Tag phrase regex (strong anchor)
    if (!anchored && tagRegex) {
      for (const d of docs) {
        const excerpt = findMatchWindowByRegex(d.text, tagRegex, 50);
        if (!excerpt) continue;
        const found = anchorExcerptInDoc(excerpt, d.text);
        if (found) {
          anchored = { researchItemId: d.id, ...found };
          method = "tag_phrase";
          break;
        }
      }
    }

    // 3) Tag tokens keywords
    if (!anchored && tagTokens.length) {
      const minHits = tagTokens.length >= 3 ? 2 : 1;
      for (const d of docs) {
        const excerpt = findMatchWindowByKeywords(d.text, tagTokens, minHits, 50);
        if (!excerpt) continue;
        const found = anchorExcerptInDoc(excerpt, d.text);
        if (found) {
          anchored = { researchItemId: d.id, ...found };
          method = "tag_keywords";
          break;
        }
      }
    }

    // 3.5) Canonical/abstract tags: use verbatim anchors listed in description
    // Expected format inside description: "Anchors: phrase one, phrase two, phrase three"
    if (!anchored && desc) {
      const anchorPart = desc.split(/anchors:\s*/i)[1];
      const anchors = (anchorPart ? anchorPart.split(",") : []).
      map((s) => s.trim()).
      filter((s) => s.length >= 4);

      for (const a of anchors) {
        for (const d of docs) {
          const found = anchorExcerptInDoc(a, d.text);
          if (found) {
            anchored = { researchItemId: d.id, ...found };
            method = "description_anchors";
            break;
          }
        }
        if (anchored) break;
      }
    }

    // 4) Description keywords
    if (!anchored && descTokens.length) {
      const minHits = descTokens.length >= 3 ? 2 : 1;
      for (const d of docs) {
        const excerpt = findMatchWindowByKeywords(d.text, descTokens, minHits, 50);
        if (!excerpt) continue;
        const found = anchorExcerptInDoc(excerpt, d.text);
        if (found) {
          anchored = { researchItemId: d.id, ...found };
          method = "description_keywords";
          break;
        }
      }
    }

    // 4b) Description anchors (comma or pipe separated)
    if (!anchored && desc) {
      const anchors = extractAnchorPhrasesFromDescription(desc);
      if (anchors.length) {
        for (const d of docs) {
          for (const a of anchors) {
            const found = anchorExcerptInDoc(a, d.text);
            if (found) {
              anchored = { researchItemId: d.id, ...found };
              method = "description_anchors";
              break;
            }
          }
          if (anchored) break;
        }
      }
    }

    // 5) Weak token match (still requires a real token hit)
    if (!anchored) {
      let weakTokens = Array.from(new Set([...tagTokens, ...descTokens]));
      if (!weakTokens.length) {
        weakTokens = safeText(tag).
        toLowerCase().
        replace(/[^a-z0-9\s]/g, " ").
        split(/\s+/).
        filter((w) => w.length >= 2);
      }
      if (weakTokens.length) {
        for (const d of docs) {
          const excerpt = findMatchWindowByKeywords(d.text, weakTokens, 1, 50);
          if (!excerpt) continue;
          const found = anchorExcerptInDoc(excerpt, d.text);
          if (found) {
            anchored = { researchItemId: d.id, ...found };
            method = "weak_tokens";
            break;
          }
        }
      }
    }

    if (anchored) {
      const start = anchored.start;
      const end = anchored.end;
      const text = anchored.text;

      items.push({
        id: `tag_${hash32(`${tag}::${anchored.researchItemId}::${start}::${end}`)}`,
        tag,
        researchItemId: anchored.researchItemId,
        start,
        end,
        text,
        origin: "repair",
        createdAt: t,
        updatedAt: t
      });

      tagsWithSpans.add(tag);
      repairLog.push({ tag, method, researchItemId: anchored.researchItemId });
    } else {
      repairLog.push({ tag, method: "no_anchor_found", researchItemId: null });
    }
  }

  return {
    items,
    missingTags,
    repairLog
  };
}


/* ------------------------------------------------------------------ */
/* Prompt facts extraction */
/* ------------------------------------------------------------------ */

export function extractPromptFacts(formData) {
  const facts = [];
  const seen = new Set();

  // Use only verbatim lines from user-provided fields (no added labels).
  const rawStrings = Object.entries(formData || {}).
  filter(([key, val]) => typeof val === "string" && val.trim()).
  filter(([key]) => !/research|transcript|support|interview|message|ticket/i.test(key)).
  map(([, val]) => String(val || ""));

  const combined = rawStrings.join("\n");
  const lines = combined.
  split(/\n+|(?<=[.!?])\s+|;+/g).
  map((s) => s.trim()).
  filter(Boolean);

  const constraintPattern =
  /\b(must|should|only|required|require|requires|required|prefer|preferred|cannot|can't|cant|dont|don't|avoid|limit|always|never|no\s+\w+|need\s+to|needs\s+to|need|needs|have\s+to|has\s+to|allowed|forbidden|prohibited|at\s+least|at\s+most|looking\s+for|looking\s+to|seeking|would\s+like|would\s+prefer|would\s+love|want\s+to|wants\s+to|want|cares?\s+about|important\s+to|expects?|asks?\s+for|asks?|must\s+have|should\s+have|has\s+to|have\s+to)\b/i;

  const constraintLines = [];
  const otherLines = [];

  for (const l of lines) {
    const normalized = normalizeForMatch(l);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);

    if (constraintPattern.test(l)) {
      constraintLines.push(l);
    } else if (l.length >= 6) {
      otherLines.push(l);
    }
  }

  const prioritized = [...constraintLines, ...otherLines];
  prioritized.forEach((line) => {
    if (facts.length >= 12) return;
    if (!facts.includes(line)) facts.push(line);
  });

  return facts.slice(0, 12);
}

/* ------------------------------------------------------------------ */
/* Insights normalization */
/* ------------------------------------------------------------------ */

function createInsightQualityMetrics() {
  return {
    insightsFlaggedGeneric: 0,
    insightsDroppedNoEvidence: 0,
    insightsRewrittenGeneric: 0,
    evidenceRecoveredResearchItemId: 0,
    evidenceLinkedToSpan: 0,
    insightsDeduped: 0
  };
}

function confidenceFromEvidenceCount(count) {
  const n = Number(count || 0);
  if (n >= 3) return "high";
  if (n === 2) return "medium";
  if (n === 1) return "low";
  return "low";
}

const GENERIC_INSIGHT_PHRASES = [
"important",
"better experience",
"easy to use",
"should improve",
"people need"];


function isGenericInsightText(text) {
  const t = normalizeForMatch(text);
  if (!t) return false;
  return GENERIC_INSIGHT_PHRASES.some((phrase) => t.includes(phrase));
}

function dedupeEvidenceByText(evidence) {
  const seen = new Set();
  const out = [];
  for (const ev of safeArray(evidence)) {
    const key = normalizeForMatch(ev?.text || "");
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(ev);
  }
  return out;
}

function insightTokens(text) {
  const t = normalizeForMatch(text);
  if (!t) return [];
  return t.split(/\s+/g).filter((tok) => tok.length >= 3);
}

function tokenOverlapRatio(aTokens, bTokens) {
  if (!aTokens.length || !bTokens.length) return 0;
  const setA = new Set(aTokens);
  const setB = new Set(bTokens);
  let overlap = 0;
  for (const tok of setA) {
    if (setB.has(tok)) overlap += 1;
  }
  return overlap / Math.min(setA.size, setB.size);
}

function dedupeInsightsSemantically(items, metrics) {
  const kept = [];
  for (const it of safeArray(items)) {
    const ideaKey = normalizeForMatch(it?.text || "");
    if (!ideaKey) continue;
    const tokens = insightTokens(it.text);
    let dupIdx = -1;
    for (let i = 0; i < kept.length; i += 1) {
      const other = kept[i];
      if (other._ideaKey === ideaKey || tokenOverlapRatio(tokens, other._tokens) > 0.8) {
        dupIdx = i;
        break;
      }
    }

    if (dupIdx === -1) {
      kept.push({ ...it, _ideaKey: ideaKey, _tokens: tokens });
      continue;
    }

    const currentEvidence = safeArray(it?.evidence).length;
    const existingEvidence = safeArray(kept[dupIdx]?.evidence).length;
    if (currentEvidence > existingEvidence) {
      kept[dupIdx] = { ...it, _ideaKey: ideaKey, _tokens: tokens };
    }
    if (metrics) metrics.insightsDeduped += 1;
  }

  return kept.map(({ _ideaKey, _tokens, ...rest }) => rest);
}

function linkEvidenceToSpans(insights, spanItems, metrics) {
  const spansByResearch = new Map();
  const spansByTag = new Map();
  const spansById = new Map();
  (Array.isArray(spanItems) ? spanItems : []).forEach((span) => {
    const researchItemId = String(span?.researchItemId || "").trim();
    if (!researchItemId || !span?.text) return;
    if (!spansByResearch.has(researchItemId)) spansByResearch.set(researchItemId, []);
    spansByResearch.get(researchItemId).push(span);
    const tag = normalizeTag(span?.tag || "");
    if (tag) {
      if (!spansByTag.has(tag)) spansByTag.set(tag, []);
      spansByTag.get(tag).push(span);
    }
    if (span?.id) spansById.set(String(span.id), span);
    if (span?.sourceSpanId) spansById.set(String(span.sourceSpanId), span);
  });

  const matchSpanForEvidence = (ev, insightTags = [], factSpans = []) => {
    const researchItemId = String(ev?.researchItemId || "").trim();
    const spans = researchItemId ? spansByResearch.get(researchItemId) || [] : [];
    const evText = normalizeForMatch(ev.text || "");
    if (spans.length && evText) {
      let best = null;
      for (const span of spans) {
        const spanText = normalizeForMatch(span.text || "");
        if (!spanText) continue;
        const overlaps =
        spanText.includes(evText) ||
        evText.includes(spanText) ||
        tokenOverlapRatio(insightTokens(spanText), insightTokens(evText)) > 0.6;
        if (!overlaps) continue;
        if (!best || spanText.length > normalizeForMatch(best.text || "").length) best = span;
      }
      if (best) return best;
    }

    if (factSpans.length) {
      const matchByText = factSpans.find((fs) => {
        const spanText = normalizeForMatch(fs?.span?.text || fs?.span?.excerpt || fs?.span?.fact || "");
        if (!spanText || !evText) return false;
        return spanText.includes(evText) || evText.includes(spanText);
      });
      if (matchByText) return matchByText;
    }

    const tags = safeArray(insightTags).map((t) => normalizeTag(t)).filter(Boolean);
    for (const tag of tags) {
      const taggedSpans = spansByTag.get(tag) || [];
      if (!taggedSpans.length) continue;
      const filtered = researchItemId ?
      taggedSpans.filter((s) => String(s.researchItemId || "").trim() === researchItemId) :
      taggedSpans;
      if (!filtered.length) continue;
      return { id: filtered[0]?.id || null, span: filtered[0] };
    }

    return null;
  };

  const nextItems = safeArray(insights?.items).map((ins) => {
    const factIds = safeArray(ins?.factIds).map((id) => String(id || "").trim()).filter(Boolean);
    const factSpans = factIds.
    map((id) => ({ id, span: spansById.get(id) })).
    filter((fs) => fs.span);
    const evidence = safeArray(ins?.evidence).map((ev, evIdx) => {
      if (!ev || typeof ev !== "object") return ev;
      const existingSpanText = safeText(ev.spanText || ev.quote).trim();
      if (existingSpanText) return ev;
      const spanFromId = ev.spanId ? spansById.get(String(ev.spanId)) : null;
      const matched =
      (spanFromId ? { id: String(ev.spanId), span: spanFromId } : null) ||
      matchSpanForEvidence(ev, ins?.tags, factSpans) || (
      factSpans.length && factSpans.length === safeArray(ins?.evidence).length ? factSpans[evIdx] : null) || (
      factSpans.length ? factSpans[0] : null);
      if (!matched || !matched.span) return ev;
      if (metrics) metrics.evidenceLinkedToSpan += 1;
      const factText = safeText(ev.factText || ev.text).trim();
      const spanId = matched.id || matched.span.id || null;
      const spanText = matched.span.excerpt || matched.span.text || matched.span.fact || "";
      return { ...ev, spanId, spanText, quote: spanText, factText };
    });
    return { ...ins, evidence };
  });

  return { ...(insights || {}), items: nextItems };
}

function improveInsightsQuality({ insights, spanItems, metrics }) {
  const cleaned = safeArray(insights?.items).
  map((it) => {
    if (!it || typeof it !== "object") return null;
    const text = safeText(it.text).trim() || "Untitled insight";
    if (isGenericInsightText(text) && metrics) metrics.insightsFlaggedGeneric += 1;
    const evidence = dedupeEvidenceByText(it.evidence);
    const sourcesCount = evidence.length;
    const confidence = confidenceFromEvidenceCount(sourcesCount);
    return {
      ...it,
      text,
      evidence,
      sourcesCount,
      confidence
    };
  }).
  filter(Boolean);

  const withSpans = linkEvidenceToSpans({ ...(insights || {}), items: cleaned }, spanItems, metrics);
  return withSpans;
}

function normalizeInsightItemsFromGroq(
rawInsights,
{ corpusText = "", researchItemsById = null, rawSpanItems = null, metrics = null } = {})
{
  const t = now();
  const arr = Array.isArray(rawInsights) ? rawInsights : [];
  const spanItemsRaw = Array.isArray(rawSpanItems) ? rawSpanItems : null;
  const spanTagById = new Map();
  const spansByTag = new Map();
  const spanIdByStart = new Map();
  const spanIdBySource = new Map();
  const spanItemBySource = new Map();
  if (spanItemsRaw) {
    spanItemsRaw.forEach((s, idx) => {
      const id = `span_${idx}`;
      const tag = normalizeTag(s?.tag || "");
      const sourceSpanId = safeText(s?.sourceSpanId).trim();
      if (tag) {
        spanTagById.set(id, tag);
        if (!spansByTag.has(tag)) spansByTag.set(tag, []);
        spansByTag.get(tag).push(id);
      }
      if (sourceSpanId && !spanIdBySource.has(sourceSpanId)) {
        spanIdBySource.set(sourceSpanId, id);
      }
      if (sourceSpanId && !spanItemBySource.has(sourceSpanId)) {
        spanItemBySource.set(sourceSpanId, s);
      }
      const start = Number.isFinite(Number(s?.start)) ? Number(s.start) : null;
      if (start != null && s?.id) {
        spanIdByStart.set(start, String(s.id));
      }
    });
  }
  const fixFactIdsForInsight = (factIds, tags) => {
    if (!spanItemsRaw || !spanItemsRaw.length) return factIds;
    const tagSet = new Set(safeArray(tags).map((t) => normalizeTag(t)).filter(Boolean));
    if (!tagSet.size) return factIds;
    const normalized = safeArray(factIds).
    map((f) => String(f || "").trim()).
    filter(Boolean).
    map((fid) => {
      const m = fid.match(/^span_(\d+)$/i);
      if (!m) return fid;
      const num = Number(m[1]);
      if (!Number.isFinite(num)) return fid;
      const sourceMapped = spanIdBySource.get(fid);
      if (sourceMapped) return sourceMapped;
      const mapped = spanIdByStart.get(num);
      return mapped || fid;
    });

    const filtered = normalized.filter((fid) => {
      const tag = spanTagById.get(fid);
      return tag && tagSet.has(tag);
    });
    if (filtered.length) return filtered;
    // If nothing matches, seed with one span per tag.
    const seeded = [];
    for (const tag of tagSet) {
      const ids = spansByTag.get(tag) || [];
      if (ids.length) seeded.push(ids[0]);
    }
    if (!seeded.length && DEBUG_AI_TAGS) {
      void 0;
    }
    return seeded.length ? seeded : factIds;
  };
  const buildEvidenceFromFactIds = (factIds, spanItems) => {
    const items = Array.isArray(spanItems) ? spanItems : [];
    if (!Array.isArray(factIds) || !factIds.length || !items.length) return [];
    return factIds.
    map((fid) => {
      const id = String(fid || "").trim();
      if (!id) return null;
      const sourceMapped = spanIdBySource.get(id);
      const resolvedId = sourceMapped || id;
      let span = null;
      const m = resolvedId.match(/^span_(\d+)$/i);
      if (m) {
        const idx = Number(m[1]);
        span = Number.isFinite(idx) ? items[idx] : null;
        if (!span) {
          const start = Number.isFinite(idx) ? idx : null;
          if (start != null) {
            span = items.find((s) => Number(s?.start) === start);
          }
        }
      }
      if (!span) {
        span = items.find((s) => String(s?.id || "").trim() === resolvedId);
      }
      if (!span && spanItemBySource.has(id)) {
        span = spanItemBySource.get(id);
      }
      if (!span) return null;
      const spanText = safeText(span.excerpt || span.text || span.fact).trim();
      const factText = safeText(span.fact).trim();
      const text = spanText;
      if (!text) return null;
      const researchItemId = safeText(span.researchItemId).trim() || null;
      const resolvedSpanId = safeText(span.id).trim() || resolvedId;
      return {
        id: `ev_${hash32(`${id}::${text}`)}`,
        type: "quote",
        text,
        researchItemId,
        spanId: resolvedSpanId,
        sourceSpanId: resolvedId,
        spanText,
        factText: factText || null
      };
    }).
    filter(Boolean);
  };
  const items = arr.
  map((x, idx) => {
    if (typeof x === "string") {
      let text = String(x).trim();
      if (!text) text = "Untitled insight";
      const title = deriveInsightTitleFromText(text);
      if (isGenericInsightText(text) && metrics) metrics.insightsFlaggedGeneric += 1;
      const id = `ins_${hash32(text + "::" + idx)}`;
      const sourceType = "ai_suggested";
      const sourcesCount = 0;
      const confidence = confidenceFromEvidenceCount(sourcesCount);
      return {
        id,
        title,
        text,
        origin: "ai",
        isReviewed: false,
        createdAt: t,
        updatedAt: t,
        tags: [],
        confidence,
        evidence: [],
        factIds: [],
        source: { type: sourceType, label: "AI suggested" },
        sourceType,
        sourceLabel: "AI suggested",
        sourcesCount,
        order: idx,
        // Default to ungrouped to avoid forbidden "General/Other" group names.
        groupId: "grp_ungrouped"
      };
    }

    const it = x && typeof x === "object" ? x : null;
    if (!it) return null;

    let text = String(it.text || "").trim();
    if (!text) text = "Untitled insight";
    const rawTitle = safeText(it.title || "").trim();
    const title = rawTitle || deriveInsightTitleFromText(text);
    if (isGenericInsightText(text) && metrics) metrics.insightsFlaggedGeneric += 1;

    const category = normalizeCategory(it.category, "Other");
    const modelSourceType = normalizeSourceType(it.sourceType);
    const groupTitle = safeText(it.groupTitle || it.group || it.groupName).trim();

    let factIds = safeArray(it.factIds).map((f) => String(f || "").trim()).filter(Boolean);
    const tags = normalizeInsightTags(it.tags);
    factIds = fixFactIdsForInsight(factIds, tags);
    let evidence = factIds.length ? buildEvidenceFromFactIds(factIds, spanItemsRaw) : normalizeEvidenceItems(it.evidence);

    if (modelSourceType === "prompt") {
      evidence = evidence.map((ev) => ({ ...ev, researchItemId: null }));
    }

    if (modelSourceType === "research" && researchItemsById) {
      evidence = evidence.
      map((ev) => {
        if (!ev) return null;
        if (ev.researchItemId) return ev;
        const hit = findResearchItemIdForEvidence(ev.text, researchItemsById);
        if (!hit) {
          void 0;



          return null;
        }
        if (metrics) metrics.evidenceRecoveredResearchItemId += 1;
        return { ...ev, researchItemId: hit };
      }).
      filter(Boolean);
    }

    if (modelSourceType === "research" && !evidence.length) {
      const fb = fallbackExtractPhrases({ insightText: text, corpusText, maxPhrases: 4 });
      evidence = fb.map((q, i) => ({
        id: `ev_${hash32(q + "::" + i)}`,
        type: "quote",
        text: q,
        researchItemId: researchItemsById ? findResearchItemIdForEvidence(q, researchItemsById) : null
      }));
    }

    evidence = dedupeEvidenceByText(evidence);

    let finalType = modelSourceType;
    if (finalType !== "ai_suggested" && !evidence.length) finalType = "ai_suggested";

    const sourcesCount = evidence.length;
    const confidence = confidenceFromEvidenceCount(sourcesCount);
    const id = `ins_${hash32(text + "::" + idx)}`;
    return {
      id,
      title,
      text,
      category,
      origin: "ai",
      isReviewed: false,
      createdAt: t,
      updatedAt: t,
      tags: tags.length ? tags : [],
      confidence,
      evidence,
      factIds,
      source: {
        type: finalType,
        label: finalType === "prompt" ? "Project prompt" : finalType === "research" ? "Research" : "AI suggested"
      },
      sourceType: finalType,
      sourceLabel: finalType === "prompt" ? "Project prompt" : finalType === "research" ? "Research" : "AI suggested",
      sourcesCount,
      order: idx,
      groupTitle,
      // Default to ungrouped to avoid forbidden "General/Other" group names.
      groupId: "grp_ungrouped"
    };
  }).
  filter(Boolean);

  return { version: 4, items, createdAt: t, updatedAt: t };
}

/* ------------------------------------------------------------------ */
/* Virtual docs */
/* ------------------------------------------------------------------ */

export function buildVirtualDocsFromResearchItems(researchItems) {
  const included = (Array.isArray(researchItems) ? researchItems : []).filter(
    (it) => (it?.include ?? true) !== false
  );

  return included.
  map((it) => ({
    id: safeText(it?.id).trim(),
    label: safeText(it?.label || it?.title || it?.name || "Research").trim(),
    text: normalizeDocText(it?.rawText || it?.text || "").trim()
  })).
  filter((d) => d.id && d.text);
}

export function buildVirtualDocsFromWizardFields(formData) {
  const bundled = parseBundledResearchBlocks(formData?.researchTranscripts || "");
  if (bundled.length) return bundled;

  const interviewNotes = normalizeDocText(formData?.researchInterviewNotes || "").trim();
  const customerMessages = normalizeDocText(formData?.researchCustomerMessages || "").trim();
  const supportTickets = normalizeDocText(formData?.researchSupportTickets || "").trim();
  const transcripts = normalizeDocText(formData?.researchTranscripts || "").trim();

  return [
  interviewNotes ? { id: "ri_interviews", label: "Interview notes", text: interviewNotes } : null,
  customerMessages ? { id: "ri_messages", label: "User feedback", text: customerMessages } : null,
  supportTickets ? { id: "ri_tickets", label: "Support tickets", text: supportTickets } : null,
  transcripts ? { id: "ri_other", label: "Other transcripts", text: transcripts } : null].
  filter(Boolean);
}

export function buildCorpusText(virtualDocs) {
  const docs = Array.isArray(virtualDocs) ? virtualDocs : [];
  if (!docs.length) return "";
  return docs.map((d) => `Document (${d.id}) — ${d.label}\n${d.text}`).join("\n\n-----\n\n");
}

const MAX_SPANS_FOR_AI_PROMPT = 220;
const MAX_EVIDENCE_PER_INSIGHT_FOR_AI_PROMPT = 5;
const MAX_TEXT_CHARS_FOR_AI_PROMPT = 520;
const MAX_EXCERPT_CHARS_FOR_AI_PROMPT = 340;

function truncateForAIPrompt(value, maxChars) {
  const text = safeText(value).replace(/\s+/g, " ").trim();
  if (!text || !Number.isFinite(maxChars) || maxChars <= 0) return text;
  return text.length > maxChars ? `${text.slice(0, maxChars).trimEnd()}…` : text;
}

function estimateJsonPayloadSize(value) {
  const json = JSON.stringify(value ?? {});
  const chars = json.length;
  const approxTokens = Math.ceil(chars / 4);
  return { chars, approxTokens };
}

function compactProjectContextForAIPrompt(projectContext = {}) {
  const src = projectContext && typeof projectContext === "object" ? projectContext : {};
  return {
    id: safeText(src.id || src.projectId || "").trim(),
    name: safeText(src.name || src.projectName || "").trim(),
    productDescription: safeText(src.productDescription || "").trim(),
    industry: safeText(src.industry || "").trim(),
    targetAudience: safeText(src.targetAudience || "").trim()
  };
}

function compactTagVocabularyForAIPrompt(tagVocabulary) {
  const items = safeArray(tagVocabulary?.items).
  map((item) => {
    const tag = safeText(item?.tag || item?.name || "").trim().toLowerCase();
    if (!tag) return null;
    const description = truncateForAIPrompt(item?.description || "", 140);
    return { tag, ...(description ? { description } : {}) };
  }).
  filter(Boolean);

  const byTag = new Map();
  items.forEach((item) => {
    if (byTag.has(item.tag)) return;
    byTag.set(item.tag, item);
  });

  return { items: Array.from(byTag.values()) };
}

function compactResearchTagSpansForAIPrompt(researchTagSpans, maxItems = MAX_SPANS_FOR_AI_PROMPT) {
  const rawItems = safeArray(researchTagSpans?.items);
  const deduped = [];
  const seen = new Set();

  for (const span of rawItems) {
    const tag = safeText(span?.tag || "").trim().toLowerCase();
    const researchItemId = safeText(span?.researchItemId || "").trim();
    const excerpt = truncateForAIPrompt(
      span?.excerpt || span?.text || span?.fact || span?.context || span?.spanText || "",
      MAX_EXCERPT_CHARS_FOR_AI_PROMPT
    );
    if (!tag || !researchItemId || !excerpt) continue;
    const key = `${tag}|${researchItemId}|${excerpt}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push({
      tag,
      researchItemId,
      excerpt
    });
    if (deduped.length >= maxItems) break;
  }

  return { items: deduped };
}

function buildFallbackResearchTagSpansFromApprovedInsights(approvedInsightsSnapshot, maxItems = MAX_SPANS_FOR_AI_PROMPT) {
  const fallback = [];
  const seen = new Set();

  safeArray(approvedInsightsSnapshot).forEach((insight) => {
    const tags = safeArray(insight?.tags).
    map((tag) => safeText(tag).trim().toLowerCase()).
    filter(Boolean);
    const primaryTag = tags[0] || "";
    if (!primaryTag) return;

    safeArray(insight?.evidence).forEach((ev) => {
      const researchItemId = safeText(ev?.researchItemId || "").trim();
      const excerpt = truncateForAIPrompt(
        ev?.text || ev?.factText || ev?.quote || ev?.spanText || "",
        MAX_EXCERPT_CHARS_FOR_AI_PROMPT
      );
      if (!researchItemId || !excerpt) return;
      const key = `${primaryTag}|${researchItemId}|${excerpt}`;
      if (seen.has(key)) return;
      seen.add(key);
      fallback.push({
        tag: primaryTag,
        researchItemId,
        excerpt
      });
    });
  });

  return { items: fallback.slice(0, maxItems) };
}

function compactApprovedInsightsForAIPrompt(approvedInsightsSnapshot) {
  return safeArray(approvedInsightsSnapshot).
  map((insight, idx) => {
    if (!insight || typeof insight !== "object") return null;
    const id = safeText(insight?.id || "").trim() || `ins_${idx + 1}`;
    const text = truncateForAIPrompt(
      insight?.text || insight?.statement || insight?.title || "",
      MAX_TEXT_CHARS_FOR_AI_PROMPT
    );
    if (!text) return null;
    const tags = safeArray(insight?.tags).
    map((tag) => safeText(tag).trim().toLowerCase()).
    filter(Boolean).
    slice(0, 8);
    const confidenceRaw = safeText(insight?.confidence || "").trim().toLowerCase();
    const confidence = ["low", "medium", "high"].includes(confidenceRaw) ? confidenceRaw : "medium";
    const evidenceSeen = new Set();
    const evidence = safeArray(insight?.evidence).
    map((ev) => {
      const text = truncateForAIPrompt(
        ev?.text || ev?.factText || ev?.quote || ev?.spanText || "",
        MAX_EXCERPT_CHARS_FOR_AI_PROMPT
      );
      const fact = truncateForAIPrompt(ev?.factText || ev?.fact || "", MAX_EXCERPT_CHARS_FOR_AI_PROMPT);
      const researchItemId = safeText(ev?.researchItemId || "").trim();
      if (!text) return null;
      const key = `${researchItemId}|${text}`;
      if (evidenceSeen.has(key)) return null;
      evidenceSeen.add(key);
      const includeFact = Boolean(fact) && String(fact).trim().toLowerCase() !== String(text).trim().toLowerCase();
      return researchItemId ?
      { text, researchItemId, ...(includeFact ? { fact } : {}) } :
      { text, ...(includeFact ? { fact } : {}) };
    }).
    filter(Boolean).
    slice(0, MAX_EVIDENCE_PER_INSIGHT_FOR_AI_PROMPT);
    const sourcesCount =
    Number.isFinite(Number(insight?.sourcesCount)) ?
    Number(insight.sourcesCount) :
    Math.max(evidence.length, safeArray(insight?.sources).length, safeArray(insight?.evidenceSources).length, 1);

    return { id, text, tags, confidence, sourcesCount, evidence };
  }).
  filter(Boolean);
}

function buildCompactAIPayload({
  projectContext = {},
  approvedInsightsSnapshot = [],
  foundationBundle = {},
  researchItemsMeta = []
} = {}) {
  const compactedSpans = compactResearchTagSpansForAIPrompt(foundationBundle?.researchTagSpans || {});
  const spansForPrompt = compactedSpans.items.length ?
  compactedSpans :
  buildFallbackResearchTagSpansFromApprovedInsights(approvedInsightsSnapshot);

  return {
    project: compactProjectContextForAIPrompt(projectContext),
    approvedInsights: compactApprovedInsightsForAIPrompt(approvedInsightsSnapshot),
    foundation: {
      tagVocabulary: compactTagVocabularyForAIPrompt(foundationBundle?.tagVocabulary || {}),
      researchTagSpans: spansForPrompt,
      tagThemes: {
        themes: safeArray(foundationBundle?.tagThemes?.themes).
        map((theme, idx) => {
          const title = safeText(theme?.title || "").trim();
          if (!title) return null;
          const tags = safeArray(theme?.tags).
          map((tag) => safeText(tag).trim().toLowerCase()).
          filter(Boolean).
          slice(0, 12);
          return {
            id: safeText(theme?.id || "").trim() || `theme_${idx + 1}`,
            title,
            tags
          };
        }).
        filter(Boolean)
      }
    },
    researchItems: safeArray(researchItemsMeta).map((item) => ({
      id: safeText(item?.id || "").trim(),
      label: safeText(item?.label || "").trim()
    }))
  };
}

function compactPromptFactsForOverview(promptFacts = [], projectContext = {}) {
  const contextCorpus = [
  projectContext?.name,
  projectContext?.projectName,
  projectContext?.productDescription,
  projectContext?.targetAudience,
  projectContext?.industry].

  map((v) => safeText(v).trim().toLowerCase()).
  filter(Boolean).
  join(" ");

  const seen = new Set();
  const out = [];
  safeArray(promptFacts).forEach((fact) => {
    const trimmed = safeText(fact).trim();
    if (!trimmed) return;
    const normalized = trimmed.toLowerCase();
    if (seen.has(normalized)) return;
    if (contextCorpus && contextCorpus.includes(normalized)) return;
    seen.add(normalized);
    out.push(trimmed);
  });
  return out;
}

/* ------------------------------------------------------------------ */
/* Prompt builder */
/* ------------------------------------------------------------------ */

// Legacy foundation prompt removed in 2-phase flow.
// If you’re testing “tags-only”, keep foundation empty as you said:
export function buildGroqFoundationPrompt() {
  return "";
}

export function buildGroqExtractorPrompt({
  corpusText
}) {
  return `
You are a UX research extraction assistant.

Return exactly ONE valid JSON object.
Do not output any text before or after the JSON.

Use ONLY the research corpus.
Do NOT use project info or prompt facts as evidence.

TASK
------------------------------------------------

Extract:

1. leaf tags
2. research spans

A leaf tag represents one explicit concept mentioned by a participant.

------------------------------------------------
WHAT TO EXTRACT
------------------------------------------------

Create tags for explicit participant signals such as:

- preferences
- constraints
- concerns or risks
- rules or prohibitions
- requirements
- service expectations
- timing or schedule needs
- pricing or payment preferences
- trust or verification needs
- access conditions
- operational details
- motivations
- benefits or outcomes

Each distinct concept should produce its own tag.

------------------------------------------------
TAG RULES
------------------------------------------------

Each tag must represent ONE concept only.

Do NOT combine multiple rules or constraints into one tag.

Bad example:
delivery rules and payment terms

Correct:
delivery rules
payment terms

Tag format:

tagVocabulary.items[].tag
- short normalized label
- 1–5 words
- lowercase
- words separated by spaces
- do NOT use underscores

Examples:
payment preference
delivery timing
verification need
safety concern

GROUPING RULES
------------------------------------------------

Before creating a new tag, check whether the signal can be represented as another anchor under an existing tag.

If multiple mentions describe different values or variants of the same concept, group them under one tag and store the differences as anchors in description.

Prefer fewer broader tags with multiple anchors over many narrow tags with one anchor.
Group mentions under one tag only when they are variants of the same concept type.
Do not group timing, frequency, and service model into one tag unless they express the same underlying need.
After drafting tags, do one consolidation pass before finalizing output.
Merge mentions into one tag when they are variants or checklist details of the same underlying concept; use anchors for the specific details.
------------------------------------------------
ANCHOR RULES
------------------------------------------------

Each tag may contain one or more anchors.

Anchors must be placed in:

tagVocabulary.items[].description

as a | separated list.

Anchor rules:

- anchors must be exact verbatim substrings from participant answers
- do not paraphrase anchors
- do not shorten phrases
- prefer meaningful phrases over single words
- anchors should usually be short phrases, not full clauses.
Example:

tag: "delivery timing"
description: "in the morning | on weekdays | after work"

------------------------------------------------
SPAN RULES
------------------------------------------------

Each anchor must have exactly ONE supporting span.

Each span must include:

tag
anchor
researchItemId
excerpt
fact

- Excerpt must come from participant answer text only, never from interviewer/question text.
- If an answer contains multiple distinct signals, use the smallest excerpt that supports the current anchor.
- Prefer a focused phrase or clause over a long list or full sentence when possible.
- Reuse a longer excerpt only when a shorter exact excerpt would lose meaning.

Excerpt rules:

- must be a verbatim substring from the corpus
- must contain the anchor
- 10–140 characters when possible
- Do not use question lines as excerpts.

If one tag contains multiple anchors, create one span per anchor.
Different anchors under the same tag may reference the same excerpt if appropriate.
------------------------------------------------
ACTOR RULES
------------------------------------------------

The actor is the concrete role or entity expressing the signal.

Actor rules:

- use the most specific actor identifiable from the excerpt
- the actor must be a concrete noun phrase
- do NOT use vague actor labels such as:
  - participant
  - person
  - someone
  - people
- use a domain-specific actor when the excerpt supports it
- if no more specific role is identifiable, use "user"

Examples:
dog owner
customer
buyer
parent
walker
service provider
student
employee
user

------------------------------------------------
FACT RULES
------------------------------------------------

The fact explains the signal expressed in the excerpt.

Fact rules:

- one short sentence
- must start with the actor
- actor must follow the ACTOR RULES
- do NOT start the fact with:
  - Participant
  - Person
  - Someone
  - People
- avoid explanations or interpretation
- keep the fact concise

Examples:

Dog owner prefers early evening walks.
Customer prefers weekday deliveries.
Parent requires identity verification.
User prefers simple payment methods.

COVERAGE RULE:
Create a tag for every explicit participant mention of:
- preference
- constraint
- concern or risk
- rule or prohibition
- requirement
- timing or schedule need
- pricing or payment preference
- trust or verification need
- access condition
- operational detail
- motivation
- benefit or expected outcome

Do not skip a signal just because it appears only once.

RECALL RULE:
After drafting the tags, re-scan every participant answer line by line.
If an answer contains more than one distinct signal, create additional tags.
Prefer too many valid tags over missing explicit signals.

SPAN REQUIREMENT (HARD):
- researchTagSpans.items MUST NOT be empty if tagVocabulary.items is not empty.
- For every tag in tagVocabulary.items, output at least one researchTagSpans.items entry with the exact same tag value.
- Every anchor listed in tagVocabulary.items[].description must appear in exactly one researchTagSpans.items entry for that same tag.
- Every researchTagSpans.items entry must use an anchor that appears in tagVocabulary.items[].description for that tag.
- If a tag has no supporting span, remove that tag.
- If a tag description contains an anchor with no supporting span, remove that anchor from the description.
- If a sentence contains multiple distinct signals, create multiple tags referencing the same excerpt.
- If a sentence contains multiple anchors under the same tag, create multiple spans referencing the same excerpt if appropriate.

------------------------------------------------
OUTPUT RULES
------------------------------------------------

Output ONLY these top-level keys:

tagVocabulary
researchTagSpans

Do NOT output any other keys.

Do NOT output metadata fields such as:

id
version
start
end
text
origin
createdAt
updatedAt
displayItems
internalCount

------------------------------------------------
RETURN EXACT JSON SHAPE
------------------------------------------------

{
  "tagVocabulary": {
    "items": []
  },
  "researchTagSpans": {
    "items": []
  }
}

------------------------------------------------
EXAMPLE
------------------------------------------------

{
  "tagVocabulary": {
    "items": [
      {
        "tag": "delivery timing",
        "description": "in the morning | on weekdays | after work"
      }
    ]
  },
  "researchTagSpans": {
    "items": [
      {
        "tag": "delivery timing",
        "anchor": "in the morning",
        "researchItemId": "research_item_1",
        "excerpt": "I usually prefer deliveries in the morning.",
        "fact": "Customers prefers deliveries in the morning."
      }
      {
        "tag": "delivery timing",
        "anchor": "on weekdays",
        "researchItemId": "research_item_2",
        "excerpt": "Weekdays work better for me than weekends.",
        "fact": "Customers prefers weekdays."
      },
      {
        "tag": "delivery timing",
        "anchor": "after work",
        "researchItemId": "research_item_3",
        "excerpt": "If needed, after work is also fine.",
        "fact": "Customers can also accept support after work."
      }
    ]
  }
}

==============================
RESEARCH CORPUS
==============================
${corpusText || "(none)"}
`.trim();
}
function buildGroqSynthesizerPrompt({ formData, promptFacts, researchItemIds, tagVocabulary, researchTagSpans }) {
  const ids = Array.from(
    new Set((Array.isArray(researchItemIds) ? researchItemIds : []).map((id) => safeText(id).trim()).filter(Boolean))
  );

  const spanIdList = Array.isArray(researchTagSpans?.items) ?
  researchTagSpans.items.map((_, idx) => `span_${idx}`) :
  [];

  const spanCount = spanIdList.length;

  return `
You are a UX research synthesis assistant.
Return ONLY valid JSON.

PROJECT
Project name: ${safeText(formData.projectName)}
Product: ${safeText(formData.productDescription)}
Target audience: ${safeText(formData.targetAudience)}
Industry: ${safeText(formData.industry)}

PROMPT FACTS (verbatim)
${promptFacts.length ? promptFacts.map((x) => `- ${x}`).join("\n") : "- (none)"}

RESEARCH ITEM IDS
${ids.length ? ids.map((id) => `- ${id}`).join("\n") : "- (none)"}

TAG VOCABULARY (verbatim)
${JSON.stringify(tagVocabulary || {})}

RESEARCH TAG SPANS (verbatim)
${JSON.stringify(researchTagSpans || {})}

VALID SPAN IDS (use ONLY these, exactly)
${spanIdList.length ? spanIdList.join(", ") : "(none)"}

SPAN COUNT
${spanCount}

RETURN EXACT JSON SHAPE:
{
  "tagThemes": { "themes": [] },
  "overview": { "researchInsights": [{ "title": "string", "text": "string", "category": "string", "groupTitle": "string", "sourceType": "research|prompt|ai_suggested", "confidence": "high|medium|low|hypothesis", "tags": ["string"], "factIds": ["span_id"] }] },
  "problemDefinition": "string",
  "weWill": "string",
  "insightGroups": { "groups": [] },
  "emergingSignals": { "items": [], "generatedAt": "timestamp" }
}

==============================
RULES (DO NOT VIOLATE)
==============================

LIGHTWEIGHT INSIGHTS PREVIEW (HARD)
------------------------------------------------
This is an early synthesis preview for the Insights page, NOT a final Overview artifact.
Generate both fields in THIS SAME RESPONSE as overview.researchInsights.

Grounding:
- All claims must be grounded only in the current research corpus.
- If something is not supported by the current research corpus, do not include it.
- Do not invent metrics, counts, percentages, or KPI claims.

problemDefinition (rendered as "What this research suggests"):
- Exactly 1 sentence.
- Prefer <= 25 words.
- Summarize the clearest pattern/tension emerging from current research.
- Plain language only. No jargon, no generic filler.
- No framework language, no "Problem/Approach/Success Criteria" formatting.

weWill (rendered as "Opportunity"):
- Exactly 1 sentence.
- Prefer <= 20 words.
- Suggest a promising direction, not a final solution or plan.
- Directional language only; do NOT sound committed.
- Do NOT start with "We will".
- Do NOT use startup phrasing like "Create a service/platform/tool...".

Do NOT generate:
- success criteria, KPIs, metrics, roadmap, implementation steps
- "Problem / Approach / Success Criteria" subsections
- long narrative paragraphs or multi-part frameworks

PROMPT INSIGHTS
------------------------------------------------
Project prompt information may contain assumptions or hypotheses.
If meaningful hypotheses are detected, generate prompt insights.
Prompt insights must:
- be labeled as hypotheses
- contain no research evidence
- not be presented as validated findings
Prompt insights must be placed under the "Prompt insights" category/group.
Prompt insights are a separate stream and must NOT be mixed with research or AI-suggested insights.
Output prompt insights in overview.researchInsights with:
- sourceType = "prompt"
- confidence = "hypothesis"
- category = "Prompt insights"
- groupTitle = "Prompt insights"
- tags = []
- factIds = []
Add an insightGroups group titled "Prompt insights" and include their indexes.

PHASE 2 — BEHAVIORAL INSIGHTS
==================================================

After PHASE 1 is complete, generate behavioral insights from the Phase 1 data.

------------------------------------------------
INPUT
------------------------------------------------

You will receive Phase 1 structured research data with:

- tagVocabulary.items
- researchTagSpans.items

Each researchTagSpans item contains:
- tag
- researchItemId
- excerpt
- actor (optional)
- fact (optional)

------------------------------------------------
SOURCE OF TRUTH
------------------------------------------------

PRIMARY SOURCE:
- researchTagSpans.items[].excerpt
- researchTagSpans.items[].fact
- researchTagSpans.items[].actor

SECONDARY CONTEXT:
- researchTagSpans.items[].tag
- tagVocabulary.items[].tag
- tagVocabulary.items[].description

Use excerpts as the evidence.
Use facts to understand the meaning of the excerpts.
Use actor to keep the subject concrete.
Use tags only as lightweight labels/context.

Do NOT invent claims beyond what is supported by the excerpts and facts.
Do NOT create insights from tags alone.
Every insight must be supported by one or more span excerpts.

------------------------------------------------
REASONING STEP (DO NOT OUTPUT)
------------------------------------------------

Before writing insights, silently analyze the Phase 1 data:

1. Identify meaningful signals such as:
   - motivations
   - decision drivers
   - constraints
   - risks or concerns
   - explicit rules mentioned by participants
   - operational conditions
   - scheduling or access constraints
   - tradeoffs or willingness conditions
   - exceptions or edge cases
   - product opportunities

2. Identify the decisions participants are making.

3. Identify what motivations, risks, or constraints shape those decisions.

Then synthesize insights explaining the reasoning behind the behavior.

Do NOT output this reasoning.

------------------------------------------------
WHAT AN INSIGHT IS
------------------------------------------------

An insight explains:

- why users behave the way they do
- how they make decisions
- what constraints or motivations shape behavior
- what the signal implies for the product

An insight explains the behavioral logic behind the evidence, not the evidence itself.

An insight is NOT:
- a theme
- a topic label
- a paraphrase of a quote
- a simple observation

Avoid writing insights like:
"users value X"
"users want X"
"users prefer X"
"users prioritize X"

BAD:
"Users value safety"

GOOD title:
"Trust must be established before owners delegate dog walking to teens"

GOOD text:
"Dog owners are open to neighborhood teen walkers only after perceived safety risks are reduced. Verification, introductions, and clear handling rules make delegation feel safe enough."

Instead explain the reasoning behind the behavior.

------------------------------------------------
SYNTHESIS
------------------------------------------------

Combine excerpts into one insight ONLY when they clearly reflect the same underlying behavioral logic.

Do NOT create one insight per excerpt.

Do NOT merge signals that imply different:
- decisions
- constraints
- motivations
- product implications

If signals explain different behaviors, they must remain separate insights.

------------------------------------------------
INSIGHT GRANULARITY
------------------------------------------------

Each insight must describe ONE behavioral logic.

If multiple signals reflect different product implications
(e.g. safety rules, scheduling patterns, payment methods),
they must be written as separate insights.

Do NOT combine unrelated operational constraints into one insight.
Do not create a new insight if its main behavioral logic is already covered by another insight.
If a signal is already represented, only add a separate insight when it introduces a distinct behavioral or product implication.

------------------------------------------------
EVIDENCE
------------------------------------------------

Evidence must come from researchTagSpans.items[].excerpt.

Evidence must directly support the insight.

Use the minimum number of excerpts necessary to support the behavioral logic.

Facts may help interpret the excerpts, but evidence must remain verbatim excerpt text.

------------------------------------------------
COVERAGE
------------------------------------------------

The final set of insights must collectively represent the meaningful signals
present in the Phase 1 data.

Important signals include:
- recurring behavioral patterns
- constraints or rules
- risks or safety concerns
- trust or verification mechanisms
- scheduling or operational constraints
- tradeoffs
- motivations
- rare but meaningful signals
- product-relevant one-off statements

Signals that appear only once may still form an insight if they represent
a meaningful constraint, rule, exception, or product implication.

Do not ignore meaningful signals.

SIGNAL COVERAGE

Signals identified in the Phase 1 data may include:
- explicit rules or prohibitions (must, never, avoid, no, always)
- thresholds or ranges (ages, durations, frequencies)
- operational expectations (how the service should work)
- trust or verification mechanisms
- safety concerns or risks
- scheduling patterns
- payment or pricing conditions
- access constraints (home access, handoff methods)
- required qualities or qualifications
- motivations or benefits
- context drivers (schedule constraints, workload, availability)

Each distinct signal should normally produce its own insight.

Do NOT discard signals because they appear only once if they represent:
- a rule
- a constraint
- a requirement
- a safety condition
- an operational expectation
- a product-relevant detail

SIGNAL MERGING

Do not merge signals that belong to different categories.

Examples of signals that must remain separate:
- scheduling constraints
- payment models
- safety rules
- trust mechanisms
- operational expectations
- service parameters

If two excerpts represent different product implications,
they must become separate insights.

SERVICE EXPECTATIONS

Operational details mentioned by participants should be treated as meaningful signals.

Examples include:
- service duration
- service frequency
- distance or location constraints
- required outcomes of the service

These signals should be represented as insights when they influence how the service must work.

------------------------------------------------
INSIGHT COUNT
------------------------------------------------

Generate as many as possible insights to cover all meaningful signals in the Phase 1 data, but do not merge unrelated signals and do not create duplicates.

------------------------------------------------
INSIGHT CLARITY
------------------------------------------------

Insights should be concise and easy to understand.

Write insights in 1–2 clear sentences.

Avoid unnecessary wording or complex phrasing.

The insight should communicate the behavioral logic quickly and clearly.

------------------------------------------------
INSIGHT FORMAT (TITLE + TEXT)
------------------------------------------------

Each insight must have:
- title: short, sharp headline capturing the core behavioral insight
- text: 1–2 sentences explaining the behavioral logic, decision logic, constraint, or tradeoff

Rules:
- title must be concise, specific, and scannable
- text must not simply repeat the title
- text must explain why the behavior happens or what condition shapes the decision
- both must remain grounded in the evidence

------------------------------------------------
FINAL CHECK (DO NOT OUTPUT)
------------------------------------------------

Before returning the insights:

1. Re-scan the Phase 1 data.
2. Verify that all meaningful signals are represented in the insights.
3. If a signal with a distinct behavioral implication is missing, add an insight.
4. Ensure no insight is just a theme or excerpt summary.
5. Ensure unrelated behaviors are not merged into one insight.
6. Ensure every insight is supported by one or more excerpt evidence items.

--------------------------------------------------
TAG THEMES (HARD)
--------------------------------------------------

- Output tagThemes.themes as an array.
- Each theme must include: id, title, order, tags.
- tags must be strings from tagVocabulary.
- Every tag with spans must belong to exactly one theme.
- Themes are broader conceptual buckets for tags only.
- Themes must not replace insights.
- Do NOT create one insight per theme by default.
- Do NOT invent tags.

--------------------------------------------------
OVERVIEW.RESEARCHINSIGHTS (HARD)
--------------------------------------------------

- Output overview.researchInsights as an array.
- Each insight must include: title, text, category, groupTitle, sourceType, confidence, tags, factIds.
- title is required.
- text is required.
- category is required; prefer category = one of the insight.tags.
- groupTitle is required and must match the title of the insight group this insight belongs to.
- sourceType must be one of: research | prompt | ai_suggested.
- confidence must be one of: high | medium | low | hypothesis.
- tags must be tag strings from tagVocabulary.
- factIds must reference ONLY valid span ids from VALID SPAN IDS.
For prompt insights:
- title should clearly indicate it is a hypothesis or assumption
- text should explain the hypothesis briefly
- sourceType = prompt
- confidence = hypothesis
- category = "Prompt insights"
- groupTitle = "Prompt insights"
- tags = []
- factIds = []

--------------------------------------------------
INSIGHT GROUPS SHAPE (HARD)
--------------------------------------------------

- Output insightGroups.groups as an array.
- Each group must include: id, title, order, insightIndexes.
- Group titles must exactly match overview.researchInsights[*].groupTitle values.
- insightIndexes must reference indexes of overview.researchInsights.
- insightIndexes is the PRIMARY grouping contract. It must be correct.
- insightIndexes must be a JSON array of integer literals, not strings.
- Valid example: [0, 1, 11, 17]
- Invalid examples: [125710], ["01117"], ["0","1"], "0,1,11,17"
- Never concatenate multiple indexes into a single number or string.
- Never wrap indexes in quotes.
- Every insight index must appear exactly once across all groups.
- Recompute the indexes from the final order of overview.researchInsights immediately before answering.
- If there are 6 or more research insights, do NOT put all research insights into a single catch-all group.
- Prefer 4–7 groups for typical outputs.
- Each non-prompt group should contain at least 2 insights when possible.
- Merge adjacent or closely related topics into broader themes before creating a new group.
- Only create a single-insight group when the insight is genuinely distinct and would become misleading if merged.
- Grouping should reflect higher-level patterns, not just restate individual insight topics.
- Do NOT use generic research group titles such as:
  - Research insights
  - Behavioral insights
  - General insights
  - General
  - Core themes
  - Key findings
- Use specific thematic group titles instead.
- Prompt insights are the only allowed generic group title and must contain only prompt insights.

INSIGHT GROUP INDEX RULE (HARD):
insightGroups.groups[].insightIndexes must contain ONLY zero-based integer positions from overview.researchInsights.
If there are 11 insights, valid indexes are only 0 through 10.
Do NOT output ids, concatenated digits, strings like "012", or arbitrary numbers like 37 or 89.
Each insight index may appear in only one group.
--------------------------------------------------
EMERGING SIGNALS
Emerging signals represent early patterns or opportunity spaces suggested by the research.

Rules:
- Generate 1–3 signals only when meaningful patterns exist.
- Signals may combine multiple related spans or insights.
- Signals may cautiously extrapolate from the research but must remain grounded in it.
- Signals should describe broader behavioral patterns or potential product opportunities.
- Signals must be short and concise (1 sentence, 4–9 words).

Do NOT use:
- market trend claims
- growth projections
- demand forecasts
- speculative business language.
- new unmet needs
- new service categories
- future behavior
- industry-wide claims

If evidence is weak, produce narrow signals grounded only in the spans.


--------------------------------------------------
EMERGING SIGNALS SHAPE (HARD)
--------------------------------------------------

- Output emergingSignals.items as an array.
- Each signal must include: id, text.
- Output emergingSignals.generatedAt as a timestamp string.
Generate 1–3 signals only when meaningful patterns exist.

Signals may combine multiple related spans or insights.

Signals may cautiously extrapolate from the research but must remain grounded in it.

Signals should describe broader behavioral patterns or potential product opportunities.

Signals must be short and concise (1 sentence, 4–9 words).

`.trim();
}

function buildGroqOverviewPrompt({
  promptFacts,
  foundation,
  approvedInsightsSnapshot,
  researchItemsMeta,
  projectContext = {}
}) {
  const projectTypeRaw = safeText(projectContext?.projectType || "").trim().toLowerCase();
  const isNewProductProject = projectTypeRaw !== "existing";
  const decisionProjectType = isNewProductProject ?
  "new product / zero-to-one" :
  "feature / workflow / enhancement";

  return `
You are a UX research synthesis assistant.

Output ONLY valid JSON.

FOUNDATION DATA:
${JSON.stringify(foundation)}

APPROVED INSIGHTS SNAPSHOT:
${JSON.stringify(approvedInsightsSnapshot || [])}

RESEARCH ITEMS (id + label only):
${
  Array.isArray(researchItemsMeta) && researchItemsMeta.length ?
  JSON.stringify(researchItemsMeta) :
  "[]"}

PROMPT FACTS:
${
  promptFacts.length ? promptFacts.map((x) => `- ${x}`).join("\\n") : "- (none)"}

PROJECT CONTEXT:
${JSON.stringify(projectContext || {})}

PROJECT TYPE FOR DECISIONS:
${decisionProjectType}


EVIDENCE RULES

All claims must be grounded in:
- APPROVED INSIGHTS SNAPSHOT
- foundation.researchTagSpans.items[].excerpt
- PROMPT FACTS (if clearly referenced)

If a prompt insight or prompt fact has no research evidence, do NOT use it in any validated output
(including Problem Definition, decisions, keySignals, synthesis patterns, synthesis tensions, or opportunityAreas).
Instead, convert it into a research gap, open risk, or a question to validate, or omit it entirely.

Never invent:
- metrics
- counts
- frequencies
- percentages
- adoption expectations
- user behavior not present in evidence
- preferences not explicitly supported by evidence

If evidence is weak return empty arrays rather than speculation.


CRITICAL STRUCTURE RULE

You MUST output EVERY section listed in the JSON schema.

Do NOT omit sections even if empty.

If evidence is insufficient:
- return an empty array
- or empty string fields

Do NOT stop early.

Complete the entire JSON object before finishing.


RETURN EXACT JSON SHAPE

{
  "overview": {
    "executiveSummary": {
      "title": "",
      "text": ""
    },
    "synthesis": {
      "patterns": [],
      "questions": [],
      "tensions": []
    },
    "problemDefinition": {
      "problem": "",
      "weWill": "",
      "successCriteria": []
    },
    "keySignals": [],
    "researchQuestions": {
      "items": [],
      "generatedAt": ""
    },
    "coverageGaps": [],
    "opportunityAreas": [],
    "decisionsV2": [],
    "recommendedNextSteps": [],
    "openRisks": [],
    "audienceGroups": []
  }
}


SCHEMA RULES

keySignals[] =
{
  "title":"",
  "description":"",
  "signalType":"need|behavior|risk|preference",
  "confidence":"high|medium|low",
  "tags":[],
  "evidence":[]
}

synthesis =
{
  "patterns": ["string"],
  "questions": ["string"],
  "tensions": ["string"]
}

researchQuestions.items[] =
{
  "title":"",
  "description":"",
  "linkedInsightIds":[],
  "linkedInsightTitles":[],
  "linkedResearchItemIds":[],
  "methodRecommendation":{
    "primaryMethod":{
      "type":"interview|survey|usability_test|analytics_review|competitive_analysis|ab_test|support_analysis",
      "reason":""
    },
    "secondaryMethods":[],
    "confidence":"low|medium|high",
    "recommendedSequence":[]
  }
}

openRisks[] =
{
  "question":"",
  "reason":"",
  "priority":"high|medium|low",
  "affectsThemes":[]
}

coverageGaps[] =
{
  "title":"",
  "description":"",
  "impact":"",
  "action":"",
  "priority":"high|medium|low"
}

opportunityAreas[] =
{
  "title":"",
  "description":"",
  "linkedInsightIds":[],
  "priority":"high|medium|low"
}

audienceGroups[] =
{
  "id":"",
  "name":"",
  "description":"",
  "isPrimary":true
}

decisionsV2[] =
{
  "title":"",
  "description":"",
  "decisionType":"product|research|go_to_market|ops",
  "status":"proposed|approved",
  "riskLevel":"low|medium|high",
  "riskDrivers":[],
  "requiresValidation":[],
  "linkedInsightIds":[],
  "supportedBy":{"insights":0,"excerpts":0}
}

recommendedNextSteps[] =
{
  "title":"",
  "description":"",
  "actionKey":"personas|interview-questions|problem-definition|decisions|journey|insights"
}


FIELD RULES

Do NOT substitute fields.

Example of forbidden substitution:

Wrong:
plain string instead of keySignals object

Correct:
keySignals must always be objects.


SECTION INTENT

executiveSummary
1-2 sentences framing the core problem and direction, grounded in approved evidence.

synthesis
Emerging patterns, open questions, and theme tensions.
- Patterns: 2-5 bullets
- Questions: 2-6 bullets
- Tensions: 0-3 bullets
- Each bullet MUST include a theme name in brackets (e.g., [Theme: Safety & Trust]).

keySignals
strongest needs, behaviors, risks, or preferences.

researchQuestions
what must be validated next.
- For each research question, include 1-3 linkedInsightIds that best justify why this question matters.
- linkedInsightIds must come from APPROVED INSIGHTS SNAPSHOT ids only.
- Include linkedInsightTitles that match the linked insight titles (or text if no title).

openRisks
uncertainties affecting success.

coverageGaps
missing evidence or missing segments.

opportunityAreas
focused opportunity themes grounded in evidence.

audienceGroups
Synthesize the main audience groups from the insights and output them in audienceGroups.
Exactly ONE audience group must have isPrimary = true. All others must be false.
Primary audience is the group that decides, pays, adopts, and determines product success.
Do NOT choose primary based on research volume, mentions, or data quality.
Audience groups must be human user groups or roles that are useful for downstream artifact generation.
Exclude animals, objects, systems, product components, and abstract actors.
Use clean, user-facing labels, not raw extracted actors.
Keep the list concise and practical.
Only include groups that are meaningfully supported by the insights.

decisionsV2
strategic product, research, go-to-market, or operational direction supported by multiple signals.

recommendedNextSteps
2-4 concrete actions to progress learning or validation.

problemDefinition
Write this as a concise 3-part artifact:
- problem
- weWill (approach direction)
- successCriteria

problem rules:
- 1 concise core statement of the main user problem or tension
- max 1-2 sentences
- do not combine critique, solution, assumptions, and multiple side points in one long sentence

weWill rules:
- describe the approach direction clearly and concretely
- use direct action language
- avoid generic phrasing like "create a service that prioritizes..."
- max 1-2 sentences

successCriteria rules:
- output as a short list (array) of 3-4 concise, outcome-oriented criteria
- each criterion should be one line and easy to scan
- avoid abstract, padded, or repetitive phrasing
- only include outcomes supported by approved evidence
- numbers/percentages are allowed only when explicitly present in evidence

general writing rules:
- avoid run-on sentences
- avoid mixing too many ideas in one statement
- keep language stakeholder-friendly and product-artifact quality


PROJECT-TYPE DECISION RULE

- If PROJECT TYPE is new product / zero-to-one:
  - decisions should be higher-level and product-shaping
  - prefer 1-2 decisions
  - decisions should express product direction, MVP focus, main value, or major scope choices
  - avoid breaking the product into too many feature-level decisions

- If PROJECT TYPE is feature / workflow / enhancement:
  - decisions may be narrower and more scoped
  - prioritization, sequencing, rules, and scoped feature decisions are acceptable
  - generate 1-3 decisions when evidence supports it

Generate decisionsV2 only when there is enough evidence to support a real direction.

A valid decision MUST:
- express a commitment, priority, sequencing choice, scope boundary, rule, tradeoff, or clear directional choice
- be something a team could realistically confirm, challenge, or act on
- have consequences for scope, sequencing, rollout, validation, policy, or experience direction
- be supported by multiple signals or strong approved insight evidence

A decision MUST NOT:
- be a direct restatement of the problemDefinition
- be a feature fragment, requirement fragment, or implementation slice
- be a generic capability statement
- be a task disguised as a decision
- simply restate items already listed in "weWill" or "successCriteria"

For new product contexts:
- titles should stay concise and product-level
- titles may mention the primary focus of the MVP
- supporting capabilities can appear in the description
- do not split every important feature into its own decision

For feature contexts:
- titles may be more operational, but still must be real decisions, not tasks

Decision title rules:
- short, clear, directional
- should read like a product or business choice, not a task list item
- avoid starting with "Implement", "Design", "Add", "Create", or "Recruit"

Direction rationale rule (whyThisDirection):
- For every generated decision, output a whyThisDirection field (1–2 sentences) that:
  - cites at least one linked approved insight by id,
  - explains causal reasoning ("because insight X shows Y risk/opportunity..."),
  - explains why this direction is prioritized now (impact, urgency, or risk),
  - may include a trade-off or contrast with alternative directions,
  - states expected user or business outcome,
  - does NOT restate the decision verbatim,
  - avoids generic wording (no vague phrases like "improve experience" without evidence).
- If no supporting insight exists, do not generate the decision.

Decision count rules:
- new product / zero-to-one: prefer 1-2 decisions
- feature / workflow / enhancement: 1-3 decisions
- prefer fewer, stronger decisions over many weak ones
- if evidence supports opportunities but not commitments, return an empty decisionsV2 array

Decision evidence rules:
- each decision must be grounded in APPROVED INSIGHTS SNAPSHOT
- linkedInsightIds must contain only ids from APPROVED INSIGHTS SNAPSHOT
- include all linkedInsightIds that directly support the decision
- supportedBy.insights and supportedBy.excerpts should reflect actual support strength
- if support is weak or mostly speculative, do not generate the decision

Decision status rules:
- use "approved" only if the evidence and current synthesis clearly support a strong direction
- otherwise default to "proposed"


GENERAL RULES

- include 2-5 researchQuestions when uncertainty exists
- recommendedNextSteps should contain 2-4 actions
- avoid vague steps like "do more research"
- do not repeat ideas across sections
- do not fabricate evidence
- output ONLY valid JSON

`.trim();
}

function normalizeIdeaKey(text) {
  return safeText(text).
  toLowerCase().
  replace(/^(users|customers|participants|people)\s+want\s+/i, "").
  replace(/[^a-z0-9]+/g, " ").
  trim();
}

function dedupeAcrossOverviewSections(overview) {
  const priority = ["keySignals", "opportunityAreas", "decisionsV2", "openRisks", "coverageGaps"];
  const seen = new Set();
  const next = { ...overview };
  priority.forEach((key) => {
    const list = safeArray(next[key]);
    next[key] = list.filter((item) => {
      const text =
      key === "openRisks" ?
      item.question || item.text || item.title :
      key === "opportunityAreas" ?
      item.title || item.description :
      key === "decisionsV2" ?
      item.title :
      key === "keySignals" ?
      item.description || item.text || item.title :
      key === "coverageGaps" ?
      item.title || item.description :
      item.text || item.statement || item.title;
      if (/^(users|customers|participants|people)\s+want/i.test(String(text || ""))) return false;
      const norm = normalizeIdeaKey(text);
      if (!norm) return false;
      if (seen.has(norm)) return false;
      seen.add(norm);
      return true;
    });
  });
  return next;
}

function sanitizeStrings(obj) {
  if (typeof obj === "string") return obj.replace(/\n/g, "\\n").replace(/\t/g, " ");
  if (Array.isArray(obj)) return obj.map(sanitizeStrings);
  if (obj && typeof obj === "object") {
    const next = {};
    Object.entries(obj).forEach(([k, v]) => {
      next[k] = sanitizeStrings(v);
    });
    return next;
  }
  return obj;
}

const OVERVIEW_RECOMMENDED_METHOD_TYPES = new Set([
"interview",
"survey",
"usability_test",
"analytics_review",
"competitive_analysis",
"ab_test",
"support_analysis"]
);

function enforceOverviewRules(overview, vocabSet, spanTagsSet, researchItemIdSet = null, promptFacts = []) {
  const clampList = (arr, max) => {
    const list = safeArray(arr).filter(Boolean);
    if (list.length > max) return list.slice(0, max);
    return list;
  };

  const filterTags = (items) =>
  safeArray(items).map((item) => ({
    ...item,
    tags: safeArray(item.tags).
    map(normalizeTag).
    filter((t) => vocabSet.has(t) && spanTagsSet.has(t))
  }));

  const promptFactSet = new Set(
    safeArray(promptFacts).
    map((t) => safeText(t).trim()).
    filter(Boolean)
  );

  const normalizeEvidenceList = (evs, parentText = "") =>
  safeArray(evs).
  map((ev, idx) => {
    if (!ev || typeof ev !== "object") return null;
    const text = safeText(ev.text || "").trim();
    if (!text) return null;
    const id = safeText(ev.id || "").trim() || `ev_${hash32(text + "::" + idx)}`;
    const type = ["quote", "observation", "fact"].includes(String(ev.type || "").toLowerCase()) ?
    String(ev.type || "").toLowerCase() :
    inferEvidenceTypeFromText(text);
    const researchItemId =
    typeof ev.researchItemId === "string" && ev.researchItemId.trim() ? ev.researchItemId.trim() : null;
    if (researchItemIdSet && researchItemId && !researchItemIdSet.has(researchItemId)) return null;
    if (researchItemIdSet && !researchItemId && !promptFactSet.has(text)) return null;
    // Do not drop evidence based on token overlap; it is too lossy.
    return { ...ev, id, type, text, researchItemId };
  }).
  filter(Boolean);

  const withEvidence = (items) =>
  safeArray(items).
  map((item) => ({ ...item, evidence: normalizeEvidenceList(item.evidence, item.text || item.statement || "") })).
  filter((item) => safeArray(item.evidence).length > 0);

  const keySignals = filterTags(safeArray(overview.keySignals));

  const synthesisRaw = overview.synthesis && typeof overview.synthesis === "object" ? overview.synthesis : {};
  const synthesis = {
    patterns: clampList(synthesisRaw.patterns, 5),
    questions: clampList(synthesisRaw.questions, 6),
    tensions: clampList(synthesisRaw.tensions, 3)
  };

  const openRisks = clampList(overview.openRisks, 5);
  const opportunityAreas = clampList(overview.opportunityAreas, 4);
  const decisionsV2 = clampList(overview.decisionsV2, 3);
  const recommendedNextSteps = clampList(overview.recommendedNextSteps, 3);
  const coverageGaps = clampList(overview.coverageGaps, 3);
  const researchQuestionsRaw = overview.researchQuestions && typeof overview.researchQuestions === "object" ?
  overview.researchQuestions :
  { items: safeArray(overview.researchQuestions) };
  const researchQuestions = {
    ...researchQuestionsRaw,
    items: clampList(safeArray(researchQuestionsRaw.items), 3)
  };

  const clampSentences = (text, maxSentences = 2) => {
    const raw = safeText(text).trim();
    if (!raw) return "";
    const parts = raw.
    split(/(?<=[.!?])\s+/).
    map((part) => part.trim()).
    filter(Boolean);
    if (parts.length <= maxSentences) return raw;
    return parts.slice(0, maxSentences).join(" ");
  };

  const normalizeSuccessCriteriaItems = (value) => {
    if (Array.isArray(value)) {
      return value.
      map((item) => safeText(item).trim()).
      map((item) => item.replace(/^[-*•]\s*/, "").trim()).
      filter(Boolean).
      slice(0, 4);
    }
    const raw = safeText(value).trim();
    if (!raw) return [];
    return raw.
    split(/\r?\n+/).
    map((line) => line.replace(/^[-*•]\s*/, "").trim()).
    filter(Boolean).
    slice(0, 4);
  };

  const pdRaw = overview?.problemDefinition && typeof overview.problemDefinition === "object" ? overview.problemDefinition : {};
  const successCriteriaItems = normalizeSuccessCriteriaItems(pdRaw.successCriteriaItems || pdRaw.successCriteria);
  const successCriteriaText =
  typeof pdRaw.successCriteria === "string" ? safeText(pdRaw.successCriteria).trim() : "";
  const problemDefinition = {
    problem: clampSentences(pdRaw.problem, 2),
    weWill: clampSentences(pdRaw.weWill, 2),
    successCriteria: successCriteriaText || successCriteriaItems.join("\n"),
    successCriteriaItems
  };

  return {
    ...overview,
    keySignals: clampList(keySignals, 6),
    synthesis,
    openRisks,
    coverageGaps,
    opportunityAreas,
    researchQuestions,
    decisionsV2,
    decisions: safeArray(decisionsV2).
    map((d) => ({
      id: safeText(d.id || "").trim(),
      title: safeText(d.title || "").trim(),
      description: safeText(d.description || "").trim(),
      linkedInsightIds: safeArray(d.linkedInsightIds || d.insightIds || d.relatedInsightIds).
      map((id) => safeText(id).trim()).
      filter(Boolean)
    })).
    filter((d) => d.title),
    recommendedNextSteps,
    problemDefinition
  };
}

function normalizeOverviewArtifactsFromGroq(rawOverview, tagThemes, researchItemsById) {
  const o = rawOverview && typeof rawOverview === "object" ? rawOverview : {};
  const themeIds = new Set(
    (Array.isArray(tagThemes?.themes) ? tagThemes.themes : []).
    map((t) => String(t?.id || "").trim()).
    filter(Boolean)
  );

  const normalizeOverviewListText = (item) => {
    if (typeof item === "string") return safeText(item).trim();
    if (!item || typeof item !== "object") return "";
    const title = safeText(item.title || item.name || "").trim();
    const description = safeText(item.description || item.text || item.detail || "").trim();
    if (title && description && description.toLowerCase() !== title.toLowerCase()) return `${title}: ${description}`;
    return title || description;
  };

  const executiveSummaryRaw = o.executiveSummary && typeof o.executiveSummary === "object" ? o.executiveSummary : {};
  const summaryText = safeText(executiveSummaryRaw.text || o.executiveSummaryText || o.executiveSummary || "").trim();
  const synthesisRaw = o.synthesis && typeof o.synthesis === "object" ? o.synthesis : {};
  const synthesis = {
    patterns: safeArray(synthesisRaw.patterns).map(normalizeOverviewListText).filter(Boolean),
    questions: safeArray(synthesisRaw.questions).map(normalizeOverviewListText).filter(Boolean),
    tensions: safeArray(synthesisRaw.tensions).map(normalizeOverviewListText).filter(Boolean)
  };
  const opportunityAreas = safeArray(o.opportunityAreas || o.strategicDirections).
  map((d) => {
    if (!d || typeof d !== "object") return null;
    const title = safeText(d.title || "").trim();
    const description = safeText(d.description || d.detail || "").trim();
    if (!title) return null;
    return { title, description };
  }).
  filter(Boolean);

  const normalizeSignalEvidence = (items) =>
  safeArray(items).
  map((ev) => {
    if (!ev || typeof ev !== "object") return null;
    const text = safeText(ev.text || "").trim();
    if (!text) return null;
    const type = String(ev.type || "quote");
    const researchItemId = safeText(ev.researchItemId || "").trim() || null;
    return { ...ev, text, type, researchItemId };
  }).
  filter(Boolean);

  const normalizeTextItem = (item, idx, { field = "text" } = {}) => {
    const text = safeText(item?.[field] || item?.text || "").trim();
    if (!text) return null;
    return text;
  };

  const normalizeKeySignals = (items) => {
    const allowed = new Set(["need", "behavior", "risk", "preference"]);
    return safeArray(items).
    map((s, idx) => {
      if (typeof s === "string") {
        const raw = safeText(s).trim();
        if (!raw) return null;
        return {
          id: `sig_${hash32(`${raw}::${idx}`)}`,
          title: "",
          description: raw,
          signalType: "behavior",
          confidence: "medium",
          tags: [],
          evidence: []
        };
      }
      if (!s || typeof s !== "object") return null;
      const title = safeText(s.title || "").trim();
      const description = safeText(s.description || s.text || s.hypothesis || "").trim();
      if (!title && !description) return null;

      const signalType = allowed.has(String(s.signalType || "").trim()) ? String(s.signalType).trim() : "behavior";

      const confidence = ["high", "medium", "low"].includes(String(s.confidence || "").toLowerCase()) ?
      String(s.confidence).toLowerCase() :
      "medium";

      const tags = Array.from(new Set(safeArray(s.tags).map(normalizeTag).filter(Boolean)));

      let evidence = normalizeEvidenceItems(s.evidence);
      if (researchItemsById) {
        evidence = evidence.map((ev) => {
          if (ev.researchItemId) return ev;
          const hit = findResearchItemIdForEvidence(ev.text, researchItemsById);
          return hit ? { ...ev, researchItemId: hit } : ev;
        });
      }

      return {
        id: s.id || `sig_${hash32(`${title}::${description}::${idx}`)}`,
        title,
        description,
        signalType,
        confidence,
        tags,
        evidence
      };
    }).
    filter(Boolean);
  };

  const normalizeOpenRisks = (items) => {
    const allowed = new Set(["high", "medium", "low"]);
    return safeArray(items).
    map((r, idx) => {
      if (!r || typeof r !== "object") return null;
      let question = safeText(r.question || r.text || r.title || r.description || "").trim();
      if (!question) return null;
      if (!question.endsWith("?")) question = `${question}?`;

      const priority = allowed.has(String(r.priority || "").toLowerCase()) ? String(r.priority).toLowerCase() : "medium";

      const affectsThemes = safeArray(r.affectsThemes).
      map((t) => safeText(t).trim()).
      filter(Boolean).
      filter((t) => themeIds.size ? themeIds.has(t) : true);

      return {
        id: r.id || `risk_${hash32(question + "::" + idx)}`,
        question,
        affectsThemes,
        priority,
        reason: safeText(r.reason || r.mitigation || "").trim()
      };
    }).
    filter(Boolean);
  };

  const normalizeCoverageGaps = (items) => {
    const allowedPriority = new Set(["high", "medium", "low"]);
    return safeArray(items).
    map((g, idx) => {
      if (!g || typeof g !== "object") return null;
      const title = safeText(g.title || "").trim();
      const description = safeText(g.description || g.gap || g.explanation || "").trim();
      if (!title || !description && !safeText(g.impact || "").trim() && !safeText(g.action || g.suggestedInput || "").trim()) return null;
      const impact = safeText(g.impact || "").trim();
      const action = safeText(g.action || g.suggestedInput || "").trim();
      const priority = allowedPriority.has(String(g.priority || "").toLowerCase()) ?
      String(g.priority).toLowerCase() :
      "medium";
      return {
        id: g.id || `gap_${hash32(`${title}::${description}::${idx}`)}`,
        title,
        description,
        impact,
        action,
        priority
      };
    }).
    filter(Boolean);
  };

  const normalizeResearchQuestions = (value) => {
    const container = value && typeof value === "object" && Array.isArray(value.items) ? value : { items: safeArray(value) };
    const items = safeArray(container.items).
    map((q, idx) => {
      if (!q || typeof q !== "object") return null;
      const title = safeText(q.title || q.question || q.text || "").trim();
      if (!title) return null;
      const description = safeText(q.description || "").trim();
      const linkedInsightIds = safeArray(
        q.linkedInsightIds || q.insightIds || q.relatedInsightIds
      ).
      map((id) => safeText(id).trim()).
      filter(Boolean);
      const linkedInsightTitles = safeArray(
        q.linkedInsightTitles || q.insightTitles || q.relatedInsights
      ).
      map((title) => safeText(title).trim()).
      filter(Boolean);
      const rec = q.methodRecommendation && typeof q.methodRecommendation === "object" ? q.methodRecommendation : {};
      const primaryRaw = rec.primaryMethod && typeof rec.primaryMethod === "object" ? rec.primaryMethod : {};
      const primaryType = safeText(primaryRaw.type || "").trim();
      const primaryMethod = OVERVIEW_RECOMMENDED_METHOD_TYPES.has(primaryType) ?
      { type: primaryType, reason: safeText(primaryRaw.reason || "").trim() } :
      null;
      const secondaryMethods = safeArray(rec.secondaryMethods).
      map((m) => {
        if (!m || typeof m !== "object") return null;
        const type = safeText(m.type || "").trim();
        if (!OVERVIEW_RECOMMENDED_METHOD_TYPES.has(type)) return null;
        return { type, reason: safeText(m.reason || "").trim() };
      }).
      filter(Boolean).
      slice(0, 3);
      const confidenceRaw = safeText(rec.confidence || "").toLowerCase();
      const confidence = ["low", "medium", "high"].includes(confidenceRaw) ? confidenceRaw : "medium";
      const recommendedSequence = safeArray(rec.recommendedSequence).
      map((m) => safeText(m || "").trim()).
      filter((m) => OVERVIEW_RECOMMENDED_METHOD_TYPES.has(m));
      return {
        id: safeText(q.id || "").trim() || `rq_${hash32(`${title}::${idx}`)}`,
        title: title.endsWith("?") ? title : `${title}?`,
        description,
        linkedInsightIds,
        linkedInsightTitles,
        methodRecommendation: primaryMethod ?
        { primaryMethod, secondaryMethods, confidence, recommendedSequence } :
        null
      };
    }).
    filter(Boolean);
    const generatedAt = safeText(container.generatedAt || "").trim() || new Date().toISOString();
    return { items, generatedAt };
  };


  const normalizeDecisionsV2 = (items) => {
    const allowedTypes = new Set(["product", "research", "go_to_market", "ops"]);
    const allowedStatus = new Set(["proposed", "approved"]);
    const allowedRisk = new Set(["low", "medium", "high"]);

    return safeArray(items).
    map((d, idx) => {
      if (!d || typeof d !== "object") return null;
      const title = safeText(d.title || d.description || "").trim();
      if (!title) return null;

      const description = safeText(d.description || "").trim();

      const decisionType = allowedTypes.has(String(d.decisionType || "").trim()) ? String(d.decisionType).trim() : "product";
      const status = allowedStatus.has(String(d.status || "").trim()) ? String(d.status).trim() : "proposed";
      const riskLevel = allowedRisk.has(String(d.riskLevel || "").trim()) ? String(d.riskLevel).trim() : "medium";

      const riskDrivers = safeArray(d.riskDrivers).
      map((r) => safeText(r).trim()).
      filter((r) => r && r.toLowerCase() !== "limited support so far");

      const requiresValidation = safeArray(d.requiresValidation).map((r) => safeText(r).trim()).filter(Boolean);
      const linkedInsightIdsDirect = safeArray(
        d.linkedInsightIds || d.insightIds || d.relatedInsightIds || d.supportingInsightIds
      ).
      map((id) => safeText(id).trim()).
      filter(Boolean);
      const linkedInsightIdsFromEvidence = safeArray(
        d.evidence || d.supportingEvidence || d.sources || d.references
      ).
      map((item) => {
        if (typeof item === "string") return safeText(item).trim();
        if (!item || typeof item !== "object") return "";
        return safeText(
          item.insightId || item.linkedInsightId || item.id || item.sourceId || item.refId || ""
        ).trim();
      }).
      filter((id) => /^ins[_-]/i.test(id));
      const linkedInsightIds = Array.from(
        new Set([...linkedInsightIdsDirect, ...linkedInsightIdsFromEvidence])
      );
      const supportedBy = d.supportedBy && typeof d.supportedBy === "object" ? d.supportedBy : {};

      return {
        id: d.id || `dec_${hash32(title + "::" + idx)}`,
        title,
        description,
        decisionType,
        status,
        riskLevel,
        riskDrivers: riskDrivers.length ? riskDrivers : ["Evidence coverage is insufficient for this decision"],
        requiresValidation,
        linkedInsightIds,
        supportedBy: {
          insights: Number(supportedBy.insights || 0) || 0,
          excerpts: Number(supportedBy.excerpts || 0) || 0
        }
      };
    }).
    filter(Boolean);
  };

  const keySignals = normalizeKeySignals(o.keySignals || []);
  const openRisksSource =
  o.openRisks && typeof o.openRisks === "object" && Array.isArray(o.openRisks.items) ?
  o.openRisks.items :
  o.openRisks;
  const openRisks = normalizeOpenRisks(openRisksSource || []);
  const coverageGaps = normalizeCoverageGaps(o.coverageGaps || []);
  const researchQuestions = normalizeResearchQuestions(o.researchQuestions || []);
  const audienceGroupsRaw = safeArray(o.audienceGroups).
  map((group, idx) => {
    if (!group || typeof group !== "object") return null;
    const name = safeText(group.name || group.title || "").trim();
    const description = safeText(group.description || "").trim();
    if (!name) return null;
    return {
      id: safeText(group.id || "").trim() || `aud_${hash32(name + "::" + idx)}`,
      name,
      description,
      isPrimary: group.isPrimary === true
    };
  }).
  filter(Boolean);
  const audienceGroups = ensurePrimaryAudienceGroups(audienceGroupsRaw);

  const decisionsV2 = normalizeDecisionsV2(o.decisionsV2 || []);

  const normalizeSuccessCriteriaItems = (value) => {
    if (Array.isArray(value)) {
      return value.
      map((item) => safeText(item).trim()).
      map((item) => item.replace(/^[-*•]\s*/, "").trim()).
      filter(Boolean).
      slice(0, 4);
    }
    const text = safeText(value).trim();
    if (!text) return [];
    return text.
    split(/\r?\n+/).
    map((line) => line.replace(/^[-*•]\s*/, "").trim()).
    filter(Boolean).
    slice(0, 4);
  };

  const pd = o.problemDefinition && typeof o.problemDefinition === "object" ? o.problemDefinition : {};
  const successCriteriaItems = normalizeSuccessCriteriaItems(pd.successCriteriaItems || pd.successCriteria);
  const successCriteriaText =
  typeof pd.successCriteria === "string" ? safeText(pd.successCriteria).trim() : "";
  const problemDefinition = {
    problem: safeText(pd.problem || "").trim(),
    weWill: safeText(pd.weWill || "").trim(),
    successCriteria: successCriteriaText || successCriteriaItems.join("\n"),
    successCriteriaItems
  };

  const recommendedNextSteps = safeArray(o.recommendedNextSteps).
  map((step) => {
    if (!step) return null;
    if (typeof step === "string") {
      const title = safeText(step).trim();
      if (!title) return null;
      return { title, description: "", actionKey: "" };
    }
    if (typeof step !== "object") return null;
    const title = safeText(step.title || step.text || "").trim();
    const description = safeText(step.description || "").trim();
    const actionKey = safeText(step.actionKey || "").trim();
    if (!title && !description) return null;
    return { title, description, actionKey };
  }).
  filter(Boolean);

  const decisions = safeArray(o.decisions).
  map((d) => {
    if (!d || typeof d !== "object") return null;
    const title = safeText(d.title || "").trim();
    const description = safeText(d.description || "").trim();
    if (!title) return null;
    const linkedInsightIdsDirect = safeArray(
      d.linkedInsightIds || d.insightIds || d.relatedInsightIds || d.supportingInsightIds
    ).
    map((id) => safeText(id).trim()).
    filter(Boolean);
    const linkedInsightIdsFromEvidence = safeArray(
      d.evidence || d.supportingEvidence || d.sources || d.references
    ).
    map((item) => {
      if (typeof item === "string") return safeText(item).trim();
      if (!item || typeof item !== "object") return "";
      return safeText(
        item.insightId || item.linkedInsightId || item.id || item.sourceId || item.refId || ""
      ).trim();
    }).
    filter((id) => /^ins[_-]/i.test(id));
    const linkedInsightIds = Array.from(
      new Set([...linkedInsightIdsDirect, ...linkedInsightIdsFromEvidence])
    );
    return { title, description, linkedInsightIds };
  }).
  filter(Boolean);

  const deduped = dedupeAcrossOverviewSections({
    openRisks,
    decisionsV2,
    keySignals,
    coverageGaps,
    opportunityAreas
  });

  return {
    executiveSummary: summaryText,
    executiveSummarySection: {
      title: safeText(executiveSummaryRaw.title || "").trim() || "Executive Summary",
      text: summaryText
    },
    synthesis,
    opportunityAreas: deduped.opportunityAreas || opportunityAreas,
    keySignals: deduped.keySignals,
    openRisks: deduped.openRisks,
    coverageGaps: deduped.coverageGaps,
    audienceGroups,
    researchQuestions,
    problemDefinition,
    recommendedNextSteps,
    decisionsV2: deduped.decisionsV2 || decisionsV2,
    decisions: safeArray(deduped.decisionsV2 || decisionsV2).
    map((d) => ({
      title: safeText(d.title || "").trim(),
      description: safeText(d.description || "").trim()
    })).
    filter((d) => d.title)
  };
}

function buildNameSeed(formData) {
  return `${safeText(formData.projectName)}|${safeText(formData.productDescription)}|${safeText(
    formData.targetAudience
  )}|${safeText(formData.industry)}`;
}

/* ------------------------------------------------------------------ */
/* ResearchTagSpans: dedupe + clamp */
/* ------------------------------------------------------------------ */

function dedupeAndClampTagSpans(items, researchItemsById) {
  const byKey = new Map();
  for (const it of Array.isArray(items) ? items : []) {
    if (!it || typeof it !== "object") continue;
    const tag = normalizeTag(it.tag);
    if (!tag) continue;
    const rid = safeText(it.researchItemId).trim();
    if (!rid || !researchItemsById.has(rid)) continue;
    const start = Number(it.start);
    const end = Number(it.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
    const key = `${tag}::${rid}::${start}::${end}`;
    if (byKey.has(key)) continue;
    byKey.set(key, {
      ...it,
      tag,
      researchItemId: rid,
      text: clampExcerptLength(it.text, 25, 120)
    });
  }
  return Array.from(byKey.values());
}

function normalizeAndMergeFinalTagSpans(items, researchItemsById) {
  const deduped = dedupeAndClampTagSpans(items, researchItemsById);
  const grouped = new Map();
  deduped.forEach((it) => {
    const key = `${it.tag}::${it.researchItemId}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(it);
  });

  const t = now();
  const merged = [];

  for (const groupItems of grouped.values()) {
    groupItems.sort((a, b) => a.start - b.start || a.end - b.end);
    let current = null;
    for (const it of groupItems) {
      if (!current) {
        current = { ...it };
        continue;
      }
      if (it.start <= current.end + 1) {
        current.end = Math.max(current.end, it.end);
      } else {
        merged.push(current);
        current = { ...it };
      }
    }
    if (current) merged.push(current);
  }

  return merged.map((it) => {
    const fullText = researchItemsById.get(it.researchItemId) || "";
    const text = fullText ? makeWindowExcerpt(fullText, it.start, it.end, 120) : clampExcerptLength(it.text, 25, 120);
    return {
      ...it,
      id: `tag_${hash32(`${it.tag}::${it.researchItemId}::${it.start}::${it.end}`)}`,
      text,
      updatedAt: t
    };
  });
}

/* ------------------------------------------------------------------ */
/* Internal self-test (manual, opt-in) */
/* ------------------------------------------------------------------ */

function runInternalSelfTest() {
  if (!DEBUG_AI_TAGS) return;
  const shouldRun = typeof globalThis !== "undefined" && globalThis.__runGroqSelfTest === true;
  if (!shouldRun) return;

  const results = [];
  const record = (name, ok, meta = null) => results.push({ name, ok, meta });

  try {
    const doc = normalizeDocText(
      "We require parental confirmation before walks. GPS tracking is expected for safety."
    );
    const researchItemsById = new Map([["d1", doc]]);

    const anchored = anchorExcerptInDoc("GPS tracking", doc);
    record("anchorExcerptInDoc", !!anchored, anchored ? { start: anchored.start, end: anchored.end } : null);

    const spans = normalizeResearchTagSpansFromGroq(
      { items: [{ tag: "GPS tracking", researchItemId: "d1", excerpt: "GPS tracking" }] },
      researchItemsById,
      null
    );
    record("normalizeResearchTagSpansFromGroq", (spans?.items || []).length === 1, {
      count: spans?.items?.length || 0
    });

    const canon = canonicalizeTagsBundle({
      vocabItemsFromGroq: [{ tag: "gps tracking", description: "" }],
      vocabDescMap: new Map([["gps tracking", ""]]),
      finalVocabSet: new Set(["gps tracking", "tracking gps"]),
      finalSpanItems: [
      {
        id: "tag_1",
        tag: "gps tracking",
        researchItemId: "d1",
        start: anchored?.start || 0,
        end: anchored?.end || 3,
        text: "GPS"
      }],

      finalInsights: { items: [{ tags: ["tracking gps"] }] },
      finalTagThemes: { themes: [{ id: "t1", title: "Safety", order: 0, tags: ["tracking gps"] }] },
      researchItemsById
    });
    record("canonicalizeTagsBundle", canon?.canonicalSet?.has("gps tracking"), {
      size: canon?.canonicalSet?.size || 0
    });
  } catch (err) {
    record("self_test_exception", false, { error: err?.message || "unknown" });
  }

  const failed = results.filter((r) => !r.ok);
  if (failed.length) {
    void 0;
  } else {
    void 0;
  }
}

/* ------------------------------------------------------------------ */
/* Groq caller */
/* ------------------------------------------------------------------ */

const GROQ_CALL_PREFIX = "[GROQ_CALL]";
let groqCallCounter = 0;

function nextGroqCallId() {
  if (typeof globalThis !== "undefined") {
    globalThis.__groqCallCounter = (globalThis.__groqCallCounter || 0) + 1;
    return globalThis.__groqCallCounter;
  }
  groqCallCounter += 1;
  return groqCallCounter;
}

export async function callGroq({
  apiKey,
  userPrompt,
  maxCompletionTokens,
  requireComplete,
  logLabel
}) {
  const GROQ_MODEL = "openai/gpt-oss-120b";
  const logUsage = (label, response, data, meta = {}) => {
    const usage = data?.usage || {};
    const headers = response?.headers;
    const requestId = headers?.get("x-request-id") || headers?.get("x-groq-request-id") || "";
    const remainingReq = headers?.get("x-ratelimit-remaining-requests");
    const remainingTokens = headers?.get("x-ratelimit-remaining-tokens");
    const resetReq = headers?.get("x-ratelimit-reset-requests");
    const resetTokens = headers?.get("x-ratelimit-reset-tokens");

    void 0;
















  };

  function analyzeBraceBalance(text) {
    let balance = 0;
    let inString = false;
    let escape = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inString) {
        if (escape) {
          escape = false;
        } else if (ch === "\\") {
          escape = true;
        } else if (ch === '"') {
          inString = false;
        }
        continue;
      }
      if (ch === '"') {
        inString = true;
        continue;
      }
      if (ch === "{") balance += 1;
      if (ch === "}") balance -= 1;
    }
    return balance;
  }

  async function doCall({ temperature, attempt, maxTokensOverride = null, extraReminder = "" }) {
    const callId = nextGroqCallId();
    const endpoint = "https://api.groq.com/openai/v1/chat/completions";
    const seed = GROQ_DETERMINISTIC ? hash32(userPrompt || "") : undefined;
    const safeMaxTokens = maxCompletionTokens || 3000;
    const systemMessage = "Return ONLY valid JSON (a single JSON object). No markdown, no code fences, no extra text.";
    const userMessage =
      userPrompt + "\n\nREMINDER: Output must be valid JSON." + (extraReminder ? `\n${extraReminder}` : "");
    const groqCombinedInput = `SYSTEM:\n${systemMessage}\n\nUSER:\n${userMessage}`;
    const requestedMaxCompletionTokens = Number.isFinite(maxTokensOverride)
      ? maxTokensOverride
      : safeMaxTokens;
    const safeMaxCompletionTokens = requestedMaxCompletionTokens;
    const requestBody = {
      model: GROQ_MODEL,
      messages: [
      {
        role: "system",
        content: systemMessage
      },
      {
        role: "user",
        content: userMessage
      }],

      ...(Number.isFinite(safeMaxCompletionTokens) ? { max_completion_tokens: safeMaxCompletionTokens } : {}),
      temperature,
      top_p: 1,
      presence_penalty: 0,
      frequency_penalty: 0,
      ...(Number.isFinite(seed) ? { seed } : {})
    };
    const requestJson = JSON.stringify(requestBody);
    const requestMeta = {
      logLabel: logLabel || null,
      requestJsonChars: requestJson.length,
      combinedInputChars: groqCombinedInput.length,
      maxCompletionTokens: safeMaxCompletionTokens,
      approxPromptTokens: Math.ceil(groqCombinedInput.length / 4),
      approxRequestTokens: Math.ceil(requestJson.length / 4),
    };

    emitGroqToast("Calling Groq…");
    if (DEBUG_AI_TAGS) {
      console.info("[Groq][REQUEST]", requestMeta);
    }

    const response = await fetch("https://api.groq.com/openai/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`
      },
      body: requestJson
    });

    response.__groqCallMeta = { callId, attempt, temperature, endpoint, ...requestMeta };
    return response;
  }

  const baseTemp = GROQ_DETERMINISTIC ? GROQ_DEFAULT_TEMPERATURE : 0.1;
  let response = await doCall({ temperature: baseTemp, attempt: "initial" });
  let data = null;

  if (!response.ok) {
    const rawText = await response.text();
    let parsedErr = null;
    try {
      parsedErr = JSON.parse(rawText);
    } catch {}
    const { parsedCode, parsedMessage } = extractGroqErrorFields(parsedErr, rawText);
    const requestMeta = response.__groqCallMeta || {};

    console.error("[Groq][ERROR]", {
      status: response.status,
      logLabel: requestMeta.logLabel || null,
      parsedErrorCode: parsedCode || null,
      parsedErrorMessage: parsedMessage || null,
      rawResponseText: rawText,
      requestJsonChars: Number.isFinite(requestMeta.requestJsonChars) ? requestMeta.requestJsonChars : null,
      combinedInputChars: Number.isFinite(requestMeta.combinedInputChars) ? requestMeta.combinedInputChars : null,
      maxCompletionTokens: Number.isFinite(requestMeta.maxCompletionTokens) ? requestMeta.maxCompletionTokens : null,
    });

    if ([429, 502, 503, 504, 529].includes(response.status)) {
      throw createTemporaryUnavailableError({ status: response.status, message: rawText });
    }

    if (isGroqRequestBudgetError(response.status, parsedErr, rawText)) {
      throw createGroqRequestFailureError({
        status: response.status,
        parsedErr,
        rawText,
        requestMeta,
        code: "groq_request_too_large",
        title: "Request too large",
        body: "Please reduce your message size and try again.",
        retryable: false,
      });
    }

    if (response.status === 400) {
      throw createGroqRequestFailureError({
        status: response.status,
        parsedErr,
        rawText,
        requestMeta,
        code: "groq_bad_request",
        title: "Groq rejected the request",
        body: parsedMessage || "The request was invalid. Please try again.",
        retryable: false,
      });
    }

    throw createGroqRequestFailureError({
      status: response.status,
      parsedErr,
      rawText,
      requestMeta,
      code: "groq_request_failed",
      title: "Groq request failed",
      body: parsedMessage || "There was an issue generating with AI. Please try again.",
      retryable: false,
    });
  }

  data = data || (await response.json());

  logUsage("response", response, data, {
    callId: response.__groqCallMeta?.callId,
    attempt: response.__groqCallMeta?.attempt,
    model: GROQ_MODEL,
    temperature: response.__groqCallMeta?.temperature ?? baseTemp,
    promptChars: userPrompt?.length || 0,
    endpoint: response.__groqCallMeta?.endpoint,
    requestedMaxCompletionTokens: Number.isFinite(response.__groqCallMeta?.maxCompletionTokens)
      ? response.__groqCallMeta.maxCompletionTokens
      : null
  });

  const makeParseError = ({ code, title, body, rawText, attemptLabel }) => {
    const err = new Error(body || title || "Groq returned invalid JSON.");
    err.code = code;
    err.title = title;
    err.body = body || title;
    err.retryable = false;
    err.details = [
      attemptLabel ? `Attempt: ${attemptLabel}` : null,
      logLabel ? `Log label: ${logLabel}` : null,
      rawText ? `Raw text: ${rawText}` : null
    ].filter(Boolean);
    return err;
  };

  const parseModelText = (rawText, attemptLabel) => {
    const normalized = normalizeModelText(rawText);
    if (!normalized) {
      throw makeParseError({
        code: "empty_generation",
        title: "Empty generation",
        body: "Groq returned no content.",
        rawText,
        attemptLabel
      });
    }

    const braceBalance = analyzeBraceBalance(normalized);
    const looksTruncated = braceBalance !== 0 || !/[}\]]\s*$/.test(normalized);

    const tryParse = (candidate) => {
      const parsed = JSON.parse(candidate);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("Parsed value is not an object.");
      }
      return parsed;
    };

    try {
      const parsed = tryParse(normalized);
      if (DEBUG_AI_TAGS) {
        console.info("[Groq][PARSE OK]", {
          logLabel: logLabel || null,
          attempt: attemptLabel,
          rawChars: normalized.length
        });
      }
      return parsed;
    } catch {}

    const firstBrace = normalized.indexOf("{");
    const lastBrace = normalized.lastIndexOf("}");
    if (firstBrace >= 0 && lastBrace > firstBrace) {
      const sliced = normalized.slice(firstBrace, lastBrace + 1).trim();
      try {
        const parsed = tryParse(sliced);
        if (DEBUG_AI_TAGS) {
          console.info("[Groq][PARSE OK]", {
            logLabel: logLabel || null,
            attempt: attemptLabel,
            rawChars: sliced.length
          });
        }
        return parsed;
      } catch {}
    }

    if (looksTruncated) {
      throw makeParseError({
        code: "truncated_generation",
        title: "Truncated generation",
        body: "Groq output looks cut off. Please try again.",
        rawText,
        attemptLabel
      });
    }

    throw makeParseError({
      code: "json_parse_failed",
      title: "Invalid JSON",
      body: "Groq returned invalid JSON. Please try again.",
      rawText,
      attemptLabel
    });
  };

  const choice = data?.choices?.[0];
  const raw = choice?.message?.content ?? "";
  const finishReason = choice?.finish_reason;
  const rawText = normalizeModelText(raw);

  if (DEBUG_AI_TAGS) {
    console.info("[Groq][RAW]", {
      logLabel: logLabel || null,
      preview: rawText.slice(0, 240),
      rawChars: rawText.length,
      finishReason: finishReason || null
    });
  }

  if (typeof globalThis !== "undefined") {
    globalThis.__lastGroqRaw = raw;
    const entry = {
      ts: Date.now(),
      callId: response.__groqCallMeta?.callId || null,
      attempt: response.__groqCallMeta?.attempt || "initial",
      model: GROQ_MODEL,
      promptChars: userPrompt?.length || 0,
      endpoint: response.__groqCallMeta?.endpoint || null,
      raw
    };
    const existing = Array.isArray(globalThis.__groqRawResponses) ? globalThis.__groqRawResponses : [];
    globalThis.__groqRawResponses = [...existing, entry].slice(-10);
  }

  if (requireComplete && finishReason === "length") {
    const truncatedError = makeParseError({
      code: "truncated_generation",
      title: "Truncated generation",
      body: "Groq output looks cut off. Please try again.",
      rawText,
      attemptLabel: "initial"
    });
    if (DEBUG_AI_TAGS) {
      console.info("[Groq][TRUNCATED]", {
        logLabel: logLabel || null,
        rawChars: rawText.length,
        tail: rawText.slice(-300),
        maxCompletionTokens: response.__groqCallMeta?.maxCompletionTokens ?? null
      });
    }
    throw truncatedError;
  }

  try {
    return parseModelText(rawText, "initial");
  } catch (err) {
    const retryableCodes = new Set(["empty_generation", "truncated_generation", "json_parse_failed"]);
    if (!retryableCodes.has(err?.code)) throw err;

    const retryMaxCompletionTokens = Math.max(
      256,
      Math.floor((response.__groqCallMeta?.maxCompletionTokens || maxCompletionTokens || 0) * 0.6)
    );

    if (DEBUG_AI_TAGS) {
      console.info("[Groq][RETRY]", {
        logLabel: logLabel || null,
        reason: err.code,
        nextMaxCompletionTokens: retryMaxCompletionTokens
      });
    }

    response = await doCall({
      temperature: baseTemp,
      attempt: "retry",
      maxTokensOverride: retryMaxCompletionTokens
    });
    if (!response.ok) {
      const text = await response.text();
      throw createGroqRequestFailureError({
        status: response.status,
        parsedErr: null,
        rawText: text,
        requestMeta: response.__groqCallMeta || {},
        code: "groq_retry_failed",
        title: "Groq request failed",
        body: "There was an issue generating with AI. Please try again.",
        retryable: false
      });
    }
    data = await response.json();
    logUsage("response", response, data, {
      callId: response.__groqCallMeta?.callId,
      attempt: response.__groqCallMeta?.attempt,
      model: GROQ_MODEL,
      temperature: response.__groqCallMeta?.temperature ?? baseTemp,
      promptChars: userPrompt?.length || 0,
      endpoint: response.__groqCallMeta?.endpoint,
      requestedMaxCompletionTokens: Number.isFinite(response.__groqCallMeta?.maxCompletionTokens)
        ? response.__groqCallMeta.maxCompletionTokens
        : null
    });
    const retryChoice = data?.choices?.[0];
    const retryRaw = retryChoice?.message?.content ?? "";
    const retryFinishReason = retryChoice?.finish_reason;
    const retryRawText = normalizeModelText(retryRaw);

    if (DEBUG_AI_TAGS) {
      console.info("[Groq][RAW]", {
        logLabel: logLabel || null,
        attempt: "retry",
        preview: retryRawText.slice(0, 240),
        rawChars: retryRawText.length,
        finishReason: retryFinishReason || null
      });
    }

    if (typeof globalThis !== "undefined") {
      globalThis.__lastGroqRaw = retryRaw;
      const entry = {
        ts: Date.now(),
        callId: response.__groqCallMeta?.callId || null,
        attempt: response.__groqCallMeta?.attempt || "retry",
        model: GROQ_MODEL,
        promptChars: userPrompt?.length || 0,
        endpoint: response.__groqCallMeta?.endpoint || null,
        raw: retryRaw
      };
      const existing = Array.isArray(globalThis.__groqRawResponses) ? globalThis.__groqRawResponses : [];
      globalThis.__groqRawResponses = [...existing, entry].slice(-10);
    }

    if (requireComplete && retryFinishReason === "length") {
      const retryTail = retryRawText.slice(-300);
      if (DEBUG_AI_TAGS) {
        console.info("[Groq][TRUNCATED]", {
          logLabel: logLabel || null,
          attempt: "retry",
          rawChars: retryRawText.length,
          tail: retryTail,
          maxCompletionTokens: response.__groqCallMeta?.maxCompletionTokens ?? null
        });
      }
      throw makeParseError({
        code: "truncated_generation",
        title: "Truncated generation",
        body: "Groq output looks cut off. Please try again.",
        rawText: retryRawText,
        attemptLabel: "retry"
      });
    }

    return parseModelText(retryRawText, "retry");
  }
}

/* ------------------------------------------------------------------ */
/* Main */
/* ------------------------------------------------------------------ */

export async function generateFoundationWithGroq(formData) {
  assertValidGroqKey();
  const consumeUsagePromise = consumeAiUsage({
    operation: "generate_insights",
    projectId: safeText(formData?.projectId || "").trim() || null,
    isRegeneration: false,
  });

  const tNow = now();
  const researchItems = Array.isArray(formData?.researchItems) ? formData.researchItems : [];

  const hasResearch =
  Array.isArray(formData?.researchItems) && formData.researchItems.length > 0 ||
  (formData.researchInterviewNotes || "").trim().length > 0 ||
  (formData.researchCustomerMessages || "").trim().length > 0 ||
  (formData.researchSupportTickets || "").trim().length > 0 ||
  (formData.researchTranscripts || "").trim().length > 0;

  void 0;








  const virtualDocs = researchItems.length > 0 ? buildVirtualDocsFromResearchItems(researchItems) : buildVirtualDocsFromWizardFields(formData);
  const corpusText = buildCorpusText(virtualDocs);
  const spanTargets = computeSpanTargets(corpusText.length);
  const promptFacts = extractPromptFacts(formData);
  const nameSeed = buildNameSeed(formData);
  void nameSeed;

  const researchItemsById = new Map();
  for (const d of virtualDocs) researchItemsById.set(d.id, d.text);

  runInternalSelfTest();

  let parsedPhase1 = null;
  let parsedPhase2 = null;

  const phase1Prompt = buildGroqExtractorPrompt({
    corpusText
  });

  if (DEBUG_AI_TAGS) {
    console.info("[Groq][PHASE1] generation starts");
    console.info("[Groq][PHASE1] raw prompt", phase1Prompt);
    console.info("[Groq][PHASE1] prompt size", {
      promptChars: phase1Prompt.length,
      corpusChars: corpusText.length,
    });
  }

  await consumeUsagePromise;
  parsedPhase1 = await callGroq({
    apiKey,
    userPrompt: phase1Prompt,
    maxCompletionTokens: 15000,
    requireComplete: true,
    logLabel: "PHASE1"
  });
  if (DEBUG_AI_TAGS) {
    const phase1TagCount = Array.isArray(parsedPhase1?.tagVocabulary?.items) ?
    parsedPhase1.tagVocabulary.items.length :
    0;
    const phase1SpanCount = Array.isArray(parsedPhase1?.researchTagSpans?.items) ?
    parsedPhase1.researchTagSpans.items.length :
    0;
    console.info("[Groq][PHASE1] generation completed", {
      tagCount: phase1TagCount,
      spanCount: phase1SpanCount,
    });
  }

  const phase1TagCount = Array.isArray(parsedPhase1?.tagVocabulary?.items) ?
  parsedPhase1.tagVocabulary.items.length :
  0;
  const phase1SpanCount = Array.isArray(parsedPhase1?.researchTagSpans?.items) ?
  parsedPhase1.researchTagSpans.items.length :
  0;
  if (phase1TagCount > 0 && phase1SpanCount === 0) {
    throw new Error("Phase 1 incomplete: no spans returned.");
  }
  if (phase1TagCount > 0 && phase1SpanCount < Math.max(1, Math.floor(phase1TagCount * 0.5))) {
    void 0;



  }

  // Phase 2: synthesizer (themes + insights + groups).
  const compactPhase2Payload = buildCompactAIPayload({
    projectContext: {
      id: safeText(formData?.projectId || "").trim(),
      name: safeText(formData?.projectName || "").trim(),
      productDescription: safeText(formData?.productDescription || "").trim(),
      industry: safeText(formData?.industry || "").trim(),
      targetAudience: safeText(formData?.targetAudience || "").trim()
    },
    foundationBundle: {
      tagVocabulary: parsedPhase1?.tagVocabulary,
      researchTagSpans: parsedPhase1?.researchTagSpans
    },
    researchItemsMeta: virtualDocs.map((doc) => ({ id: doc.id, label: doc.label }))
  });
  let rawPhase2Size = 0;
  let compactPhase2Size = 0;
  if (DEBUG_AI_TAGS) {
    const rawPhase2Payload = {
      tagVocabulary: parsedPhase1?.tagVocabulary || {},
      researchTagSpans: parsedPhase1?.researchTagSpans || {},
      researchItems: virtualDocs.map((doc) => ({ id: doc.id, label: doc.label }))
    };
    rawPhase2Size = estimateJsonPayloadSize(rawPhase2Payload);
    compactPhase2Size = estimateJsonPayloadSize({
      tagVocabulary: compactPhase2Payload?.foundation?.tagVocabulary || {},
      researchTagSpans: compactPhase2Payload?.foundation?.researchTagSpans || {},
      researchItems: compactPhase2Payload?.researchItems || []
    });
  }
  void 0;








  const phase2Prompt = buildGroqSynthesizerPrompt({
    formData,
    promptFacts,
    researchItemIds: virtualDocs.map((doc) => doc.id),
    tagVocabulary: compactPhase2Payload?.foundation?.tagVocabulary,
    researchTagSpans: compactPhase2Payload?.foundation?.researchTagSpans
  });
  if (DEBUG_AI_TAGS) {
    console.info("[Groq][PHASE2] generation starts");
    console.info("[Groq][PHASE2] raw prompt", phase2Prompt);
    console.info("[Groq][PHASE2] prompt stats", {
      promptChars: phase2Prompt.length,
      corpusChars: corpusText.length,
      promptFactsCount: promptFacts.length,
      researchItemCount: virtualDocs.length,
      rawPhase2Size,
      compactPhase2Size,
    });
  }

  if (DEBUG_AI_TAGS) {
    const promptChars2 = phase2Prompt.length;
    const promptTokensEstimate2 = Math.ceil(promptChars2 / 4);
    void promptChars2;
    void promptTokensEstimate2;
  }







  parsedPhase2 = await callGroq({
    apiKey,
    userPrompt: phase2Prompt,
    maxCompletionTokens: 15000,
    requireComplete: true,
    logLabel: "PHASE2"
  });
  if (DEBUG_AI_TAGS) {
    const spanCount = Array.isArray(compactPhase2Payload?.foundation?.researchTagSpans?.items) ?
    compactPhase2Payload.foundation.researchTagSpans.items.length :
    0;
    const insightsCount = Array.isArray(parsedPhase2?.overview?.researchInsights) ?
    parsedPhase2.overview.researchInsights.length :
    0;
    console.info("[Groq][PHASE2] generation completed", {
      spanCount,
      insightsCount,
    });
  }
  const parsed = {
    tagVocabulary: parsedPhase1?.tagVocabulary,
    researchTagSpans: parsedPhase1?.researchTagSpans,
    tagThemes: parsedPhase2?.tagThemes || { themes: [] },
    overview: parsedPhase2?.overview || { researchInsights: [] },
    problemDefinition: toSingleSentencePreview(
      parsedPhase2?.problemDefinition || parsedPhase2?.overview?.problemDefinition?.problem || "",
      25
    ),
    weWill: toSingleSentencePreview(
      parsedPhase2?.weWill || parsedPhase2?.overview?.problemDefinition?.weWill || "",
      20
    ),
    insightGroups: parsedPhase2?.insightGroups || { groups: [] },
    emergingSignals: parsedPhase2?.emergingSignals || { items: [], generatedAt: new Date().toISOString() }
  };

  validateGroqFoundationResponse(parsed);

  const parsedFinal = validateEvidenceIdsUniqueAndConsistent(parsed);

  const debugRawTags = extractRawTagsForDebug(parsedFinal?.tagVocabulary);
  if (DEBUG_AI_TAGS) {
    void 0;
    void 0;
    void 0;
    void 0;
    void 0;
    void 0;
    void 0;
    void 0;



  }

  // 1) Groq vocab
  const vocabItemsFromGroq = normalizeTagVocabularyFromGroq(parsedFinal?.tagVocabulary);
  const vocabDescMap = new Map(vocabItemsFromGroq.map((x) => [x.tag, x.description || ""]));
  let vocabSet = new Set(vocabItemsFromGroq.map((x) => x.tag));

  // 2) Normalize insights (do NOT force vocab gating here)
  const insightQualityMetrics = createInsightQualityMetrics();
  const phase1SpanItems = Array.isArray(parsedPhase1?.researchTagSpans?.items) ?
  parsedPhase1.researchTagSpans.items :
  [];
  const canonicalInsightsBase = normalizeInsightItemsFromGroq(parsedFinal?.overview?.researchInsights, {
    corpusText: normalizeDocText(corpusText),
    researchItemsById,
    rawSpanItems: phase1SpanItems,
    metrics: insightQualityMetrics
  });

  // 3) Normalize groups
  const normalizedGroups = normalizeInsightGroupsFromGroq(
    parsedFinal?.insightGroups,
    Array.isArray(parsedFinal?.overview?.researchInsights) ? parsedFinal.overview.researchInsights.length : 0
  );

  let canonicalInsights = normalizedGroups ?
  applyGroupsToCanonicalInsights(canonicalInsightsBase, normalizedGroups) :
  canonicalInsightsBase;

  // Prompt insights must be generated by the model (do not auto-inject prompt facts here).

  // 4) Normalize spans (do NOT drop tags not in vocab yet)
  let tagSpans = { version: 1, items: [], createdAt: tNow, updatedAt: tNow };
  if (virtualDocs.length) {
    tagSpans = normalizeResearchTagSpansFromGroq(parsedFinal?.researchTagSpans, researchItemsById, vocabDescMap);
  }

  if (!DEBUG_DISABLE_POST_PHASE_EXPANSION) {
    // Add spans anchored from insight evidence to prevent missing tags/highlight drift.
    if (researchItemsById.size > 0 && canonicalInsights?.items?.length) {
      const evidenceSpans = buildTagSpansFromInsightEvidence(canonicalInsights, researchItemsById);
      if (evidenceSpans.length) tagSpans.items.push(...evidenceSpans);
    }

    // Add spans from canonical tag description anchors (covers multiple mentions).
    if (researchItemsById.size > 0 && vocabDescMap.size) {
      const anchorDescSpans = buildAnchorSpansFromDescriptions(vocabDescMap, researchItemsById, 4);
      if (anchorDescSpans.length) tagSpans.items.push(...anchorDescSpans);
    }

    if (researchItemsById.size > 0 && vocabDescMap.size) {
      const enforced = enforceAnchorCoverage({
        vocabDescMap,
        spanItems: tagSpans.items,
        researchItemsById,
        maxSpansPerAnchor: 3
      });
      tagSpans.items = enforced.items;
      if (DEBUG_AI_TAGS) {
        void 0;
      }
    }
  }

  // 5) Normalize tag themes (allow themes to add tags)
  const normalizedTagThemes = normalizeTagThemesFromGroq(parsed?.tagThemes, vocabSet);

  // 6) Expand vocab from insights + spans so nothing "disappears" due to mismatch
  if (EXPAND_VOCAB_FROM_INSIGHTS_AND_SPANS && !DEBUG_DISABLE_POST_PHASE_EXPANSION) {
    for (const it of canonicalInsights?.items || []) {
      for (const tg of it.tags || []) {
        const tag = normalizeTag(tg);
        if (!tag) continue;
        vocabSet.add(tag);
        if (!vocabDescMap.get(tag)) setDescAnchorsStrict(vocabDescMap, tag, researchItemsById, 3);
      }
    }
    for (const sp of tagSpans?.items || []) {
      const tag = normalizeTag(sp?.tag);
      if (!tag) continue;
      vocabSet.add(tag);
      if (!vocabDescMap.get(tag)) setDescAnchorsStrict(vocabDescMap, tag, researchItemsById, 3);
    }
  }

  // 7) If spans under-produced, fallback spans from vocabulary
  if (
  !DEBUG_DISABLE_POST_PHASE_EXPANSION &&
  tagSpans.items.length < Math.min(Math.max(MIN_SPANS_FLOOR, spanTargets.min), 200) &&
  researchItemsById.size > 0)
  {
    const tagsWithEvidenceSpans = new Set(
      (tagSpans.items || []).map((s) => normalizeTag(s?.tag)).filter(Boolean)
    );
    const extra = buildFallbackTagSpansFromVocabulary(
      researchItemsById,
      vocabSet,
      Math.max(MIN_SPANS_FLOOR, spanTargets.min),
      tagsWithEvidenceSpans,
      vocabDescMap
    );
    tagSpans.items.push(...extra);
  }

  // 8) Clamp/dedupe spans (still no vocab gating)
  if (DEBUG_AI_TAGS) {
    const rawSpans = Array.isArray(tagSpans?.items) ? tagSpans.items : [];
    const sample = rawSpans[0] || null;
    void 0;











  }
  let finalSpanItems = dedupeAndClampTagSpans(tagSpans.items, researchItemsById);

  // Preserve raw span excerpt/fact for UI rendering when available.
  const rawSpanById = new Map();
  const rawSpanItems = Array.isArray(parsedFinal?.researchTagSpans?.items) ? parsedFinal.researchTagSpans.items : [];
  rawSpanItems.forEach((s, idx) => {
    rawSpanById.set(`span_${idx}`, s);
  });
  finalSpanItems = finalSpanItems.map((s) => {
    const raw = s?.sourceSpanId ? rawSpanById.get(String(s.sourceSpanId)) : null;
    const excerpt = safeText(s?.excerpt || raw?.excerpt).trim();
    const fact = safeText(s?.fact || raw?.fact).trim();
    const text = safeText(s?.text || excerpt || fact).trim();
    return {
      ...s,
      text,
      excerpt: excerpt || null,
      fact: fact || null
    };
  });

  // 9) Enforce vocab on insights AFTER expansion (so you keep “missing” tags)
  const finalVocab = Array.from(vocabSet).
  map((t) => normalizeTag(t)).
  filter((t) => t);

  let finalVocabSet = new Set(finalVocab);

  let finalInsights = {
    ...(canonicalInsights || {}),
    items: (canonicalInsights?.items || []).map((it) => ({
      ...it,
      tags: (it.tags || []).map(normalizeTag).filter((t) => finalVocabSet.has(t))
    }))
  };

  let repairResult = null;
  if (!DEBUG_DISABLE_POST_PHASE_EXPANSION) {
    repairResult = repairMissingTagSpans({
      spanItems: finalSpanItems,
      finalVocabSet,
      insights: finalInsights,
      vocabDescMap,
      researchItemsById
    });

    if (repairResult?.items) {
      finalSpanItems = dedupeAndClampTagSpans(repairResult.items, researchItemsById);
    }
  }

  finalSpanItems = normalizeAndMergeFinalTagSpans(finalSpanItems, researchItemsById);

  const spanTagsSet = new Set(finalSpanItems.map((s) => s.tag));
  finalVocabSet = new Set([...finalVocabSet, ...spanTagsSet]);
  const hasSpans = finalSpanItems.length > 0;
  const displayVocabSet = hasSpans ?
  new Set(Array.from(finalVocabSet).filter((t) => spanTagsSet.has(t))) :
  new Set(finalVocabSet);

  finalInsights = {
    ...(finalInsights || {}),
    items: (finalInsights?.items || []).map((it) => ({
      ...it,
      tags: (it.tags || []).map(normalizeTag).filter((t) => finalVocabSet.has(t))
    }))
  };

  let finalTagThemes =
  normalizedTagThemes && typeof normalizedTagThemes === "object" ?
  {
    ...normalizedTagThemes,
    themes: (normalizedTagThemes.themes || []).map((theme) => {
      const normalizedTags = (theme.tags || []).map(normalizeTag);
      const filteredTags = normalizedTags.filter((t) => displayVocabSet.has(t));
      return {
        ...theme,
        tags: filteredTags.length ? filteredTags : normalizedTags
      };
    }),
    updatedAt: now()
  } :
  null;

  if (finalTagThemes?.themes) {
    finalTagThemes = {
      ...finalTagThemes,
      themes: finalTagThemes.themes.
      map((th) => ({ ...th, tags: Array.isArray(th.tags) ? th.tags : [] })).
      filter((th) => th.tags.length > 0),
      updatedAt: now()
    };
  }

  // Canonicalize duplicate/near-duplicate tags across vocab, insights, themes, spans.
  if (!DEBUG_DISABLE_POST_PHASE_EXPANSION) {
    const canon = canonicalizeTagsBundle({
      vocabItemsFromGroq,
      vocabDescMap,
      finalVocabSet,
      finalSpanItems,
      finalInsights,
      finalTagThemes,
      researchItemsById
    });
    if (canon?.canonicalSet) {
      finalSpanItems = canon.updatedSpanItems || finalSpanItems;
      finalInsights = canon.updatedInsights || finalInsights;
      finalTagThemes = canon.updatedThemes || finalTagThemes;
      vocabDescMap.clear();
      for (const [k, v] of canon.updatedVocabDescMap.entries()) vocabDescMap.set(k, v);
      finalVocabSet = new Set(canon.canonicalSet);
    }
  }

  // Re-run span repair after canonicalization to keep anchor↔span↔theme alignment.
  if (!DEBUG_DISABLE_POST_PHASE_EXPANSION) {
    const postCanonRepair = repairMissingTagSpans({
      spanItems: finalSpanItems,
      finalVocabSet,
      insights: finalInsights,
      vocabDescMap,
      researchItemsById
    });
    if (postCanonRepair?.items) {
      finalSpanItems = dedupeAndClampTagSpans(postCanonRepair.items, researchItemsById);
    }
  }

  // Recompute tag sets after canonicalization and use only final sets for filtering.
  const spanTagsSetFinal = new Set(finalSpanItems.map((s) => s.tag));
  const displayVocabSetFinal = new Set(Array.from(finalVocabSet).filter((t) => spanTagsSetFinal.has(t)));

  finalInsights = {
    ...(finalInsights || {}),
    items: (finalInsights?.items || []).map((it) => ({
      ...it,
      tags: (it.tags || []).map(normalizeTag).filter((t) => displayVocabSetFinal.has(t))
    }))
  };

  finalInsights = improveInsightsQuality({
    insights: finalInsights,
    spanItems: finalSpanItems,
    metrics: insightQualityMetrics
  });

  // Re-apply group mapping after any insight filtering/deduping.
  {
    const groupSource =
    finalInsights?.insightGroups && Array.isArray(finalInsights.insightGroups.groups) && finalInsights.insightGroups.groups.length ?
    finalInsights.insightGroups :
    normalizedGroups;
    if (groupSource && Array.isArray(groupSource.groups) && groupSource.groups.length) {
      finalInsights = applyGroupsToCanonicalInsights(finalInsights, groupSource);
    }
  }

  if (finalTagThemes?.themes) {
    const preThemeSnapshot = finalTagThemes.themes.map((t) => ({
      id: t.id,
      title: t.title,
      tags: safeArray(t.tags)
    }));
    finalTagThemes = {
      ...finalTagThemes,
      themes: (finalTagThemes.themes || []).map((theme) => ({
        ...theme,
        tags: (theme.tags || []).map(normalizeTag).filter((t) => displayVocabSetFinal.has(t))
      })),
      updatedAt: now()
    };
    const afterTagFilter = finalTagThemes.themes.map((t) => ({
      id: t.id,
      title: t.title,
      tags: safeArray(t.tags)
    }));
    finalTagThemes = {
      ...finalTagThemes,
      themes: finalTagThemes.themes.filter((th) => Array.isArray(th.tags) && th.tags.length > 0),
      updatedAt: now()
    };
    if (DEBUG_AI_TAGS) {
      const dropped = preThemeSnapshot.filter((pre) => {
        const after = afterTagFilter.find((a) => a.id === pre.id);
        return !after || !Array.isArray(after.tags) || after.tags.length === 0;
      });
      if (dropped.length) {
        void 0;
      }
    }
  }

  if (DEBUG_AI_TAGS) {
    void 0;
  }

  // Invariants (warnings only)
  {
    const invalidSpans = finalSpanItems.filter(
      (s) => !s || !s.researchItemId || !researchItemsById.has(s.researchItemId)
    );
    warnInvariant(invalidSpans.length === 0, "span has invalid researchItemId", {
      count: invalidSpans.length
    });

    const themeTags = (finalTagThemes?.themes || []).flatMap((t) => t.tags || []);
    const themeTagMismatches = themeTags.filter((t) => !displayVocabSetFinal.has(t));
    warnInvariant(themeTagMismatches.length === 0, "theme tag not in final display vocab set", {
      count: themeTagMismatches.length
    });

    const insightTags = (finalInsights?.items || []).flatMap((it) => it.tags || []);
    const insightTagMismatches = insightTags.filter((t) => !displayVocabSetFinal.has(t));
    warnInvariant(insightTagMismatches.length === 0, "insight tag not in final display vocab set", {
      count: insightTagMismatches.length
    });
  }

  // ✅ Debug AFTER finalTagThemes exists
  if (DEBUG_AI_TAGS) {
    const rawThemeTags = (parsed?.tagThemes?.themes || []).
    flatMap((t) => t.tags || []).
    map(normalizeTag);

    const finalThemeTags = (finalTagThemes?.themes || []).flatMap((t) => t.tags || []);

    const missingFromFinalThemes = Array.from(new Set(rawThemeTags)).filter(
      (t) => !finalThemeTags.includes(t)
    );

    void 0;
    void 0;
  }

  if (DEBUG_AI_TAGS) {
    const themeTags = (finalTagThemes?.themes || []).flatMap((t) => t.tags || []);
    const spansTags = new Set(finalSpanItems.map((s) => s.tag));
    const missingSpans = themeTags.filter((t) => !spansTags.has(t));

    void 0;
    void 0;
    void 0;

    // Also check vocab tags with no spans:
    const vocabNoSpans = Array.from(finalVocabSet).filter((t) => !spansTags.has(t));
    void 0;
  }

  // Ensure every tag is in exactly one theme.
  // If Groq forgets tags, put them into an auto theme (keeps UI consistent).
  if (finalTagThemes && Array.isArray(finalTagThemes.themes)) {
    const assigned = new Set();
    for (const th of finalTagThemes.themes) for (const tg of th.tags || []) assigned.add(tg);

    const missing = Array.from(displayVocabSetFinal).filter((tg) => !assigned.has(tg));

    if (missing.length >= 2) {
      // Only create an Unassigned theme when we have at least two missing tags.
      finalTagThemes = {
        ...finalTagThemes,
        themes: [
        ...finalTagThemes.themes,
        {
          id: "theme_unassigned",
          title: "Unassigned",
          order: finalTagThemes.themes.length,
          tags: missing,
          origin: "system",
          createdAt: tNow,
          updatedAt: now()
        }],

        updatedAt: now()
      };
    } else if (missing.length === 1) {
      // Merge a single missing tag into the smallest existing theme to avoid 1-tag themes.
      const tag = missing[0];
      const sorted = [...finalTagThemes.themes].sort(
        (a, b) => (a.tags?.length || 0) - (b.tags?.length || 0)
      );
      const target = sorted[0];
      if (target) {
        finalTagThemes = {
          ...finalTagThemes,
          themes: finalTagThemes.themes.map((t) =>
          t.id === target.id ?
          { ...t, tags: Array.from(new Set([...(t.tags || []), tag])), updatedAt: now() } :
          t
          ),
          updatedAt: now()
        };
      }
    }
  }

  const spansTags = new Set(finalSpanItems.map((s) => s.tag));
  const finalMissingTags = Array.from(finalVocabSet).filter((t) => !spansTags.has(t));

  if (DEBUG_AI_TAGS && finalMissingTags.length) {
    void 0;
    const missingPreview = finalMissingTags.slice(0, 30).map((tag) => {
      const inThemes = (finalTagThemes?.themes || []).some((th) => (th.tags || []).includes(tag));
      const inInsights = (finalInsights?.items || []).some((it) => (it.tags || []).includes(tag));
      const anchors = buildAnchorSnippetsForTag(tag, researchItemsById, 1);
      return { tag, inThemes, inInsights, hasAnchorCandidate: anchors.length > 0 };
    });
    void 0;
  }

  if (DEBUG_AI_TAGS) {
    const rawNormalized = debugRawTags.normalized.map(normalizeTag).filter(Boolean);
    const rawSet = new Set(rawNormalized);
    const spanSet = new Set(finalSpanItems.map((s) => normalizeTag(s.tag)));
    const displaySet = new Set(Array.from(finalVocabSet).filter((t) => spanSet.has(t)));
    const droppedFromDisplay = rawNormalized.filter((t) => !displaySet.has(t));
    const keptInDisplay = rawNormalized.filter((t) => displaySet.has(t));
    const rawDuplicates = rawNormalized.filter((t, i, arr) => arr.indexOf(t) !== i);
    void 0;
    void 0;
    void 0;
    void 0;

    if (repairResult?.missingTags?.length) {
      void 0;
      void 0;
    }

    const droppedFromRaw = debugRawTags.normalized.filter((t) => !finalVocabSet.has(t));
    const droppedReasons = [];

    if (debugRawTags.invalid.length) {
      droppedReasons.push({
        reason: "invalid_or_empty_after_normalization",
        tags: debugRawTags.invalid
      });
    }

    if (droppedFromRaw.length) {
      droppedReasons.push({
        reason: "not_in_final_vocab_set",
        tags: droppedFromRaw
      });
    }

    void 0;
    void 0;



    void 0;

    const spansByTag = {};
    finalSpanItems.forEach((s) => {
      spansByTag[s.tag] = (spansByTag[s.tag] || 0) + 1;
    });
    void 0;

    if (droppedReasons.length) {
      void 0;
    } else {
      void 0;
    }
  }

  const emergingSignals =
  parsedFinal?.emergingSignals && typeof parsedFinal.emergingSignals === "object" ?
  parsedFinal.emergingSignals :
  { items: [], generatedAt: new Date().toISOString() };

  const finalInsightsWithSignals = finalInsights ?
  { ...finalInsights, emergingSignals } :
  finalInsights;

  const foundationBundle = {
    tagVocabulary: { items: Array.from(finalVocabSet).map((tag) => ({ tag })) },
    tagThemes: finalTagThemes,
    researchInsights: finalInsightsWithSignals,
    insightGroups: finalInsights?.insightGroups || normalizedGroups,
    researchTagSpans: { items: finalSpanItems }
  };

  return {
    tagVocabulary: {
      version: 1,
      items: Array.from(finalVocabSet).map((tag) => ({
        tag,
        description: vocabDescMap.get(tag) || ""
      })),
      internalCount: finalVocabSet.size,
      displayCount: displayVocabSetFinal.size,
      displayItems: Array.from(displayVocabSetFinal),
      createdAt: tNow,
      updatedAt: now()
    },
    tagThemes: finalTagThemes,
    researchInsights: finalInsightsWithSignals,
    insightGroups: finalInsights?.insightGroups || normalizedGroups,
    researchTagSpans: {
      version: 1,
      items: finalSpanItems.
      map((it) => ({ ...it, tag: normalizeTag(it.tag) })).
      filter((it) => finalVocabSet.has(it.tag)),
      createdAt: tagSpans.createdAt ?? tNow,
      updatedAt: now()
    },
    // Persist prompt facts for debugging/traceability in the project state.
    promptFacts,
    promptFactsUpdatedAt: tNow,
    emergingSignals,
    problemDefinition: toSingleSentencePreview(parsedFinal?.problemDefinition || "", 25),
    weWill: toSingleSentencePreview(parsedFinal?.weWill || "", 20)
  };
}

export async function generateInsightsFromManualClusters({
  formData,
  promptFacts = [],
  researchItemIds = [],
  draftGroups = []
} = {}) {
  if (!apiKey) throw new Error("Missing Groq API key. Set VITE_GROQ_API_KEY in your .env");
  const consumeUsagePromise = consumeAiUsage({
    operation: "regenerate_insights_manual",
    projectId: safeText(formData?.projectId || "").trim() || null,
    isRegeneration: true,
  });

  const groups = Array.isArray(draftGroups) ? draftGroups : [];
  const tagSet = new Set();
  const spanItems = [];

  groups.forEach((group, idx) => {
    const title = safeText(group?.title).trim() || `Group ${idx + 1}`;
    const tag = safeText(title).trim().toLowerCase().replace(/\s+/g, " ");
    if (tag) tagSet.add(tag);
    const facts = Array.isArray(group?.atomicFacts) ? group.atomicFacts : [];
    facts.forEach((fact) => {
      if (!fact || typeof fact !== "object") return;
      const factText = safeText(fact.text).trim();
      if (!factText) return;
      const evidence = safeArray(fact.evidence).filter((e) => e && typeof e.text === "string" && e.text.trim());
      if (!evidence.length) return;
      evidence.forEach((ev) => {
        const excerpt = safeText(ev.text).trim();
        if (!excerpt) return;
        spanItems.push({
          tag: tag || "general",
          researchItemId: safeText(ev.researchItemId).trim(),
          excerpt,
          actor: "other",
          fact: factText
        });
      });
    });
  });

  const tagVocabulary = { items: Array.from(tagSet).map((tag) => ({ tag })) };
  const researchTagSpans = { items: spanItems };

  const prompt = buildGroqSynthesizerPrompt({
    formData: formData || {},
    promptFacts: Array.isArray(promptFacts) ? promptFacts : [],
    researchItemIds,
    tagVocabulary,
    researchTagSpans
  });

  await consumeUsagePromise;
  const parsed = await callGroq({ apiKey, userPrompt: prompt });
  const safeParsed = parsed && typeof parsed === "object" ? parsed : {};
  return {
    ...safeParsed,
    problemDefinition: toSingleSentencePreview(
      safeParsed?.problemDefinition || safeParsed?.overview?.problemDefinition?.problem || "",
      25
    ),
    weWill: toSingleSentencePreview(
      safeParsed?.weWill || safeParsed?.overview?.problemDefinition?.weWill || "",
      20
    )
  };
}

export async function generateOverviewWithGroq({
  foundationBundle,
  approvedInsightsSnapshot,
  promptFacts = [],
  researchItemsMeta = [],
  projectContext = {},
  isRegeneration = false
}) {
  assertValidGroqKey();
  const consumeUsagePromise = consumeAiUsage({
    operation: "generate_overview",
    projectId: safeText(projectContext?.id || "").trim() || null,
    isRegeneration,
  });

  const tNow = now();
  const normalizedApprovedInsights = safeArray(approvedInsightsSnapshot).
  map((ins, idx) => {
    if (!ins || typeof ins !== "object") return null;
    const id = safeText(ins?.id).trim() || `ins_${idx + 1}`;
    const title = safeText(ins?.title || "").trim();
    const text = safeText(ins?.text || ins?.statement || "").trim();
    const tags = safeArray(ins?.tags).
    map((t) => safeText(t).trim().toLowerCase()).
    filter(Boolean);
    const evidence = safeArray(ins?.evidence).
    map((ev, evIdx) => {
      const evText = safeText(ev?.text || ev?.factText || ev?.spanText || ev?.quote || "").trim();
      if (!evText) return null;
      const typeRaw = safeText(ev?.type).trim().toLowerCase();
      const type = ["quote", "observation", "fact"].includes(typeRaw) ? typeRaw : "observation";
      return {
        id: safeText(ev?.id).trim() || `ev_${id}_${evIdx + 1}`,
        type,
        text: evText,
        researchItemId: safeText(ev?.researchItemId).trim() || null
      };
    }).
    filter(Boolean);

    const evidenceWithFallback =
    evidence.length || !text ?
    evidence :
    [{ id: `ev_${id}_fallback`, type: "observation", text, researchItemId: null }];

    if (!title && !text) return null;
    return { id, ...(title ? { title } : {}), ...(text ? { text } : {}), tags, evidence: evidenceWithFallback };
  }).
  filter(Boolean);

  const overviewInput = {
    allowedTags: safeArray(foundationBundle?.tagVocabulary?.items).
    map((t) => safeText(t?.tag || "").trim().toLowerCase()).
    filter(Boolean),
    approvedInsights: normalizedApprovedInsights,
    groundingEvidence: {
      source: "hybrid",
      items: [
      ...safeArray(foundationBundle?.researchTagSpans?.items).map((s) => ({
        tag: safeText(s?.tag || "").trim().toLowerCase(),
        text: safeText(s?.excerpt || s?.text || s?.fact || s?.context || "").trim(),
        researchItemId: safeText(s?.researchItemId || "").trim() || null
      })),
      ...normalizedApprovedInsights.flatMap((ins) =>
      safeArray(ins?.evidence).map((ev) => ({
        tag: safeArray(ins?.tags)[0] || "",
        text: safeText(ev?.text || "").trim(),
        researchItemId: safeText(ev?.researchItemId || "").trim() || null
      }))
      )].
      filter((it) => it.tag && it.text)
    },
    researchItems: safeArray(researchItemsMeta).map((ri) => ({
      id: safeText(ri?.id || "").trim(),
      label: safeText(ri?.label || "").trim()
    })),
    promptFacts: compactPromptFactsForOverview(
      safeArray(promptFacts).map((x) => safeText(x).trim()).filter(Boolean),
      projectContext || {}
    )
  };

  const compactOverviewPayload = buildCompactAIPayload({
    projectContext,
    approvedInsightsSnapshot: normalizedApprovedInsights,
    foundationBundle: {
      tagVocabulary: foundationBundle?.tagVocabulary || { items: [] },
      researchTagSpans: foundationBundle?.researchTagSpans || { items: [] },
      tagThemes: foundationBundle?.tagThemes || { themes: [] }
    },
    researchItemsMeta: overviewInput.researchItems
  });
  const rawOverviewPayload = {
    project: projectContext || {},
    foundation: {
      tagVocabulary: foundationBundle?.tagVocabulary || { items: [] },
      researchTagSpans: foundationBundle?.researchTagSpans || { items: [] },
      tagThemes: foundationBundle?.tagThemes || { themes: [] }
    },
    approvedInsights: normalizedApprovedInsights,
    researchItems: overviewInput.researchItems,
    promptFacts: overviewInput.promptFacts
  };
  void 0;
  void 0;



  void 0;
  void 0;







  const rawOverviewSize = estimateJsonPayloadSize(rawOverviewPayload);
  const compactOverviewSize = estimateJsonPayloadSize(compactOverviewPayload);
  const rawSpanCount = Array.isArray(foundationBundle?.researchTagSpans?.items) ?
  foundationBundle.researchTagSpans.items.length :
  0;
  const compactSpanCount = Array.isArray(compactOverviewPayload?.foundation?.researchTagSpans?.items) ?
  compactOverviewPayload.foundation.researchTagSpans.items.length :
  0;
  const usedFallbackSpans = rawSpanCount === 0 && compactSpanCount > 0;
  void 0;











  const overviewPrompt = buildGroqOverviewPrompt({
    promptFacts: overviewInput.promptFacts,
    foundation: compactOverviewPayload.foundation,
    approvedInsightsSnapshot: compactOverviewPayload.approvedInsights,
    researchItemsMeta: compactOverviewPayload.researchItems,
    projectContext: compactOverviewPayload.project
  });
  await consumeUsagePromise;
  void 0;

  const promptChars = overviewPrompt.length;
  const promptTokensEstimate = Math.ceil(promptChars / 4);

  void 0;












  const overviewParsed = await callGroq({
    apiKey,
    userPrompt: overviewPrompt,
    maxCompletionTokens: 10000,
    logLabel: "OVERVIEW"
  });
  const tagThemes = foundationBundle?.tagThemes || null;

  let overviewArtifactsRaw = normalizeOverviewArtifactsFromGroq(overviewParsed?.overview, tagThemes, null);

  // Recover missing researchItemId from approved snapshot evidence before filtering.
  if (Array.isArray(approvedInsightsSnapshot)) {
    const evidenceMap = new Map();
    approvedInsightsSnapshot.forEach((ins) => {
      safeArray(ins?.evidence).forEach((ev) => {
        const text = safeText(ev?.text || "").trim();
        const rid = safeText(ev?.researchItemId || "").trim();
        if (text && rid) evidenceMap.set(normalizeForMatch(text), rid);
      });
    });

    const fillEvidenceIds = (items) =>
    safeArray(items).map((item) => {
      const nextEvidence = safeArray(item?.evidence).map((ev) => {
        const rid = safeText(ev?.researchItemId || "").trim();
        if (rid) return ev;
        const key = normalizeForMatch(ev?.text || "");
        const mapped = evidenceMap.get(key);
        return mapped ? { ...ev, researchItemId: mapped } : ev;
      });
      return { ...item, evidence: nextEvidence };
    });

    overviewArtifactsRaw = {
      ...overviewArtifactsRaw,
      keySignals: fillEvidenceIds(overviewArtifactsRaw?.keySignals)
    };
  }

  const spanTagsSet = new Set(
    Array.isArray(foundationBundle?.researchTagSpans?.items) ?
    foundationBundle.researchTagSpans.items.map((s) => s.tag) :
    []
  );

  const vocabSet = new Set(
    Array.isArray(foundationBundle?.tagVocabulary?.items) ?
    foundationBundle.tagVocabulary.items.map((t) => t.tag) :
    []
  );

  const researchItemIdSet = new Set(
    Array.isArray(researchItemsMeta) ?
    researchItemsMeta.map((ri) => String(ri.id || "").trim()).filter(Boolean) :
    []
  );

  let overviewArtifacts = enforceOverviewRules(
    overviewArtifactsRaw,
    vocabSet,
    spanTagsSet,
    researchItemIdSet,
    promptFacts
  );

  overviewArtifacts = sanitizeStrings(overviewArtifacts);

  const approvedInsightTitleById = new Map();
  const approvedInsightIdByTitle = new Map();
  const approvedInsightResearchIdsById = new Map();
  safeArray(normalizedApprovedInsights).forEach((ins) => {
    const id = safeText(ins?.id).trim();
    const title = safeText(ins?.title || ins?.text || "").trim();
    if (!id || !title) return;
    approvedInsightTitleById.set(id, title);
    const key = title.toLowerCase();
    if (!approvedInsightIdByTitle.has(key)) approvedInsightIdByTitle.set(key, id);
    const researchIds = Array.from(
      new Set(
        [
        ...safeArray(ins?.researchItemIds),
        ...safeArray(ins?.evidenceSources).map((ev) => safeText(ev?.researchItemId).trim()),
        ...safeArray(ins?.evidence).map((ev) => safeText(ev?.researchItemId).trim()),
        ...safeArray(ins?.sources).map((src) => safeText(src?.researchItemId).trim())].

        map((rid) => safeText(rid).trim()).
        filter(Boolean)
      )
    );
    if (researchIds.length) approvedInsightResearchIdsById.set(id, researchIds);
  });

  if (overviewArtifacts?.researchQuestions?.items?.length) {
    const nextItems = overviewArtifacts.researchQuestions.items.map((q) => {
      const linkedInsightIds = safeArray(q?.linkedInsightIds).
      map((id) => safeText(id).trim()).
      filter(Boolean).
      slice(0, 3);
      const linkedInsightTitles = safeArray(q?.linkedInsightTitles).
      map((title) => safeText(title).trim()).
      filter(Boolean);
      const linkedResearchItemIds = new Set(
        safeArray(q?.linkedResearchItemIds || q?.researchItemIds).
        map((id) => safeText(id).trim()).
        filter(Boolean)
      );
      const withResearchIds = (item, insightIds) => {
        safeArray(insightIds).forEach((insightId) => {
          safeArray(approvedInsightResearchIdsById.get(insightId)).forEach((researchId) => {
            const normalized = safeText(researchId).trim();
            if (normalized) linkedResearchItemIds.add(normalized);
          });
        });
        return {
          ...item,
          linkedResearchItemIds: Array.from(linkedResearchItemIds)
        };
      };

      if (linkedInsightIds.length) {
        const resolvedTitles = linkedInsightTitles.length ?
        linkedInsightTitles :
        linkedInsightIds.
        map((id) => approvedInsightTitleById.get(id)).
        filter(Boolean);
        return withResearchIds({
          ...q,
          linkedInsightIds,
          linkedInsightTitles: resolvedTitles
        }, linkedInsightIds);
      }

      if (linkedInsightTitles.length) {
        const inferredIds = linkedInsightTitles.
        map((title) => approvedInsightIdByTitle.get(title.toLowerCase())).
        filter(Boolean).
        slice(0, 3);
        return withResearchIds({
          ...q,
          linkedInsightIds: inferredIds,
          linkedInsightTitles
        }, inferredIds);
      }

      return withResearchIds({ ...q, linkedInsightIds: [], linkedInsightTitles: [] }, []);
    });

    overviewArtifacts = {
      ...overviewArtifacts,
      researchQuestions: {
        ...overviewArtifacts.researchQuestions,
        items: nextItems
      }
    };
  }

  if (tagThemes?.themes) {
    const themeTags = tagThemes.themes.flatMap((t) => t.tags || []);
    const themeTagsMissingSpans = themeTags.filter((t) => !spanTagsSet.has(t));
    if (themeTagsMissingSpans.length) {
      void 0;
    }
  }

  try {
    void 0;










  } catch {}

  return {
    overview: {
      ...overviewArtifacts,
      generatedAt: tNow,
      source: "approved_insights_ai",
      approvedAt: tNow,
      overviewMode: "research"
    },
    problemDefinition: overviewArtifacts.problemDefinition,
    decisions: overviewArtifacts.decisionsV2 || overviewArtifacts.decisions || []
  };
}

function buildGroqExplorationOverviewPrompt({
  projectContext = {},
  hypothesisInsights = []
} = {}) {
  const projectName = safeText(projectContext?.projectName || "").trim() || "Untitled project";
  const projectDescription = safeText(projectContext?.productDescription || "").trim() || "(none)";
  const targetAudience = safeText(projectContext?.targetAudience || "").trim() || "(not provided)";
  const industryContext = safeText(projectContext?.industry || "").trim() || "(not provided)";
  const projectTypeRaw = safeText(projectContext?.projectType || "").trim().toLowerCase();
  const projectType = projectTypeRaw === "existing" ? "existing product" : "new product / concept";
  const hypothesisInsightsJson = JSON.stringify(
    safeArray(hypothesisInsights).
    map((ins) => {
      const text = safeText(ins?.text || "").trim();
      if (!text) return null;
      return {
        id: safeText(ins?.id || "").trim() || null,
        title: safeText(ins?.title || "").trim() || null,
        text,
        tags: safeArray(ins?.tags).map((t) => safeText(t).trim()).filter(Boolean)
      };
    }).
    filter(Boolean).
    slice(0, 12),
    null,
    2
  );

  return `
You are an expert UX and product strategy assistant.

Generate an exploration overview for a project with NO research data yet.

Return ONLY valid JSON.

PROJECT:
- Name: ${projectName}
- Type: ${projectType}
- Description: ${projectDescription}
- Target audience: ${targetAudience}
- Industry / context: ${industryContext}

OPTIONAL HYPOTHESIS INSIGHTS:
${hypothesisInsightsJson}

IMPORTANT:
This project has no uploaded research.
Do NOT present assumptions as evidence.
Do NOT mention interviews, findings, quotes, evidence excerpts, or validated themes.

STRICT RULES:
1. Treat the project as exploratory, not evidence-backed.
2. Do NOT claim or imply that research has been conducted.
3. Use language such as: may, likely, hypothesis, assumption, unknown, could.
4. Executive Summary must frame the problem or opportunity space, not summarize research.
5. Key Signals must become Early Hypotheses.
6. Coverage Gaps must become Unknowns.
7. Research Questions must focus on what should be validated first.
8. Recommended Next Steps must suggest 2–4 concrete actions that would meaningfully reduce uncertainty or validate the strongest hypotheses.
9. These actions may include research, validation, experimentation, prototype exploration, or other learning-oriented actions.
10. Do not output only one generic action if multiple distinct Research Questions or Unknowns are identified.
11. Avoid internal preparation tasks unless they directly support a validation action.
12. Keep all text concise and product-oriented.
13. Do not invent statistics, evidence counts, user quotes, or validated findings.
14. Do not repeat the same idea across sections.
15. Each section must serve a different purpose.
16. Map each research question to the most relevant hypothesis insight(s).
17. For linkedInsightIds, prefer IDs from OPTIONAL HYPOTHESIS INSIGHTS when available.
18. Return strict JSON only.
19. For each Recommended Next Step, use an actionKey from this allowed set only:
${NEXT_STEP_ACTIONS.join(", ")}
20. Synthesize the main audience groups from the available context and output them in audienceGroups.
21. Exactly ONE audience group must have isPrimary = true. All others must be false.
22. Primary audience is the group that decides, pays, adopts, and determines product success.
23. Do NOT choose primary based on research volume, mentions, or data quality.

SECTION ROLE RULES:
- Executive Summary frames the opportunity or problem space at a high level.
- Early Hypotheses describe possible user motivations, behaviors, risks, or decision drivers.
- Research Questions define what needs to be learned first.
- Unknowns identify missing information that could materially affect direction.
- Recommended Next Steps describe concrete actions to reduce uncertainty or generate learning.
- Audience Groups identify the most relevant human user groups or roles for downstream artifact creation.
- Do not restate the same idea across sections.

HYPOTHESIS QUALITY RULES:
- Each hypothesis must represent a distinct idea.
- Avoid repeating the same concept with different wording.
- Before finalizing the list, compare hypotheses and merge overlapping ones.
- If two hypotheses describe the same behavior, keep only the strongest version.
- Generate 5-7 hypotheses maximum.
- Focus on meaningful user motivations, risks, or decision drivers.
- Avoid generic or obvious statements.
- Prioritize behavioral hypotheses over feature ideas.
- Focus on why users behave a certain way, not what product features could exist.
- If you output hypothesis objects, do not repeat title text verbatim in description.
- For each Unknown item, include concrete non-empty fields for both impact and action.
- Do not leave Unknown impact or action blank.
- For each Recommended Next Step, include both a short title and a separate description.
- Do not use the same sentence for both title and description.
- Recommended Next Step actionKey values must come only from:
${NEXT_STEP_ACTIONS.join(", ")}
- Audience groups must be human user groups or roles that are useful for downstream artifact generation.
- Exclude animals, objects, systems, product components, and abstract actors.
- Use clean, user-facing labels such as Dog owners, Dog walkers, or Pet sitters, not raw extracted actors such as dog, app, or platform.
- Keep the list concise and practical.
- Only include groups that are meaningfully supported by the available context and hypotheses.

Return strict JSON with this exact structure:
{
  "overviewMode": "exploration",
  "executiveSummary": {
    "title": "",
    "text": ""
  },
  "keySignals": {
    "title": "Early Hypotheses",
    "items": [
      {
        "id": "sig_explore_1",
        "title": "",
        "description": "",
        "signalType": "need|behavior|risk|preference",
        "confidence": "low|medium|high"
      }
    ]
  },
  "researchQuestions": {
    "title": "Research Questions",
    "items": [
      {
        "id": "rq_1",
        "title": "",
        "description": "",
        "methodRecommendation": {
          "primaryMethod": {
            "type": "interview|survey|usability_test|analytics_review|competitive_analysis|ab_test|support_analysis",
            "reason": ""
          },
          "secondaryMethods": [],
          "confidence": "low|medium|high",
          "recommendedSequence": []
        },
        "linkedInsightIds": ["sig_explore_1"],
        "linkedInsightTitles": [""]
      }
    ]
  },
  "coverageGaps": {
    "title": "Unknowns",
    "items": [
      {
        "id": "gap_1",
        "title": "",
        "description": "",
        "impact": "",
        "action": "",
        "priority": "high|medium|low"
      }
    ]
  },
  "audienceGroups": [
    {
      "id": "",
      "name": "",
      "description": "",
      "isPrimary": true
    }
  ],
  "recommendedNextSteps": {
    "title": "Recommended Next Steps",
    "items": [
      {
        "id": "step_1",
        "title": "",
        "description": "",
        "actionKey": ""
      }
    ]
  }
}
`.trim();
}

function normalizeExplorationOverviewFromGroq(rawOverview) {
  const root = rawOverview && typeof rawOverview === "object" ? rawOverview : {};
  const o = root.overview && typeof root.overview === "object" ? root.overview : root;
  const executiveSummaryRaw = o.executiveSummary && typeof o.executiveSummary === "object" ? o.executiveSummary : {};
  const keySignalsRaw = o.keySignals && typeof o.keySignals === "object" ? o.keySignals : {};
  const researchQuestionsRaw = o.researchQuestions && typeof o.researchQuestions === "object" ? o.researchQuestions : {};
  const coverageGapsRaw = o.coverageGaps && typeof o.coverageGaps === "object" ? o.coverageGaps : {};
  const nextStepsRaw = o.recommendedNextSteps && typeof o.recommendedNextSteps === "object" ? o.recommendedNextSteps : {};
  const audienceGroupsRaw = safeArray(o.audienceGroups).
  map((group, idx) => {
    if (!group || typeof group !== "object") return null;
    const name = safeText(group.name || group.title || "").trim();
    const description = safeText(group.description || "").trim();
    if (!name) return null;
    return {
      id: safeText(group.id || "").trim() || `aud_${hash32(name + "::" + idx)}`,
      name,
      description,
      isPrimary: group.isPrimary === true
    };
  }).
  filter(Boolean);
  const audienceGroups = ensurePrimaryAudienceGroups(audienceGroupsRaw);

  const keySignalsItems = safeArray(keySignalsRaw.items).
  map((item, idx) => {
    const titleRaw =
    typeof item === "string" ?
    "" :
    safeText(item?.title || "").trim();
    const descriptionRaw =
    typeof item === "string" ?
    safeText(item).trim() :
    safeText(item?.description || item?.text || item?.hypothesis || "").trim();
    if (!titleRaw && !descriptionRaw) return null;
    return {
      id: safeText(item?.id).trim() || `sig_explore_${idx + 1}`,
      title: titleRaw,
      description: descriptionRaw,
      signalType: safeText(item?.signalType || "").trim() || "behavior",
      confidence: safeText(item?.confidence || "").trim().toLowerCase() || "low",
      evidence: []
    };
  }).
  filter(Boolean);

  const researchQuestionsItems = safeArray(researchQuestionsRaw.items).
  map((item, idx) => {
    if (!item) return null;
    if (typeof item === "string") {
      const question = safeText(item).trim();
      if (!question) return null;
      return {
        id: `rq_${idx + 1}`,
        title: question.endsWith("?") ? question : `${question}?`,
        description: "",
        linkedInsightIds: [],
        linkedInsightTitles: [],
        methodRecommendation: null
      };
    }
    const question = safeText(item?.title || item?.question || item?.text).trim();
    if (!question) return null;
    const recRaw = item?.methodRecommendation && typeof item.methodRecommendation === "object" ? item.methodRecommendation : {};
    const primaryRaw = recRaw?.primaryMethod && typeof recRaw.primaryMethod === "object" ? recRaw.primaryMethod : {};
    const legacyMethodRaw = safeText(item?.recommendedMethod).trim().toLowerCase();
    const primaryTypeRaw = safeText(primaryRaw?.type || legacyMethodRaw).trim();
    const primaryMethod = OVERVIEW_RECOMMENDED_METHOD_TYPES.has(primaryTypeRaw) ?
    { type: primaryTypeRaw, reason: safeText(primaryRaw?.reason || "").trim() } :
    null;
    const secondaryMethods = safeArray(recRaw?.secondaryMethods).
    map((method) => {
      if (!method || typeof method !== "object") return null;
      const type = safeText(method.type || "").trim();
      if (!OVERVIEW_RECOMMENDED_METHOD_TYPES.has(type)) return null;
      return { type, reason: safeText(method.reason || "").trim() };
    }).
    filter(Boolean).
    slice(0, 3);
    const confidenceRaw = safeText(recRaw?.confidence || "").toLowerCase();
    const confidence = ["low", "medium", "high"].includes(confidenceRaw) ? confidenceRaw : "";
    const recommendedSequence = safeArray(recRaw?.recommendedSequence).
    map((value) => safeText(value).trim()).
    filter((value) => OVERVIEW_RECOMMENDED_METHOD_TYPES.has(value));
    const linkedInsightIds = safeArray(
      item?.linkedInsightIds || item?.insightIds || item?.relatedInsightIds
    ).
    map((id) => safeText(id).trim()).
    filter(Boolean);
    const linkedInsightTitles = safeArray(
      item?.linkedInsightTitles || item?.insightTitles || item?.relatedInsights
    ).
    map((title) => safeText(title).trim()).
    filter(Boolean);
    const linkedResearchItemIds = safeArray(item?.linkedResearchItemIds || item?.researchItemIds).
    map((id) => safeText(id).trim()).
    filter(Boolean);
    return {
      id: safeText(item?.id).trim() || `rq_${idx + 1}`,
      title: question.endsWith("?") ? question : `${question}?`,
      description: safeText(item?.description || item?.whyItMatters).trim(),
      linkedInsightIds,
      linkedInsightTitles,
      linkedResearchItemIds,
      methodRecommendation:
      primaryMethod || secondaryMethods.length || confidence || recommendedSequence.length ?
      {
        primaryMethod,
        secondaryMethods,
        confidence: confidence || "low",
        recommendedSequence
      } :
      null
    };
  }).
  filter(Boolean);

  const fallbackSignalIds = keySignalsItems.map((signal) => String(signal.id));
  const fallbackSignalTitleById = new Map(
    keySignalsItems.map((signal) => [String(signal.id), String(signal.title || "").trim()])
  );
  const questionsWithLinks = researchQuestionsItems.map((question) => {
    const ids = safeArray(question?.linkedInsightIds).
    map((id) => String(id || "").trim()).
    filter(Boolean);
    const normalizedTitles = safeArray(question?.linkedInsightTitles).
    map((title) => String(title || "").trim()).
    filter(Boolean);

    if (ids.length) {
      return {
        ...question,
        linkedInsightIds: ids,
        linkedInsightTitles: normalizedTitles.length ? normalizedTitles : ids.
        map((id) => fallbackSignalTitleById.get(id)).
        filter(Boolean),
        linkedResearchItemIds: safeArray(question?.linkedResearchItemIds).
        map((id) => String(id || "").trim()).
        filter(Boolean)
      };
    }

    const titleNeedle = String(question?.title || "").toLowerCase();
    const descriptionNeedle = String(question?.description || "").toLowerCase();
    const keywordSource = `${titleNeedle} ${descriptionNeedle}`;
    const inferredIds = keySignalsItems.
    filter((signal) => {
      const signalTitle = String(signal?.title || "").toLowerCase();
      const signalDescription = String(signal?.description || "").toLowerCase();
      if (!signalTitle && !signalDescription) return false;
      if (signalTitle && keywordSource.includes(signalTitle)) return true;
      if (signalDescription && keywordSource.includes(signalDescription)) return true;
      const tokens = signalTitle.split(/[^a-z0-9]+/g).filter((t) => t.length >= 5);
      return tokens.some((token) => keywordSource.includes(token));
    }).
    map((signal) => String(signal.id || "").trim()).
    filter(Boolean).
    slice(0, 3);

    const finalIds = inferredIds.length ? inferredIds : fallbackSignalIds.length ? [fallbackSignalIds[0]] : [];
    return {
      ...question,
      linkedInsightIds: finalIds,
      linkedInsightTitles: finalIds.map((id) => fallbackSignalTitleById.get(id)).filter(Boolean),
      linkedResearchItemIds: safeArray(question?.linkedResearchItemIds).
      map((id) => String(id || "").trim()).
      filter(Boolean)
    };
  });

  const coverageGapsItems = safeArray(coverageGapsRaw.items).
  map((item, idx) => {
    if (!item) return null;
    const title =
    typeof item === "string" ?
    safeText(item).trim() :
    safeText(item?.title || "").trim();
    if (!title) return null;
    const description =
    typeof item === "string" ? "" : safeText(item?.description || item?.gap || item?.explanation).trim();
    const impactRaw =
    typeof item === "string" ?
    "" :
    safeText(item?.impact || item?.risk || item?.whyItMatters || "").trim();
    const actionRaw =
    typeof item === "string" ?
    "" :
    safeText(item?.action || item?.nextStep || item?.recommendedAction || "").trim();
    return {
      id: safeText(item?.id).trim() || `gap_${idx + 1}`,
      title,
      description,
      impact: impactRaw,
      action: actionRaw,
      priority: safeText(item?.priority || "").trim().toLowerCase() || "medium"
    };
  }).
  filter((item) => item && (item.description || item.impact || item.action));

  const nextStepsSource = safeArray(nextStepsRaw.items).length ?
  nextStepsRaw.items :
  Array.isArray(o.recommendedNextSteps) ?
  o.recommendedNextSteps :
  Array.isArray(o.nextSteps) ?
  o.nextSteps :
  safeArray(o?.nextSteps?.items);

  const nextStepsItems = safeArray(nextStepsSource).
  map((item, idx) => {
    if (!item) return null;
    const legacyText =
    typeof item === "string" ?
    safeText(item).trim() :
    safeText(item?.text || item?.action).trim();
    const title =
    typeof item === "string" ?
    legacyText :
    safeText(item?.title || "").trim();
    const description =
    typeof item === "string" ?
    "" :
    safeText(item?.description || item?.text || item?.action || "").trim();
    if (!title && !description && !legacyText) return null;
    return {
      id: safeText(item?.id).trim() || `step_${idx + 1}`,
      title,
      description,
      actionKey: (() => {
        const key = safeText(item?.actionKey || "").trim();
        return NEXT_STEP_ACTIONS.includes(key) ? key : "";
      })()
    };
  }).
  filter(Boolean);

  const summaryText =
  safeText(executiveSummaryRaw.text || o.executiveSummaryText || "").trim();

  return {
    overviewMode: "exploration",
    executiveSummary: summaryText,
    executiveSummarySection: {
      title: safeText(executiveSummaryRaw.title || "").trim() || "Executive Summary",
      text: summaryText
    },
    keySignalsTitle: safeText(keySignalsRaw.title || "").trim() || "Early Hypotheses",
    keySignals: keySignalsItems,
    researchQuestionsTitle: safeText(researchQuestionsRaw.title || "").trim() || "Research Questions",
    researchQuestions: {
      title: safeText(researchQuestionsRaw.title || "").trim() || "Research Questions",
      items: questionsWithLinks,
      generatedAt: new Date().toISOString()
    },
    coverageGapsTitle: safeText(coverageGapsRaw.title || "").trim() || "Unknowns",
    coverageGaps: coverageGapsItems,
    audienceGroups,
    recommendedNextStepsTitle:
    safeText(nextStepsRaw.title || "").trim() || "Recommended Next Steps",
    recommendedNextSteps: nextStepsItems.map((step) => ({
      title: step.title,
      description: step.description,
      actionKey: step.actionKey
    }))
  };
}

function ensurePrimaryAudienceGroups(groups) {
  const list = safeArray(groups).filter(Boolean);
  if (!list.length) return [];
  const firstPrimaryIdx = list.findIndex((g) => g?.isPrimary === true);
  const primaryIdx = firstPrimaryIdx >= 0 ? firstPrimaryIdx : 0;
  return list.map((g, idx) => ({
    ...g,
    isPrimary: idx === primaryIdx
  }));
}

export async function generateExplorationOverview({
  projectContext = {},
  hypothesisInsights = []
}) {
  assertValidGroqKey();
  const consumeUsagePromise = consumeAiUsage({
    operation: "generate_overview_exploration",
    projectId: safeText(projectContext?.id || "").trim() || null,
    isRegeneration: false,
  });
  const tNow = now();
  const prompt = buildGroqExplorationOverviewPrompt({
    projectContext,
    hypothesisInsights
  });
  await consumeUsagePromise;
  const parsed = await callGroq({
    apiKey,
    userPrompt: prompt,
    maxCompletionTokens: 10000,
    requireComplete: false,
    logLabel: "OVERVIEW_EXPLORATION"
  });
  const overviewArtifacts = normalizeExplorationOverviewFromGroq(parsed);

  return {
    overview: {
      ...overviewArtifacts,
      generatedAt: tNow,
      source: "exploration_ai"
    },
    problemDefinition: {
      problem: "",
      weWill: "",
      successCriteria: ""
    },
    decisions: []
  };
}

function buildHypothesisInsightsPrompt({ formData, minItems = 5, maxItems = 7 } = {}) {
  const projectName = safeText(formData?.projectName || "").trim() || "Untitled project";
  const description = safeText(formData?.productDescription || "").trim() || "(none)";
  const targetAudience = safeText(formData?.targetAudience || "").trim() || "(not provided)";
  const industry = safeText(formData?.industry || "").trim() || "(not provided)";
  const projectTypeRaw = safeText(formData?.projectType || "").trim().toLowerCase();
  const projectType = projectTypeRaw === "existing" ? "existing product" : "new product / concept";

  return `
You are a senior product researcher.
No user research data is available yet.
Generate initial HYPOTHESIS insights from project context only.

Project context:
- Project name: ${projectName}
- Project description: ${description}
- Target audience: ${targetAudience}
- Industry/context: ${industry}
- Project type: ${projectType}

Output requirements:
- Return ${minItems}-${maxItems} insights.
- Cover these signal types across the set:
  1) likely user motivations
  2) possible pain points
  3) expected constraints
  4) possible behavioral patterns
  5) early product opportunities
- Each insight should be 1-2 concise sentences.
- Do not claim direct evidence or quotes.
- Treat all outputs as hypotheses.

HYPOTHESIS QUALITY RULES:
- Each hypothesis must represent a distinct idea.
- Avoid repeating the same concept with different wording.
- Before finalizing the list, compare hypotheses and merge overlapping ones.
- If two hypotheses describe the same behavior, keep only the strongest version.
- Generate 5-7 hypotheses maximum.
- Focus on meaningful user motivations, risks, or decision drivers.
- Avoid generic or obvious statements.
- Prioritize behavioral hypotheses over feature ideas.
- Focus on why users behave a certain way, not what product features could exist.
- Keep titles short and distinct from the text body; do not repeat the title verbatim in text.

Return ONLY valid JSON:
{
  "insights": [
    {
      "title": "string",
      "text": "string",
      "category": "motivation|pain_point|constraint|behavior_pattern|opportunity",
      "tags": ["string"]
    }
  ]
}
`.trim();
}

function buildFallbackHypothesisTexts(formData) {
  const projectName = safeText(formData?.projectName || "").trim() || "the product";
  const targetAudience = safeText(formData?.targetAudience || "").trim() || "target users";
  const description = safeText(formData?.productDescription || "").trim();
  const projectTypeRaw = safeText(formData?.projectType || "").trim().toLowerCase();
  const isExisting = projectTypeRaw === "existing";

  return [
  {
    title: "Users want quicker progress",
    text: `${targetAudience} likely prioritize fast outcomes and low setup effort when using ${projectName}.`,
    category: "motivation",
    tags: ["speed to value", "adoption"]
  },
  {
    title: "Early friction may block adoption",
    text: `Initial steps may feel unclear or heavy, causing users to postpone or abandon key workflows in ${projectName}.`,
    category: "pain_point",
    tags: ["onboarding friction", "drop-off"]
  },
  {
    title: "Workflow complexity creates constraints",
    text: `Users may face time, process, or cross-team coordination constraints that limit consistent usage.`,
    category: "constraint",
    tags: ["time constraints", "workflow dependency"]
  },
  {
    title: "Users optimize for familiar behavior",
    text: `Users may default to existing habits and only adopt new flows when the value is immediate and obvious.`,
    category: "behavior_pattern",
    tags: ["habit inertia", "behavioral pattern"]
  },
  {
    title: "Opportunity to guide decisions earlier",
    text: isExisting ?
    `There is an early opportunity to simplify existing journeys and improve decision confidence with clearer guidance.` :
    `There is an early opportunity to shape a clearer product direction with guided actions and lightweight validation.`,
    category: "opportunity",
    tags: ["product opportunity", "decision support"]
  },
  ...(description ?
  [
  {
    title: "Positioning must match intent",
    text: `How ${projectName} is framed may strongly influence whether users see it as relevant to their immediate goals.`,
    category: "motivation",
    tags: ["positioning", "value framing"]
  }] :

  [])];

}

export async function generateHypothesisInsightsWithGroq(formData, { minItems = 5, maxItems = 10 } = {}) {
  assertValidGroqKey();
  const consumeUsagePromise = consumeAiUsage({
    operation: "generate_hypothesis_insights",
    projectId: safeText(formData?.projectId || "").trim() || null,
    isRegeneration: false,
  });
  const t = now();
  const clampedMin = Math.max(5, Math.min(7, Number(minItems) || 5));
  const clampedMax = Math.max(clampedMin, Math.min(7, Number(maxItems) || 7));
  const prompt = buildHypothesisInsightsPrompt({ formData, minItems: clampedMin, maxItems: clampedMax });

  await consumeUsagePromise;
  let parsed = {};
  try {
    parsed = await callGroq({
      apiKey,
      userPrompt: prompt,
      maxCompletionTokens: 10000,
      requireComplete: true,
      logLabel: "HYPOTHESIS"
    });
  } catch (err) {
    void 0;
  }

  const rawModelItems = Array.isArray(parsed?.insights) ? parsed.insights : [];
  const normalizeHypothesisTags = (tags) =>
  normalizeInsightTags(
    safeArray(tags).map((tag) => {
      if (typeof tag !== "string") return tag;
      return tag.replace(/_/g, " ");
    })
  );
  let hypothesisItems = rawModelItems.
  map((item, idx) => {
    const raw = item && typeof item === "object" ? item : {};
    const text = safeText(raw.text || "").trim();
    if (!text) return null;
    const title = safeText(raw.title || "").trim() || deriveInsightTitleFromText(text);
    const category = normalizeCategory(raw.category, "Other");
    const tags = normalizeHypothesisTags(raw.tags);
    const id = `ins_${hash32(`hypothesis::${title}::${text}::${idx}`)}`;
    return {
      id,
      title,
      text,
      category,
      origin: "ai",
      isReviewed: false,
      createdAt: t,
      updatedAt: t,
      tags,
      confidence: "hypothesis",
      evidence: [],
      factIds: [],
      source: "hypothesis",
      sourceType: "ai_suggested",
      sourceLabel: "Hypothesis",
      sourcesCount: 0,
      order: idx,
      groupId: "grp_ungrouped"
    };
  }).
  filter(Boolean).
  slice(0, clampedMax);

  if (hypothesisItems.length < clampedMin) {
    const fallbackItems = buildFallbackHypothesisTexts(formData);
    const existingText = new Set(hypothesisItems.map((it) => normalizeForMatch(it.text)));
    fallbackItems.forEach((item) => {
      if (hypothesisItems.length >= clampedMin) return;
      const text = safeText(item?.text || "").trim();
      if (!text) return;
      const key = normalizeForMatch(text);
      if (existingText.has(key)) return;
      existingText.add(key);
      const idx = hypothesisItems.length;
      hypothesisItems.push({
        id: `ins_${hash32(`fallback_hypothesis::${text}::${idx}`)}`,
        title: safeText(item?.title || "").trim() || deriveInsightTitleFromText(text),
        text,
        category: normalizeCategory(item?.category, "Other"),
        origin: "ai",
        isReviewed: false,
        createdAt: t,
        updatedAt: t,
        tags: normalizeInsightTags(item?.tags),
        confidence: "hypothesis",
        evidence: [],
        factIds: [],
        source: "hypothesis",
        sourceType: "ai_suggested",
        sourceLabel: "Hypothesis",
        sourcesCount: 0,
        order: idx,
        groupId: "grp_ungrouped"
      });
    });
  }

  hypothesisItems = hypothesisItems.slice(0, clampedMax);

  return {
    version: 4,
    items: hypothesisItems,
    createdAt: t,
    updatedAt: t,
    emergingSignals: { items: [], generatedAt: new Date(t).toISOString() }
  };
}

export async function generateWithGroq(formData) {
  return generateFoundationWithGroq(formData);
}

export function assertValidGroqKey() {
  if (!apiKey) {
    throw new Error("Missing Groq API key. Set VITE_GROQ_API_KEY in your .env");
  }
  if (typeof apiKey === "string" && /^https?:\/\//i.test(apiKey.trim())) {
    throw new Error("Invalid Groq API key (looks like a URL). Check VITE_GROQ_API_KEY.");
  }
}
